import './polyfills.js';
import './styles.css';
import * as THREE from 'three';
import { createCalloutLabel } from './callout-labels.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { cacheFile,restoreFiles,clearCachedFiles,deleteCachedFile,createProject,saveProject,getProject,listProjects,deleteProject } from './storage.js';
import { SurfaceSampler } from './surface-sampler.js';
import { latLonToUtm, utmCrs, utmToLatLon } from './utm.js';
import { estimateBoundaryVolume } from './volume.js';
import { selectionForFeature } from './selection-geometry.js';
import { extractGeoPdfMetadata, reprojectGeoPdfMetadata } from './geopdf.js';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { parseKml, parseKmz, parseShp } from './vector-import.js';
import { worldReference as makeWorldReference } from './world-coordinates.js';
import {canAutoResolveUtmLikeLayer,sameGridCandidates,sameGridStatusLabel} from './same-grid-policy.js';
import {canonicalBoundsFromProjectBounds,createProjectSpatialFrame,projectToCanonical} from './spatial-frame.js';
import {chooseSpatialRenderFrame,combineRenderBounds,enuBoundsToRender,projectCoordinatesToRender,
  projectPointToRender,
  projectedBoundsToRender,
  RENDER_FRAME_ENU,
  RENDER_FRAME_PROJECTED,
  renderPointToProject
} from './spatial-render-adapter.js';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Browser } from '@capacitor/browser';

const NativePdfRenderer=registerPlugin('GeoPdfRenderer');
const BackgroundLocation =
    registerPlugin('BackgroundLocation');

let backgroundLocationListener = null;
let backgroundLocationErrorListener = null;
const nativeDocumentUrls=new WeakMap();

let pdfJsPromise=null;
async function loadPdfJs(){
  if(!pdfJsPromise){
    pdfJsPromise=import('pdfjs-dist/legacy/build/pdf.mjs').then(pdfjsLib=>{
      pdfjsLib.GlobalWorkerOptions.workerSrc=pdfWorkerUrl;
      return pdfjsLib;
    }).catch(error=>{
      pdfJsPromise=null;
      throw error;
    });
  }
  return pdfJsPromise;
}

function bytesBase64(bytes){
  let binary='';
  for(let offset=0;offset<bytes.length;offset+=32768)binary+=String.fromCharCode(...bytes.subarray(offset,Math.min(bytes.length,offset+32768)));
  return btoa(binary);
}
function loadRasterImage(source){
  return new Promise((resolve,reject)=>{const image=new Image();image.onload=()=>resolve(image);image.onerror=()=>reject(new Error('The native PDF raster could not be loaded.'));image.src=source;});
}
async function renderPdfNative(buffer,file){
  const path=nativeDocumentUrls.get(file),options=path?{path,maxDimension:4096}:{data:bytesBase64(new Uint8Array(buffer)),maxDimension:4096};
  const result=await NativePdfRenderer.render(options),image=await loadRasterImage(Capacitor.convertFileSrc(result.uri));
  const canvas=document.createElement('canvas');canvas.width=result.width;canvas.height=result.height;
  const context=canvas.getContext('2d',{alpha:false});context.fillStyle='#fff';context.fillRect(0,0,canvas.width,canvas.height);context.drawImage(image,0,0,canvas.width,canvas.height);
  return {canvas,pages:result.pages||1,destroy:async()=>{}};
}
async function renderPdfWeb(buffer){
  const pdfjsLib=await loadPdfJs();
  const documentTask=pdfjsLib.getDocument({data:new Uint8Array(buffer),isEvalSupported:false,verbosity:0});
  const pdf=await documentTask.promise,page=await pdf.getPage(1),baseViewport=page.getViewport({scale:1});
  const scale=Math.min(3,4096/Math.max(baseViewport.width,baseViewport.height)),viewport=page.getViewport({scale});
  const canvas=document.createElement('canvas');canvas.width=Math.ceil(viewport.width);canvas.height=Math.ceil(viewport.height);
  const context=canvas.getContext('2d',{alpha:false});
  await page.render({canvasContext:context,viewport,background:'rgb(255,255,255)'}).promise;
  return {canvas,pages:pdf.numPages,destroy:()=>documentTask.destroy()};
}

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);
const $ = selector => document.querySelector(selector);
const $$ = selector => [...document.querySelectorAll(selector)];
const ui = {
  canvas: $('#sceneCanvas'), viewport: $('#viewport'), drawer: $('#drawer'), scrim: $('#scrim'), layerList: $('#layerList'), empty: $('#emptyLayers'),
  fileInput: $('#fileInput'), processing: $('#processing'), progress: $('#progress'), processingTitle: $('#processingTitle'), processingDetail: $('#processingDetail'),
  status: $('#statusText'), readout: $('#coordinateReadout'), sectionPanel: $('#sectionPanel'), sectionCanvas: $('#sectionCanvas'), sectionInfo: $('#sectionInfo'), sectionMetrics:$('#sectionMetrics'),sectionWidth:$('#sectionWidth'),
  toast: $('#toast'), exag: $('#exaggeration'), exagValue: $('#exagValue'), exportMenu: $('#exportMenu'), compass: $('#compass'),
  coordinateSearch: $('#coordinateSearch'), contentSearch: $('#contentSearch'), coordinateE: $('#coordinateE'), coordinateN: $('#coordinateN'),
  boundaryPanel:$('#boundaryPanel'),boundaryInstruction:$('#boundaryInstruction'),volumeResults:$('#volumeResults'),boundaryElevation:$('#boundaryElevation'),
  gpsIndicator:$('#gpsIndicator'),gpsIndicatorText:$('#gpsIndicatorText'),gpsIndicatorArrow:$('#gpsIndicatorArrow'),
  projectHub:$('#projectHub'),recentProjects:$('#recentProjects'),noRecentProjects:$('#noRecentProjects'),newProjectForm:$('#newProjectForm'),newProjectName:$('#newProjectName'),currentProjectName:$('#currentProjectName'),projectCrs:$('#projectCrs'),projectCrsDetail:$('#projectCrsDetail'),
  nativeOpenTargetModal:$('#nativeOpenTargetModal'),nativeOpenFileName:$('#nativeOpenFileName'),nativeAddCurrentProject:$('#nativeAddCurrentProject'),nativeCreateProject:$('#nativeCreateProject'),nativeExistingProjectSection:$('#nativeExistingProjectSection'),nativeExistingProject:$('#nativeExistingProject'),nativeUseExistingProject:$('#nativeUseExistingProject'),nativeCancelOpen:$('#nativeCancelOpen'),
  crsAdvancedModal:$('#crsAdvancedModal'),crsAdvancedForm:$('#crsAdvancedForm'),crsAdvancedTitle:$('#crsAdvancedTitle'),crsAdvancedSubtitle:$('#crsAdvancedSubtitle'),crsAdvancedActions:$('#crsAdvancedActions'),crsAdvancedUtmFields:$('#crsAdvancedUtmFields'),crsAdvancedZone:$('#crsAdvancedZone'),crsAdvancedHemisphere:$('#crsAdvancedHemisphere'),crsAdvancedReferenceField:$('#crsAdvancedReferenceField'),crsAdvancedReference:$('#crsAdvancedReference'),crsAdvancedMessage:$('#crsAdvancedMessage'),crsAdvancedCancel:$('#crsAdvancedCancel'),
};

const state = {savedTracks: [], savedTracksLoaded: false, marksLoaded: false,
  layers: [], pdfLayers: [], savedBoundaries: [], savedBoundariesLoaded: false, projectOrigin: null, spatialFrame: null, renderFrame: RENDER_FRAME_PROJECTED, renderFrameReason: 'projected-requested', mode: '2d', tool: 'select', exaggeration: 1,
  section: { a: null, b: null, drag: null, press: null, last: [], metrics:null }, 
  measure: {mode: 'line',points: [],snaps: [],projectCoordinates: [],canonicalCoordinates: [],preview: null,
  finalized: false,result: null}, gpsWatch: null, installPrompt: null,
  gpsFix:null,tracking:{active:false,points:[],startedAt:null,nativeActive:false,projectCrs:null,crsStatus:null},marks:[],boundary:{points:[],closed:false,result:null,preview:null,referenceZ:null},
  currentProject:null,pendingOpenMode:null,layerOrderCounter:0,gridVisible:false,gridSpacing:null,draftGesture:null,lastDraftTap:null,activePointers:new Set(),
};

const SPATIAL_RENDER_REQUEST =
  new URLSearchParams(location.search).get('spatial-render') === 'enu'
    ? RENDER_FRAME_ENU
    : RENDER_FRAME_PROJECTED;
const SPATIAL_SHADOW_MODE =
  new URLSearchParams(location.search).get('spatial-shadow') || 'off';
const SPATIAL_SHADOW_ENABLED =
  SPATIAL_SHADOW_MODE === 'validate' ||
  SPATIAL_RENDER_REQUEST === RENDER_FRAME_ENU;
let spatialShadowWorker = null;
let spatialShadowJobCounter = 0;

function updateSpatialShadowProjectSummary() {
  if (!state.currentProject) return;
  const verifiedLayers = state.layers.filter(
    layer => layer.sourceCrsStatus === 'resolved'
  );
  const completed = verifiedLayers.filter(
    layer => layer.spatialShadowStatus === 'ready'
  );
  const passed = completed.filter(
    layer => layer.spatialShadow?.validation?.passed
  );
  const status = !SPATIAL_SHADOW_ENABLED
    ? 'disabled'
    : !verifiedLayers.length
      ? 'waiting-for-verified-layer'
      : completed.length < verifiedLayers.length
        ? 'building'
        : passed.length === verifiedLayers.length
          ? 'passed'
          : 'failed';

  state.currentProject = {
    ...state.currentProject,
    worldModel: {
      ...state.currentProject.worldModel,
      renderFrame: state.renderFrame,
      renderFrameReason: state.renderFrameReason,
      renderFrameRequested: SPATIAL_RENDER_REQUEST,
      shadowFrame: 'ENU',
      shadowMode: SPATIAL_SHADOW_MODE,
      shadowStatus: status,
      shadowVerifiedLayers: verifiedLayers.length,
      shadowPassedLayers: passed.length
    },
    updatedAt: Date.now()
  };
  scheduleProjectAutosave(250);
}

function ensureSpatialShadowWorker() {
  if (spatialShadowWorker) return spatialShadowWorker;
  spatialShadowWorker = new Worker(
    new URL('./spatial-shadow-worker.js', import.meta.url),
    { type: 'module' }
  );
  spatialShadowWorker.onmessage = event => {
    const message = event.data || {};
    const layer = state.layers.find(
      item => item.spatialShadowJobId === message.id
    );
    if (!layer) return;

    if (message.type === 'result' && message.shadow) {
      layer.spatialShadow = message.shadow;
      layer.spatialShadowStatus = 'ready';
      layer.spatialShadowJobId = null;
      if (!message.shadow.validation?.passed) {
        console.warn(
          '[Maply Spatial Frame] ENU shadow validation failed:',
          layer.result?.name,
          message.shadow.validation
        );
      } else {
        console.info(
          '[Maply Spatial Frame] ENU shadow ready:',
          {
            layer: layer.result?.name,
            vertices: message.shadow.vertexCount,
            validation: message.shadow.validation
          }
        );
      }
    } else {
      layer.spatialShadow = null;
      layer.spatialShadowStatus = 'error';
      layer.spatialShadowJobId = null;
      console.warn(
        '[Maply Spatial Frame] ENU shadow could not be built:',
        layer.result?.name,
        message.message || 'Unknown worker error'
      );
    }
    applySpatialRenderFrame();
    updateSpatialShadowProjectSummary();
  };
  spatialShadowWorker.onerror = error => {
    console.warn(
      '[Maply Spatial Frame] ENU shadow worker error:',
      error?.message || String(error)
    );
  };
  return spatialShadowWorker;
}

function resetSpatialShadowWorker() {
  spatialShadowWorker?.terminate();
  spatialShadowWorker = null;
}

function scheduleSpatialShadow(layer) {
  if (
    !SPATIAL_SHADOW_ENABLED ||
    !layer?.result ||
    layer.sourceCrsStatus !== 'resolved' ||
    !state.spatialFrame
  ) {
    if (layer) layer.spatialShadowStatus = 'unavailable';
    applySpatialRenderFrame();
    updateSpatialShadowProjectSummary();
    return;
  }

  const linePositions = layer.result.linePositions?.slice();
  const pointPositions = layer.result.pointPositions?.slice();
  const surfacePositions = layer.result.surface?.positions?.slice();
  const textPositions = layer.result.texts?.length
    ? new Float32Array(layer.result.texts.flatMap(item => [
        Number(item.x) - Number(layer.result.origin.x),
        Number(item.y) - Number(layer.result.origin.y),
        Number(item.z) || 0
      ]))
    : null;
  const transfer = [
    linePositions?.buffer,
    pointPositions?.buffer,
    surfacePositions?.buffer,
    textPositions?.buffer
  ].filter(Boolean);
  const id = ++spatialShadowJobCounter;
  layer.spatialShadow = null;
  layer.spatialShadowStatus = 'building';
  layer.spatialShadowJobId = id;
  updateSpatialShadowProjectSummary();

  ensureSpatialShadowWorker().postMessage({
    id,
    frame: state.spatialFrame,
    geometry: {
      origin: layer.result.origin,
      linePositions,
      pointPositions,
      surfacePositions,
      textPositions
    }
  }, transfer);
}

function canonicalPointBounds(points) {
  const valid = points
    .map(point => point?.canonical?.enu || point?.enu || null)
    .filter(point =>
      Number.isFinite(Number(point?.east)) &&
      Number.isFinite(Number(point?.north)) &&
      Number.isFinite(Number(point?.up))
    );
  if (!valid.length) return null;
  return valid.reduce((bounds, point) => ({
    minEast: Math.min(bounds.minEast, Number(point.east)),
    minNorth: Math.min(bounds.minNorth, Number(point.north)),
    minUp: Math.min(bounds.minUp, Number(point.up)),
    maxEast: Math.max(bounds.maxEast, Number(point.east)),
    maxNorth: Math.max(bounds.maxNorth, Number(point.north)),
    maxUp: Math.max(bounds.maxUp, Number(point.up))
  }), {
    minEast: Infinity,
    minNorth: Infinity,
    minUp: Infinity,
    maxEast: -Infinity,
    maxNorth: -Infinity,
    maxUp: -Infinity
  });
}

function spatialRenderAdditionalBounds() {
  return [
    ...state.pdfLayers.map(layer => layer.canonicalBounds),
    ...state.savedBoundaries.map(layer => layer.canonicalBounds),
    ...state.savedTracks.map(layer => layer.canonicalBounds),
    canonicalPointBounds(state.marks),
    canonicalPointBounds(state.gpsFix ? [state.gpsFix] : []),
    canonicalPointBounds(state.tracking.points)
  ].filter(Boolean);
}

function spatialRenderHasBlockers() {
  const missingCanonicalLayer = [
    ...state.pdfLayers,
    ...state.savedBoundaries,
    ...state.savedTracks
  ].some(layer => !layer.canonicalBounds);
  const missingCanonicalPoint = [
    ...state.marks,
    ...(state.gpsFix ? [state.gpsFix] : []),
    ...state.tracking.points
  ].some(point => !point?.canonical?.enu);
  return Boolean(
    missingCanonicalLayer ||
    missingCanonicalPoint
  );
}

function activeLayerPositions(layer, kind = 'line') {
  if (
    state.renderFrame === RENDER_FRAME_ENU &&
    layer?.activeRenderFrame === RENDER_FRAME_ENU
  ) {
    if (kind === 'point') return layer.spatialShadow?.pointPositions;
    if (kind === 'surface') return layer.spatialShadow?.surfacePositions;
    return layer.spatialShadow?.linePositions;
  }
  if (kind === 'point') return layer?.result?.pointPositions;
  if (kind === 'surface') return layer?.result?.surface?.positions;
  return layer?.result?.linePositions;
}

function setVectorLayerRenderFrame(layer, renderFrame) {
  if (!layer?.result || layer.activeRenderFrame === renderFrame) return;
  const useEnu =
    renderFrame === RENDER_FRAME_ENU &&
    layer.spatialShadow?.validation?.passed;
  const linePositions = useEnu
    ? layer.spatialShadow.linePositions
    : layer.result.linePositions;
  const pointPositions = useEnu
    ? layer.spatialShadow.pointPositions
    : layer.result.pointPositions;
  const surfacePositions = useEnu
    ? layer.spatialShadow.surfacePositions
    : layer.result.surface?.positions;

  if (linePositions) {
    layer.lines.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(linePositions, 3)
    );
    layer.lines.geometry.computeBoundingSphere();
  }
  if (layer.pointsObject && pointPositions) {
    layer.pointsObject.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(pointPositions, 3)
    );
    layer.pointsObject.geometry.computeBoundingSphere();
  }
  if (layer.surfaceMesh && surfacePositions) {
    layer.surfaceMesh.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(surfacePositions, 3)
    );
    layer.surfaceMesh.geometry.computeVertexNormals();
    layer.surfaceMesh.geometry.computeBoundingSphere();
  }

  if (useEnu) {
    layer.group.position.set(
      layer.spatialShadow.origin.east,
      layer.spatialShadow.origin.north,
      layer.spatialShadow.origin.up
    );
    layer.textSprites?.forEach((sprite, index) => {
      const offset = index * 3;
      const positions = layer.spatialShadow.textPositions;
      if (!positions || offset + 2 >= positions.length) return;
      sprite.position.set(
        positions[offset],
        positions[offset + 1],
        positions[offset + 2] + (sprite.userData.textLift || 0)
      );
    });
    layer.activeRenderFrame = RENDER_FRAME_ENU;
  } else {
    layer.group.position.set(
      layer.result.origin.x - (state.projectOrigin?.x || 0),
      layer.result.origin.y - (state.projectOrigin?.y || 0),
      0
    );
    layer.textSprites?.forEach((sprite, index) => {
      const item = layer.result.texts?.[index];
      if (!item) return;
      sprite.position.set(
        item.x - layer.result.origin.x,
        item.y - layer.result.origin.y,
        (Number(item.z) || 0) + (sprite.userData.textLift || 0)
      );
    });
    layer.activeRenderFrame = RENDER_FRAME_PROJECTED;
  }
}

function setSavedLineRenderFrame(layer, renderFrame, closePath = false) {
  const coordinates = layer?.record?.coordinates;
  const rendered = projectCoordinatesToRender(
    coordinates,
    spatialRenderOptions(renderFrame)
  );
  if (!rendered?.length) return false;
  const path = closePath ? [...rendered, rendered[0]] : rendered;
  layer.line.geometry.setPositions(
    path.flatMap(point => [point.x, point.y, point.z])
  );
  layer.line.computeLineDistances();
  layer.group.position.set(0, 0, 0);
  layer.activeRenderFrame = renderFrame;
  return true;
}

function setSurveyPointRenderFrame(point, renderFrame) {
  if (!point ||
      !Number.isFinite(Number(point.easting)) ||
      !Number.isFinite(Number(point.northing))) return false;
  const elevation = Number(point.rl) || 0;
  const rendered = projectPointToRender({
    x: Number(point.easting),
    y: Number(point.northing),
    z: elevation
  }, spatialRenderOptions(renderFrame));
  if (!rendered) return false;
  point.x = rendered.x;
  point.y = rendered.y;
  point.renderZ = rendered.z;
  point.displayZ = rendered.z * state.exaggeration + 8;
  if (point.node) {
    point.node.position.set(point.x, point.y, point.displayZ + 3);
  }
  return true;
}

function transformDraftPoint(point, fromFrame, toFrame) {
  if (!point || fromFrame === toFrame) return point;
  const projected = renderPointToProject({
    x: Number(point.x),
    y: Number(point.y),
    z: 0
  }, spatialRenderOptions(fromFrame));
  const rendered = projected
    ? projectPointToRender({ x: projected.x, y: projected.y, z: 0 }, spatialRenderOptions(toFrame))
    : null;
  return rendered ? new THREE.Vector2(rendered.x, rendered.y) : point;
}

function transformDraftSpatialContent(fromFrame, toFrame) {
  if (fromFrame === toFrame) return;
  state.boundary.points = state.boundary.points.map(point =>
    transformDraftPoint(point, fromFrame, toFrame)
  );
  if (state.boundary.preview) {
    state.boundary.preview = transformDraftPoint(
      state.boundary.preview,
      fromFrame,
      toFrame
    );
  }
  if (state.section.a) {
    state.section.a = transformDraftPoint(state.section.a, fromFrame, toFrame);
  }
  if (state.section.b) {
    state.section.b = transformDraftPoint(state.section.b, fromFrame, toFrame);
  }
}

function setNonVectorSpatialRenderFrame(renderFrame) {
  state.pdfLayers.forEach(layer => {
    rebuildGeoPdfGeometry(layer, renderFrame);
    layer.activeRenderFrame = renderFrame;
  });
  state.savedBoundaries.forEach(layer =>
    setSavedLineRenderFrame(layer, renderFrame, true)
  );
  state.savedTracks.forEach(layer =>
    setSavedLineRenderFrame(layer, renderFrame, false)
  );
  state.marks.forEach(mark =>
    setSurveyPointRenderFrame(mark, renderFrame)
  );
  state.tracking.points.forEach(point =>
    setSurveyPointRenderFrame(point, renderFrame)
  );
  if (state.tracking.points.length) updateTrackVisual();
  if (state.gpsFix) {
    setSurveyPointRenderFrame(state.gpsFix, renderFrame);
    updateGpsVisual();
  }
  refreshMeasureVisual();
  if (state.boundary.points.length) updateBoundaryVisual();
  if (state.section.a && state.section.b) updateSection();
}

function spatialRenderDecision() {
  return chooseSpatialRenderFrame({
    requestedFrame: SPATIAL_RENDER_REQUEST,
    layers: state.layers,
    additionalBounds: spatialRenderAdditionalBounds(),
    spatialFrame: state.spatialFrame,
    hasSpatialBlockers: spatialRenderHasBlockers()
  });
}

function applySpatialRenderFrame({ refit = true } = {}) {
  const decision = spatialRenderDecision();
  const changed = state.renderFrame !== decision.frame;
  const reasonChanged = state.renderFrameReason !== decision.reason;
  state.renderFrameReason = decision.reason;
  if (!changed) {
    if (reasonChanged && SPATIAL_RENDER_REQUEST === RENDER_FRAME_ENU) {
      console.info(
        '[Maply Spatial Frame] renderer kept projected: ' +
        JSON.stringify({
          reason: decision.reason,
          span: decision.span ?? null
        })
      );
    }
    return decision;
  }

  state.layers.forEach(layer =>
    setVectorLayerRenderFrame(layer, decision.frame)
  );
  transformDraftSpatialContent(state.renderFrame, decision.frame);
  state.renderFrame = decision.frame;
  setNonVectorSpatialRenderFrame(decision.frame);
  selectionOverlay && (selectionOverlay.visible = false);
  selectionFill && (selectionFill.visible = false);
  selectionHatch && (selectionHatch.visible = false);
  if (grid && decision.frame === RENDER_FRAME_ENU) grid.visible = false;
  if (refit) fitView();
  console.info(
    '[Maply Spatial Frame] renderer frame changed: ' +
    JSON.stringify({
      frame: decision.frame,
      reason: decision.reason,
      span: decision.span ?? null
    })
  );
  return decision;
}

function projectCoordinateToRender(x, y, z = 0) {
  return projectPointToRender({ x, y, z }, {
    renderFrame: state.renderFrame,
    projectOrigin: state.projectOrigin,
    spatialFrame: state.spatialFrame
  });
}

function renderCoordinateToProject(x, y, z = 0) {
  return renderPointToProject({ x, y, z }, {
    renderFrame: state.renderFrame,
    projectOrigin: state.projectOrigin,
    spatialFrame: state.spatialFrame
  });
}

function canonicalProjectCoordinate(x, y, z = 0) {
  return state.spatialFrame
    ? projectToCanonical(x, y, z, state.spatialFrame)
    : null;
}

function canonicalProjectCoordinates(coordinates) {
  if (!state.spatialFrame || !Array.isArray(coordinates)) return null;
  return coordinates.map(([x, y, z = 0]) =>
    canonicalProjectCoordinate(Number(x), Number(y), Number(z) || 0)
  );
}

function spatialRenderOptions(renderFrame = state.renderFrame) {
  return {
    renderFrame,
    projectOrigin: state.projectOrigin,
    spatialFrame: state.spatialFrame
  };
}

const renderer = new THREE.WebGLRenderer({ canvas: ui.canvas, antialias: true, alpha: false, preserveDrawingBuffer: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setClearColor(0x0b1118);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x0b1118, 0.00011);
scene.add(
  new THREE.HemisphereLight(
    0xb9ddff,
    0x26321f,
    1.15
  )
);

const sun =
  new THREE.DirectionalLight(
    0xffffff,
    2.8
  );

sun.position.set(
  -900,
  -650,
  1400
);

scene.add(sun);
let grid = new THREE.GridHelper(6000,60,0x426273,0x243746);grid.rotation.x=Math.PI/2;grid.position.z=-.2;grid.material.opacity=.3;grid.material.transparent=true;grid.visible=false;scene.add(grid);

const systemTheme=matchMedia('(prefers-color-scheme: dark)');
function applySystemTheme(){
  const css=getComputedStyle(document.documentElement),mapBackground=css.getPropertyValue('--map-bg').trim()||'#0b1118',panelColor=css.getPropertyValue('--panel').trim()||'#111c28';
  renderer.setClearColor(mapBackground);scene.fog.color.set(mapBackground);
  document.documentElement.dataset.colorScheme=systemTheme.matches?'dark':'light';
  const themeMeta=document.querySelector('meta[name="theme-color"]');if(themeMeta)themeMeta.content=panelColor;
}
applySystemTheme();
if(systemTheme.addEventListener)systemTheme.addEventListener('change',()=>requestAnimationFrame(applySystemTheme));
else systemTheme.addListener(()=>requestAnimationFrame(applySystemTheme));

const perspective = new THREE.PerspectiveCamera(45, 1, 0.5, 100000);
const ortho = new THREE.OrthographicCamera(-500, 500, 500, -500, -10000, 10000);
perspective.position.set(900, -900, 800); ortho.position.set(0, 0, 1500);
perspective.up.set(0, 0, 1); ortho.up.set(0, 1, 0);
const controls3d = new OrbitControls(perspective, ui.canvas); controls3d.enableDamping = true; controls3d.dampingFactor = 0.08; controls3d.screenSpacePanning = true;
const controls2d = new OrbitControls(ortho, ui.canvas); controls2d.enableDamping = true; controls2d.enableRotate = false; controls2d.screenSpacePanning = true;
controls2d.mouseButtons.LEFT=THREE.MOUSE.PAN;controls2d.touches.ONE=THREE.TOUCH.PAN;controls2d.touches.TWO=THREE.TOUCH.DOLLY_PAN;
// ==========================================
// 2D TWO-FINGER GESTURE
// 1 jari  = pan oleh OrbitControls
// 2 jari:
//   - jarak berubah dominan = pinch zoom
//   - sudut berubah dominan = twist rotate
//   - titik tengah bergeser = pan
// Twist hanya mengubah heading 2D, tidak tilt.
// ==========================================

const rotate2dPointers = new Map();

let rotate2dLastAngle = null;
let rotate2dLastDistance = null;
let rotate2dGesture = null;


// ------------------------------------------
// Ambil sudut garis antara dua jari
// ------------------------------------------
function twoFingerAngle2d() {
  if (rotate2dPointers.size !== 2) return null;

  const points = [...rotate2dPointers.values()];

  return Math.atan2(
    points[1].y - points[0].y,
    points[1].x - points[0].x
  );
}


// ------------------------------------------
// Ambil jarak antara dua jari
// ------------------------------------------
function twoFingerDistance2d() {
  if (rotate2dPointers.size !== 2) return null;

  const points = [...rotate2dPointers.values()];

  return Math.hypot(
    points[1].x - points[0].x,
    points[1].y - points[0].y
  );
}


// ------------------------------------------
// Jaga delta sudut agar tidak lompat
// dari +PI ke -PI
// ------------------------------------------
function normalizeAngleDelta2d(angle) {
  while (angle > Math.PI) {
    angle -= Math.PI * 2;
  }

  while (angle < -Math.PI) {
    angle += Math.PI * 2;
  }

  return angle;
}


// ------------------------------------------
// POINTER DOWN
// Simpan posisi masing-masing jari
// ------------------------------------------
ui.canvas.addEventListener(
  'pointerdown',
  (event) => {
    if (
      state.mode !== '2d' ||
      event.pointerType !== 'touch'
    ) {
      return;
    }

    rotate2dPointers.set(
      event.pointerId,
      {
        x: event.clientX,
        y: event.clientY
      }
    );

    // Jari kedua baru masuk.
    // Catat kondisi awal gesture.
    if (rotate2dPointers.size === 2) {
      rotate2dLastAngle =
        twoFingerAngle2d();

      rotate2dLastDistance =
        twoFingerDistance2d();

      // Belum diputuskan pinch atau twist.
      rotate2dGesture = null;

      // Pastikan zoom awalnya tetap aktif.
      controls2d.enableZoom = true;
    }
  }
);


// ------------------------------------------
// POINTER MOVE
// Tentukan apakah user sedang pinch / twist
// ------------------------------------------
ui.canvas.addEventListener(
  'pointermove',
  (event) => {
    if (
      state.mode !== '2d' ||
      event.pointerType !== 'touch' ||
      !rotate2dPointers.has(event.pointerId)
    ) {
      return;
    }

    // Update posisi jari yang bergerak.
    rotate2dPointers.set(
      event.pointerId,
      {
        x: event.clientX,
        y: event.clientY
      }
    );

    if (
      rotate2dPointers.size !== 2 ||
      rotate2dLastAngle === null ||
      rotate2dLastDistance === null
    ) {
      return;
    }

    const angle =
      twoFingerAngle2d();

    const distance =
      twoFingerDistance2d();

    if (
      angle === null ||
      distance === null
    ) {
      return;
    }


    // --------------------------------------
    // Seberapa besar perubahan sudut?
    // --------------------------------------
    const angleDelta =
      normalizeAngleDelta2d(
        angle - rotate2dLastAngle
      );


    // --------------------------------------
    // Seberapa besar perubahan jarak?
    // --------------------------------------
    const distanceDelta =
      Math.abs(
        distance - rotate2dLastDistance
      );


    // --------------------------------------
    // KLASIFIKASI GESTURE
    //
    // angleDelta sendiri berupa radian,
    // jadi dikalikan jarak antar jari
    // agar skalanya lebih mudah dibandingkan
    // dengan perubahan jarak dalam pixel.
    // --------------------------------------
    if (!rotate2dGesture) {
      const twistAmount =
        Math.abs(angleDelta) * distance;

      // Jika perubahan sudut jelas lebih dominan,
      // anggap gesture sebagai TWIST.
      if (
        twistAmount > 4 &&
        twistAmount > distanceDelta * 1.4
      ) {
        rotate2dGesture = 'twist';

        // Saat twist, matikan zoom agar tidak
        // ikut zoom in/out karena jarak jari
        // berubah sedikit secara tidak sengaja.
        controls2d.enableZoom = false;
      }

      // Kalau perubahan jarak yang dominan,
      // anggap sebagai PINCH.
      else if (distanceDelta > 3) {
        rotate2dGesture = 'pinch';

        controls2d.enableZoom = true;
      }
    }


    // --------------------------------------
    // TWIST MODE
    // --------------------------------------
    if (
      rotate2dGesture === 'twist'
    ) {
      // Jitter kecil diabaikan.
      if (
        Math.abs(angleDelta) >= 0.002
      ) {
        // POSITIF delta:
        // arah map mengikuti arah twist jari.
        const cos =
          Math.cos(angleDelta);

        const sin =
          Math.sin(angleDelta);

        const oldX =
          ortho.up.x;

        const oldY =
          ortho.up.y;


        // Putar vector UP kamera
        // hanya pada bidang XY.
        ortho.up.set(
          oldX * cos - oldY * sin,
          oldX * sin + oldY * cos,
          0
        ).normalize();


        // Paksa posisi kamera tetap tepat
        // di atas target agar tidak tilt.
        ortho.position.x =
          controls2d.target.x;

        ortho.position.y =
          controls2d.target.y;


        ortho.lookAt(
          controls2d.target
        );

        ortho.updateProjectionMatrix();
      }
    }


    // --------------------------------------
    // Update baseline untuk move berikutnya
    // --------------------------------------
    rotate2dLastAngle =
      angle;

    rotate2dLastDistance =
      distance;
  }
);


// ------------------------------------------
// POINTER UP / CANCEL
// ------------------------------------------
function finishRotate2dPointer(event) {
  rotate2dPointers.delete(
    event.pointerId
  );


  // Kalau masih tepat dua pointer,
  // reset baseline dari posisi terbaru.
  if (
    rotate2dPointers.size === 2
  ) {
    rotate2dLastAngle =
      twoFingerAngle2d();

    rotate2dLastDistance =
      twoFingerDistance2d();
  }

  else {
    rotate2dLastAngle = null;
    rotate2dLastDistance = null;
    rotate2dGesture = null;

    // Kembalikan zoom setelah gesture selesai.
    controls2d.enableZoom = true;
  }
}


ui.canvas.addEventListener(
  'pointerup',
  finishRotate2dPointer
);

ui.canvas.addEventListener(
  'pointercancel',
  finishRotate2dPointer
);
let activeCamera = ortho, activeControls = controls2d;
controls3d.enabled = false;

const sectionGroup = new THREE.Group(); scene.add(sectionGroup);
const sectionLineGeometry = new LineGeometry();
const sectionHaloGeometry = new LineGeometry();
const sectionHaloMaterial = new LineMaterial({ color: 0x21051e, linewidth: 6.5, worldUnits: false, depthTest: false, transparent: true, opacity: .88 });
const sectionHalo = new Line2(sectionHaloGeometry, sectionHaloMaterial); sectionHalo.renderOrder = 19;
const sectionLineMaterial = new LineMaterial({ color: 0xff3bea, linewidth: 3.5, worldUnits: false, depthTest: false, transparent: false });
const sectionLine = new Line2(sectionLineGeometry, sectionLineMaterial); sectionLine.renderOrder = 20;
const markerMaterialA = new THREE.MeshBasicMaterial({ color: 0x34d2b7, depthTest: false });
const markerMaterialB = new THREE.MeshBasicMaterial({ color: 0xff716b, depthTest: false });
const markerMaterialM = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false });
const markerGeometry = new THREE.SphereGeometry(7, 16, 10);
const markerA = new THREE.Mesh(markerGeometry, markerMaterialA), markerB = new THREE.Mesh(markerGeometry, markerMaterialB), markerM = new THREE.Mesh(markerGeometry, markerMaterialM);
function endpointLabel(text, color) {
  const canvas = document.createElement('canvas'); canvas.width = 96; canvas.height = 96;
  const context = canvas.getContext('2d'); context.fillStyle = '#071018dd'; context.beginPath(); context.arc(48, 48, 38, 0, Math.PI * 2); context.fill();
  context.strokeStyle = color; context.lineWidth = 6; context.stroke(); context.fillStyle = '#ffffff'; context.font = '800 48px system-ui'; context.textAlign = 'center'; context.textBaseline = 'middle'; context.fillText(text, 48, 50);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, depthTest: false })); sprite.scale.set(22, 22, 1); sprite.renderOrder = 25; return sprite;
}
const labelA = endpointLabel('A', '#34d2b7'), labelB = endpointLabel('B', '#ff716b');
[sectionHalo, sectionLine, markerA, markerB, markerM, labelA, labelB].forEach(item => { item.visible = false; sectionGroup.add(item); });

const measureGroup = new THREE.Group(); scene.add(measureGroup);
const measureHaloGeometry = new LineGeometry(), measureLineGeometry = new LineGeometry();
const measureHaloMaterial = new LineMaterial({
  color: 0x082c32,
  linewidth: 6.5,
  worldUnits: false,
  depthTest: false,
  transparent: true,
  opacity: 0.9
});

const measureLineMaterial = new LineMaterial({
  color: 0x52e0d0,
  linewidth: 3.2,
  worldUnits: false,
  depthTest: false
});
const measureHalo = new Line2(measureHaloGeometry, measureHaloMaterial), measureLine = new Line2(measureLineGeometry, measureLineMaterial);
measureHalo.visible = measureLine.visible = false; measureHalo.renderOrder = 20; measureLine.renderOrder = 21; measureGroup.add(measureHalo,measureLine);
const measurePointOuterGeometry =
  new THREE.RingGeometry(3.4, 5, 24);

const measurePointInnerGeometry =
  new THREE.CircleGeometry(3, 24);

const measurePointOuterMaterial =
  new THREE.MeshBasicMaterial({
    color: 0x082c32,
    depthTest: false
  });

const measurePointInnerMaterial =
  new THREE.MeshBasicMaterial({
    color: 0x52e0d0,
    depthTest: false
  });

const measurePoints = [];

function ensureMeasurePointMarkers(count) {
  while (measurePoints.length < count) {
    const marker =
      new THREE.Group();

    const outer =
      new THREE.Mesh(
        measurePointOuterGeometry,
        measurePointOuterMaterial
      );

    const inner =
      new THREE.Mesh(
        measurePointInnerGeometry,
        measurePointInnerMaterial
      );

    outer.renderOrder = 22;
    inner.renderOrder = 23;

    marker.add(outer);
    marker.add(inner);

    marker.visible = false;

    measureGroup.add(marker);
    measurePoints.push(marker);
  }
}
const measureAreaFill = new THREE.Mesh(
  new THREE.BufferGeometry(),
  new THREE.MeshBasicMaterial({
    color: 0x52e0d0,
    transparent: true,
    opacity: 0.18,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false
  })
);

measureAreaFill.visible = false;
measureAreaFill.renderOrder = 19;
measureGroup.add(measureAreaFill);
function hideUnusedMeasurePointMarkers(count = 0) {
  measurePoints.forEach(
    (marker, index) => {
      marker.visible = index < count;
    }
  );
}

let gpsGroup = null;
const selectMarker = new THREE.Mesh(new THREE.RingGeometry(7, 9, 28), new THREE.MeshBasicMaterial({ color: 0xffe273, side: THREE.DoubleSide, depthTest: false })); selectMarker.visible = false; selectMarker.renderOrder = 30; scene.add(selectMarker);
const selectionGeometry=new LineSegmentsGeometry(),selectionMaterial=new LineMaterial({color:0xffdf3a,linewidth:7,worldUnits:false,depthTest:false,transparent:false});
const selectionOverlay=new LineSegments2(selectionGeometry,selectionMaterial);selectionOverlay.visible=false;selectionOverlay.renderOrder=29;scene.add(selectionOverlay);
// --- TAMBAHAN UNTUK ARSIRAN & BLOK KUNING SELECTION ---
const selectionFill = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({color: 0xffdf3a, transparent: true, opacity: 0.25, side: THREE.DoubleSide, depthTest: false, depthWrite: false}));
selectionFill.visible = false;
selectionFill.renderOrder = 27;
scene.add(selectionFill);

const selectionHatchCanvas = document.createElement('canvas');
selectionHatchCanvas.width = 64; selectionHatchCanvas.height = 64;
const selectionHatchContext = selectionHatchCanvas.getContext('2d');
selectionHatchContext.strokeStyle = 'rgba(255, 223, 58, 0.85)';
selectionHatchContext.lineWidth = 4;
selectionHatchContext.beginPath();
selectionHatchContext.moveTo(-12, 64); selectionHatchContext.lineTo(64, -12);
selectionHatchContext.moveTo(20, 76); selectionHatchContext.lineTo(76, 20);
selectionHatchContext.stroke();
const selectionHatchTexture = new THREE.CanvasTexture(selectionHatchCanvas);
selectionHatchTexture.wrapS = selectionHatchTexture.wrapT = THREE.RepeatWrapping;
selectionHatchTexture.colorSpace = THREE.SRGBColorSpace;

const selectionHatch = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({map: selectionHatchTexture, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthTest: false, depthWrite: false}));
selectionHatch.visible = false;
selectionHatch.renderOrder = 28;
scene.add(selectionHatch);
const coordinateMarker = new THREE.Mesh(new THREE.RingGeometry(10, 14, 32), new THREE.MeshBasicMaterial({ color: 0x4b9fff, side: THREE.DoubleSide, depthTest: false })); coordinateMarker.visible = false; coordinateMarker.renderOrder = 30; scene.add(coordinateMarker);
let trackGeometry=new LineGeometry(),trackMaterial=new LineMaterial({color:0x38a9ff,linewidth:4,worldUnits:false,depthTest:false,transparent:true,opacity:.95});
const trackLine=new Line2(trackGeometry,trackMaterial);trackLine.visible=false;trackLine.renderOrder=22;scene.add(trackLine);
const marksGroup=new THREE.Group();scene.add(marksGroup);
const boundaryHaloGeometry=new LineGeometry(),boundaryHaloMaterial=new LineMaterial({color:0x260809,linewidth:5.25,worldUnits:false,depthTest:false,transparent:true,opacity:.92});
const boundaryHalo=new Line2(boundaryHaloGeometry,boundaryHaloMaterial);boundaryHalo.visible=false;boundaryHalo.renderOrder=23;scene.add(boundaryHalo);
const boundaryGeometry=new LineGeometry(),boundaryMaterial=new LineMaterial({color:0xff514d,linewidth:2.6,worldUnits:false,depthTest:false,transparent:false});
const boundaryLine=new Line2(boundaryGeometry,boundaryMaterial);boundaryLine.visible=false;boundaryLine.renderOrder=24;scene.add(boundaryLine);
const boundaryFill=new THREE.Mesh(new THREE.BufferGeometry(),new THREE.MeshBasicMaterial({color:0xff4b45,transparent:true,opacity:.18,side:THREE.DoubleSide,depthTest:false,depthWrite:false}));boundaryFill.visible=false;boundaryFill.renderOrder=20;scene.add(boundaryFill);
const boundaryHatchCanvas=document.createElement('canvas');boundaryHatchCanvas.width=64;boundaryHatchCanvas.height=64;const boundaryHatchContext=boundaryHatchCanvas.getContext('2d');boundaryHatchContext.strokeStyle='rgba(255,65,60,.9)';boundaryHatchContext.lineWidth=4;boundaryHatchContext.beginPath();boundaryHatchContext.moveTo(-12,64);boundaryHatchContext.lineTo(64,-12);boundaryHatchContext.moveTo(20,76);boundaryHatchContext.lineTo(76,20);boundaryHatchContext.stroke();
const boundaryHatchTexture=new THREE.CanvasTexture(boundaryHatchCanvas);boundaryHatchTexture.wrapS=boundaryHatchTexture.wrapT=THREE.RepeatWrapping;boundaryHatchTexture.colorSpace=THREE.SRGBColorSpace;
const boundaryHatch=new THREE.Mesh(new THREE.BufferGeometry(),new THREE.MeshBasicMaterial({map:boundaryHatchTexture,transparent:true,opacity:.72,side:THREE.DoubleSide,depthTest:false,depthWrite:false}));boundaryHatch.visible=false;boundaryHatch.renderOrder=22;scene.add(boundaryHatch);
const boundaryVertices=new THREE.Group();scene.add(boundaryVertices);
const boundaryPointGeometry=new THREE.SphereGeometry(5,12,8),boundaryPointMaterial=new THREE.MeshBasicMaterial({color:0xff4b45,depthTest:false});
const boundaryAreaCanvas=document.createElement('canvas');boundaryAreaCanvas.width=768;boundaryAreaCanvas.height=192;
const boundaryAreaTexture=new THREE.CanvasTexture(boundaryAreaCanvas),boundaryAreaLabel=new THREE.Sprite(new THREE.SpriteMaterial({map:boundaryAreaTexture,transparent:true,depthTest:false}));
boundaryAreaLabel.visible=false;boundaryAreaLabel.scale.set(180,45,1);boundaryAreaLabel.renderOrder=34;scene.add(boundaryAreaLabel);
const measureCalloutEngine =
  createCalloutLabel(scene, {
    color: 0x52e0d0,
    textColor: '#52e0d0',
    leaderWidth: 2.2,
    offsetX: 82,
    offsetY: 56,
    renderOrder: 40,
  });

measureCalloutEngine.hide();
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
const ground = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

function resize() {
  const width = ui.viewport.clientWidth, height = ui.viewport.clientHeight;
  renderer.setSize(width, height, false);
  sectionLineMaterial.resolution.set(width, height);sectionHaloMaterial.resolution.set(width,height);measureLineMaterial.resolution.set(width,height);measureHaloMaterial.resolution.set(width,height);measureCalloutEngine.leaderMaterial.resolution.set(width,height);
  selectionMaterial.resolution.set(width,height);
  trackMaterial.resolution.set(width,height);boundaryHaloMaterial.resolution.set(width,height);boundaryMaterial.resolution.set(width,height);state.savedBoundaries.forEach(layer=>layer.material.resolution.set(width,height));
  perspective.aspect = width / height; perspective.updateProjectionMatrix();
  const centerX = (ortho.left + ortho.right) / 2, centerY = (ortho.top + ortho.bottom) / 2;
  const vertical = ortho.top - ortho.bottom;
  ortho.left = centerX - vertical * width / height / 2; ortho.right = centerX + vertical * width / height / 2;
  ortho.top = centerY + vertical / 2; ortho.bottom = centerY - vertical / 2; ortho.updateProjectionMatrix();
  drawSection();
// --- KODE TAMBAHAN UNTUK ANOTASI INTERAKTIF ---
  if (state.section.hoverX !== undefined && state.section.hoverX !== null && data.length > 0) {
    const hoverX = state.section.hoverX;
    
    if (hoverX >= m.l && hoverX <= w - m.r) {
      const index = Math.max(0, Math.min(data.length - 1, Math.round((hoverX - m.l) / (w - m.l - m.r) * (data.length - 1))));
      const p = data[index];
      const cx = x(index);

      if (p.top !== null && p.bottom !== null) {
        const yTop = y(p.top);
        const yBot = y(p.bottom);

        // 1. Gambar Garis Vertikal (Hanya di antara Top dan Bottom)
        ctx.strokeStyle = '#ff3bea';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(cx, yTop);
        ctx.lineTo(cx, yBot);
        ctx.stroke();
        ctx.setLineDash([]);

        // 2. Siapkan Teks Informasi
        const topRl = p.top.toFixed(2);
        const botRl = p.bottom.toFixed(2);
        let diffText = '-';
        if (p.delta !== null) {
          diffText = Math.abs(p.delta).toFixed(2);
          diffText = p.delta > 0 ? `Cut: ${diffText}m` : (p.delta < 0 ? `Fill: ${diffText}m` : `On Grade`);
        }

        // 3. Posisi Kotak Tooltip Dinamis
        const boxW = 100, boxH = 50;
        const boxX = cx + 10 + boxW > w - m.r ? cx - 15 - boxW : cx + 10;
        // Letakkan kotak sedikit di atas garis atau di bawah garis agar tak menutupi
        const boxY = Math.min(yTop, yBot) - boxH - 10 > m.t ? Math.min(yTop, yBot) - boxH - 10 : Math.max(yTop, yBot) + 10;

        ctx.fillStyle = 'rgba(11, 17, 24, 0.9)'; 
        ctx.beginPath(); ctx.roundRect(boxX, boxY, boxW, boxH, 6); ctx.fill();
        ctx.strokeStyle = '#486276'; ctx.stroke();

        ctx.font = '11px system-ui';
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffad5c'; ctx.fillText(`Top: ${topRl}`, boxX + 10, boxY + 16);
        ctx.fillStyle = '#6fe7ff'; ctx.fillText(`Bot: ${botRl}`, boxX + 10, boxY + 30);
        ctx.fillStyle = p.delta > 0 ? '#ff5c52' : (p.delta < 0 ? '#2c8dff' : '#35d782');
        ctx.fillText(diffText, boxX + 10, boxY + 44);
      }
    }
  }
}
function updateMeasurePointSizes() {
  if (!measurePoints.length) return;

  const height =
    Math.max(1, ui.viewport.clientHeight);

  const targetRadiusPx = 6;
  const geometryRadius = 5;

  measurePoints.forEach(marker => {
    if (!marker.visible) return;

    const point = marker.position;

    const unitsPerPixel =
      state.mode === '2d'
        ? (ortho.top - ortho.bottom) /
          Math.max(0.001, ortho.zoom) /
          height
        : 2 *
          Math.max(
            1,
            perspective.position.distanceTo(point)
          ) *
          Math.tan(
            THREE.MathUtils.degToRad(
              perspective.fov / 2
            )
          ) /
          height;

    const scale =
      unitsPerPixel *
      targetRadiusPx /
      geometryRadius;

    marker.scale.setScalar(
      Math.max(0.0001, scale)
    );
  });
}
function measurePerimeter(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;

  let total = 0;

  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    total += Math.hypot(
      b.x - a.x,
      b.y - a.y
    );
  }

  return total;
}
function formatMeasureLength(meters) {
  const value = Number(meters) || 0;

  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(2)} km`;
  }

  return `${value.toFixed(2)} m`;
}

function formatMeasureArea(squareMeters) {
  const value = Math.abs(Number(squareMeters) || 0);

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)} km²`;
  }

  if (value >= 10000) {
    return `${(value / 10000).toFixed(2)} ha`;
  }

  return `${value.toFixed(2)} m²`;
}

function getMeasureSegmentLength() {
  const points = state.measure?.projectCoordinates || [];

  if (points.length < 2) return 0;

  const a = points[points.length - 2];
  const b = points[points.length - 1];

  return Math.hypot(
    b.x - a.x,
    b.y - a.y
  );
}
function getMeasureLineMidpoint() {
  const points =
    state.measure?.projectCoordinates || [];

  if (points.length < 2) return null;

  let totalLength = 0;
  const segments = [];

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];

    const length =
      Math.hypot(
        b.x - a.x,
        b.y - a.y
      );

    segments.push({
      a,
      b,
      length
    });

    totalLength += length;
  }

  if (totalLength <= 0) return null;

  const half = totalLength / 2;
  let travelled = 0;

  for (const segment of segments) {
    if (
      travelled + segment.length >= half
    ) {
      const t =
        (half - travelled) /
        segment.length;

      return {
        x:
          segment.a.x +
          (segment.b.x - segment.a.x) * t,

        y:
          segment.a.y +
          (segment.b.y - segment.a.y) * t,

        z:
          (Number(segment.a.z) || 0) +
          ((Number(segment.b.z) || 0) -
            (Number(segment.a.z) || 0)) *
            t
      };
    }

    travelled += segment.length;
  }

  return points[points.length - 1];
}
function updateMeasureLiveLabel() {
  const mode =
    state.measure?.mode || 'line';

  const confirmedPoints =
    state.measure?.projectCoordinates || [];

  const preview =
    state.measure?.preview || null;

  const workingPoints =
    preview
      ? [...confirmedPoints, preview]
      : [...confirmedPoints];

  let text = '';
  let anchor = null;

  if (mode === 'area') {
    if (workingPoints.length < 3) {
      measureCalloutEngine.hide();
      return;
    }

    const metricPoints =
      workingPoints.map(
        point =>
          new THREE.Vector2(
            point.x,
            point.y
          )
      );

    const metrics =
      polygonMetrics(metricPoints);

    if (metrics.area < 0.01) {
      measureCalloutEngine.hide();
      return;
    }

    text =
      formatMeasureArea(metrics.area);

    anchor = {
      x: metrics.centroid.x,
      y: metrics.centroid.y,
      z:
        workingPoints.reduce(
          (sum, point) =>
            sum + (Number(point.z) || 0),
          0
        ) / workingPoints.length
    };
  } else {
    if (workingPoints.length < 2) {
      measureCalloutEngine.hide();
      return;
    }

    let totalLength = 0;
    const segments = [];

    for (
      let i = 1;
      i < workingPoints.length;
      i++
    ) {
      const a = workingPoints[i - 1];
      const b = workingPoints[i];

      const length =
        Math.hypot(
          b.x - a.x,
          b.y - a.y
        );

      segments.push({
        a,
        b,
        length
      });

      totalLength += length;
    }

    if (totalLength <= 0) {
      measureCalloutEngine.hide();
      return;
    }

    text =
      formatMeasureLength(totalLength);

    anchor =
      preview ||
      workingPoints[
        workingPoints.length - 1
      ];
    }
  if (!anchor) {
    measureCalloutEngine.hide();
    return;
  }

  const rendered =
    projectCoordinateToRender(
      anchor.x,
      anchor.y,
      anchor.z
    );

  if (!rendered) {
    measureCalloutEngine.hide();
    return;
  }

const target =
  new THREE.Vector3(
    rendered.x,
    rendered.y,
    rendered.z *
      state.exaggeration +
      12
  );

const height =
  Math.max(
    1,
    ui.viewport.clientHeight
  );

const unitsPerPixel =
  state.mode === '2d'
    ? (ortho.top - ortho.bottom) /
      Math.max(0.001, ortho.zoom) /
      height
    : 2 *
      Math.max(
        1,
        perspective.position.distanceTo(
          target
        )
      ) *
      Math.tan(
        THREE.MathUtils.degToRad(
          perspective.fov / 2
        )
      ) /
      height;

const viewportWidth =
  ui.viewport.clientWidth || 1;

const viewportHeight =
  ui.viewport.clientHeight || 1;

const projectedScreen =
  target
    .clone()
    .project(activeCamera);

const screenX =
  (projectedScreen.x + 1) *
  0.5 *
  viewportWidth;

const screenY =
  (1 - projectedScreen.y) *
  0.5 *
  viewportHeight;

measureCalloutEngine.updateLayout({
  text,
  target,
  unitsPerPixel,
  screenX,
  screenY,
  viewportWidth,
  viewportHeight,
});
}
function getMeasureAreaCentroid() {
  const points = state.measure?.projectCoordinates || [];

  if (points.length < 3) return null;

  let signedArea = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    const cross =
      a.x * b.y -
      b.x * a.y;

    signedArea += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }

  signedArea *= 0.5;

  if (Math.abs(signedArea) < 1e-9) {
    return null;
  }

  cx /= 6 * signedArea;
  cy /= 6 * signedArea;

  return {
    x: cx,
    y: cy
  };
}
function updateMeasureAreaFill(points) {
  if (
    state.measure?.mode !== 'area' ||
    !Array.isArray(points) ||
    points.length < 3
  ) {
    measureAreaFill.visible = false;
    return;
  }

  const base = points[0];
  const shape = new THREE.Shape();

  shape.moveTo(0, 0);

  for (let i = 1; i < points.length; i++) {
    shape.lineTo(
      points[i].x - base.x,
      points[i].y - base.y
    );
  }

  shape.closePath();

  measureAreaFill.geometry.dispose();
  measureAreaFill.geometry =
    new THREE.ShapeGeometry(shape);

  const averageZ =
    points.reduce(
      (sum, point) => sum + (point.z || 0),
      0
    ) / points.length;

  measureAreaFill.position.set(
    base.x,
    base.y,
    averageZ + 1
  );

  measureAreaFill.visible = true;
}
function setMeasureLinePositions(positions) {
  if (!Array.isArray(positions) || positions.length < 6) {
    measureHalo.visible = false;
    measureLine.visible = false;
    return;
  }

  measureHalo.geometry.dispose();
  measureLine.geometry.dispose();

  const haloGeometry = new LineGeometry();
  const lineGeometry = new LineGeometry();

  haloGeometry.setPositions(positions);
  lineGeometry.setPositions(positions);

  measureHalo.geometry = haloGeometry;
  measureLine.geometry = lineGeometry;

  measureHalo.computeLineDistances();
  measureLine.computeLineDistances();

  measureHalo.material.resolution.set(
    ui.viewport.clientWidth || 1,
    ui.viewport.clientHeight || 1
  );

  measureLine.material.resolution.set(
    ui.viewport.clientWidth || 1,
    ui.viewport.clientHeight || 1
  );

  measureHalo.visible = true;
  measureLine.visible = true;
}
new ResizeObserver(resize).observe(ui.viewport);
window.visualViewport?.addEventListener('resize',()=>requestAnimationFrame(resize));window.addEventListener('orientationchange',()=>setTimeout(resize,120));

function niceGridSpacing(value){const power=10**Math.floor(Math.log10(Math.max(value,.001))),fraction=value/power,step=fraction<=1?1:fraction<=2?2:fraction<=5?5:10;return step*power;}
function gridWorldPerPixel(){
  const height=Math.max(1,ui.viewport.clientHeight);if(state.mode==='2d')return (ortho.top-ortho.bottom)/Math.max(.001,ortho.zoom)/height;
  return 2*Math.max(1,perspective.position.distanceTo(controls3d.target))*Math.tan(THREE.MathUtils.degToRad(perspective.fov/2))/height;
}
function rebuildAdaptiveGrid(spacing){
  scene.remove(grid);grid.geometry.dispose();grid.material.dispose();grid=new THREE.GridHelper(spacing*60,60,0x426273,0x557381);grid.rotation.x=Math.PI/2;grid.material.opacity=.28;grid.material.transparent=true;grid.material.depthWrite=false;grid.position.z=-.2;grid.visible=state.gridVisible;scene.add(grid);state.gridSpacing=spacing;const button=$('#gridButton');if(button)button.title=`Adaptive Grid · ${spacing} m interval`;
}
function updateAdaptiveGrid(){
  if(!state.gridVisible)return;const spacing=niceGridSpacing(gridWorldPerPixel()*85);if(spacing!==state.gridSpacing)rebuildAdaptiveGrid(spacing);const target=activeControls.target;grid.position.x=Math.round(target.x/spacing)*spacing;grid.position.y=Math.round(target.y/spacing)*spacing;
}
function toggleGrid(){state.gridVisible=!state.gridVisible;if(state.gridVisible){updateAdaptiveGrid();grid.visible=true;showToast(`Adaptive grid enabled · ${state.gridSpacing} m interval`);}else{grid.visible=false;showToast('Grid hidden.');}$('#gridButton').classList.toggle('active',state.gridVisible);}

function animate() {
  requestAnimationFrame(animate);
  activeControls.update();
  updateAdaptiveGrid();
  updateGpsMarkerSize();
  updatePositionMarkSizes();
  updateBoundaryVertexSizes();
  updateMeasurePointSizes();
  updateBoundaryAreaLabelSize();
  updateMeasureLiveLabelSize();
  const direction = new THREE.Vector3(); activeCamera.getWorldDirection(direction);
  const azimuth = Math.atan2(direction.x, direction.y) * 180 / Math.PI;
  ui.compass.style.transform = `rotate(${azimuth}deg)`;
  renderer.render(scene, activeCamera);
}
animate();

function showToast(message, ms = 2600) {
  ui.toast.textContent = message; ui.toast.classList.add('show');
  clearTimeout(showToast.timer); showToast.timer = setTimeout(() => ui.toast.classList.remove('show'), ms);
}
function setBusy(show, title = '', detail = '', progress = 0) {
  ui.processing.classList.toggle('hidden', !show); ui.processingTitle.textContent = title; ui.processingDetail.textContent = detail; ui.progress.value = progress;
}
function setStatus() {
  const segments = state.layers.reduce((sum, l) => sum + (l.result.segments || 0), 0);
  const parts = [];
  if (state.layers.length) parts.push(`${state.layers.length} vector layer${state.layers.length===1?'':'s'}`, `${segments.toLocaleString('en-US')} segments`);
  if (state.pdfLayers.length) parts.push(`${state.pdfLayers.length} GeoPDF`);
  ui.status.textContent = parts.length ? `${parts.join(' · ')} · offline` : 'Ready to open data';
}
function openDrawer(open = true) { ui.drawer.classList.toggle('open', open); ui.scrim.classList.toggle('active', open && innerWidth < 960); }

const ACTIVE_PROJECT_KEY='eagle-terrain-active-project-v1';
function projectDisplayDate(timestamp){
  if(!timestamp)return 'Not opened yet';
  const date=new Date(timestamp),today=new Date(),sameDay=date.toDateString()===today.toDateString();
  return sameDay?`Today, ${date.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`:date.toLocaleDateString([], {day:'2-digit',month:'short',year:'numeric'});
}
function projectNameFromFiles(files){
  const first=[...files][0];
  return first?.name?.replace(/\.(dxf|pdf|kml|kmz|shp|prj)$/i,'').replace(/[_-]+/g,' ').trim()||`Project ${new Date().toLocaleDateString()}`;
}
function normalizeProjectCrs(value){
  if(value?.type==='utm')return utmCrs(value.zone,value.hemisphere);
  const match=String(value||'').match(/EPSG:(326|327)(\d{2})/i);
  if(match)return utmCrs(Number(match[2]),match[1]==='326'?'N':'S');
  return null;
}
function currentProjectCrs() {
  return normalizeProjectCrs(
    state.currentProject?.crs
  );
}
function refreshProjectSpatialFrame() {
  state.spatialFrame = createProjectSpatialFrame(
    currentProjectCrs(),
    state.currentProject?.worldAnchor
  );
  return state.spatialFrame;
}
function projectCrsStatus() {
  if (!state.currentProject) return 'pending';

  if (state.currentProject.crsStatus) {
    return state.currentProject.crsStatus;
  }

  // Kompatibilitas project lama
  if (state.currentProject.crs) {
    return state.currentProject.crsMode === 'manual'
      ? 'confirmed'
      : 'suggested';
  }

  return 'pending';
}

function isProjectCrsConfirmed() {
  return projectCrsStatus() === 'confirmed';
}
function gpsSuggestedCrs(latitude, longitude) {
  const zone =
    Math.floor((Number(longitude) + 180) / 6) + 1;

  const hemisphere =
    Number(latitude) >= 0 ? 'N' : 'S';

  return utmCrs(zone, hemisphere);
}
function trackingProjectCrs() {
  return normalizeProjectCrs(
    state.tracking?.projectCrs
  );
}
function boundsGapScore(a,b){
  if(!a||!b)return Infinity;const dx=a.maxX<b.minX?b.minX-a.maxX:b.maxX<a.minX?a.minX-b.maxX:0,dy=a.maxY<b.minY?b.minY-a.maxY:b.maxY<a.minY?a.minY-b.maxY:0,scale=Math.max(100,Math.hypot(a.maxX-a.minX,a.maxY-a.minY));return Math.hypot(dx,dy)/scale;
}
function bestGeoPdfProjection(metadata){
  const resolvedLayers=state.layers.filter(layer=>layer.sourceCrsStatus!=='unknown'),reference=resolvedLayers.find(layer=>layer.role==='top')?.result.bounds||resolvedLayers[0]?.result.bounds;if(!reference)return {metadata,aligned:false,score:Infinity};
  let best={metadata,score:boundsGapScore(reference,metadata.bounds)};
  for(let zone=1;zone<=60;zone++)for(const hemisphere of ['S','N']){const candidate=reprojectGeoPdfMetadata(metadata,utmCrs(zone,hemisphere)),score=boundsGapScore(reference,candidate.bounds);if(score<best.score)best={metadata:candidate,score};}
  return {...best,aligned:best.score<=1};
}
function updateProjectCrsUi() {
  const crs = currentProjectCrs();
  const status = projectCrsStatus();
  let label = 'CRS Pending';
  let detail = 'Coordinate system not confirmed yet';
  if (crs) {
    if (status === 'confirmed') {
      label = `UTM ${crs.zone}${crs.hemisphere} 🔒`;
      detail = `${crs.name} · EPSG:${crs.epsg} · Confirmed`;
    } else if (status === 'suggested') {
      label = `UTM ${crs.zone}${crs.hemisphere} · Suggested`;
      detail = `${crs.name} · EPSG:${crs.epsg} · Waiting for confirmation`;
    } else {
      label = `UTM ${crs.zone}${crs.hemisphere}`;
      detail = `${crs.name} · EPSG:${crs.epsg}`;
    }
  }

  if (ui.projectCrs) {
    ui.projectCrs.textContent = label;
  }

  if (ui.projectCrsDetail) {
    ui.projectCrsDetail.textContent = detail;
  }
}
async function assignProjectCrs(
  crs,
  source = 'manual',
  {
    status = null,
    force = false
  } = {}
) {
  const normalized =
    normalizeProjectCrs(crs);

  if (!normalized || !state.currentProject) {
    return false;
  }

  const current =
    currentProjectCrs();

  const currentStatus =
    projectCrsStatus();

  const activeTrackCrs =
    trackingProjectCrs();

  // Satu GPS track wajib memakai satu Project CRS tetap.
  // Konfirmasi EPSG yang sama tetap diperbolehkan, tetapi zona
  // tidak boleh berubah sampai tracking dihentikan.
  if (
    state.tracking.active &&
    activeTrackCrs &&
    activeTrackCrs.epsg !== normalized.epsg &&
    !force
  ) {
    console.warn(
      '[Maply CRS] Ignored CRS change while GPS tracking is active:',
      {
        track: activeTrackCrs.epsg,
        requested: normalized.epsg,
        source
      }
    );

    return false;
  }

  // CRS confirmed tidak boleh diubah otomatis
  if (
    current &&
    currentStatus === 'confirmed' &&
    !force
  ) {
    if (current.epsg !== normalized.epsg) {
      console.warn(
        '[Maply CRS] Ignored CRS change because project CRS is locked:',
        {
          current: current.epsg,
          requested: normalized.epsg,
          source
        }
      );

      return false;
    }
  }
  // Suggestion dengan EPSG yang sama tidak boleh menurunkan
  // Project CRS yang sudah confirmed menjadi suggested.
  if (
    current &&
    currentStatus === 'confirmed' &&
    current.epsg === normalized.epsg &&
    source !== 'manual' &&
    !force
  ) {
    return true;
  }

  let nextStatus = status;

  if (!nextStatus) {
    nextStatus =
      source === 'manual'
        ? 'confirmed'
        : 'suggested';
  }


  state.currentProject = {
    ...state.currentProject,

    crs: normalized,

    crsMode: source,

    crsStatus: nextStatus,

    crsSource: source,

    crsAlgorithmVersion: 2,

    updatedAt: Date.now()
  };

  refreshProjectSpatialFrame();

  // Project CRS dan Source CRS setiap layer adalah state terpisah.
  // DXF Unknown hanya boleh diselesaikan melalui keputusan user
  // pada workflow Source CRS per-layer.
  state.pdfLayers.forEach(layer => {
    layer.metadata =
      reprojectGeoPdfMetadata(
        layer.metadata,
        normalized
      );

    layer.canonicalBounds = state.spatialFrame
      ? canonicalBoundsFromProjectBounds(layer.metadata.bounds, state.spatialFrame)
      : null;

    rebuildGeoPdfGeometry(layer);
  });


  await saveProject(
    state.currentProject
  );

  updateProjectCrsUi();

  console.log(
    '[Maply CRS] project CRS updated:',
    {
      crs: normalized,
      status: nextStatus,
      source
    }
  );

  return true;
}
let crsAdvancedResolver=null;
let crsAdvancedContext=null;
function crsAdvancedOption({value,title,description,disabled=false}){
  const label=document.createElement('label');label.className='crs-advanced-option';
  const input=document.createElement('input');input.type='radio';input.name='crsAdvancedAction';input.value=value;input.disabled=disabled;
  const copy=document.createElement('span');const heading=document.createElement('b');heading.textContent=title;const detail=document.createElement('small');detail.textContent=description;copy.append(heading,detail);label.append(input,copy);return label;
}
function finishCrsAdvanced(value){
  ui.crsAdvancedModal.classList.add('hidden');
  const resolve=crsAdvancedResolver;crsAdvancedResolver=null;crsAdvancedContext=null;resolve?.(value);
}
function availableSameGridLayers(targetLayer){
  return sameGridCandidates(state.layers,targetLayer);
}
function selectedCrsAdvancedAction(){
  return ui.crsAdvancedActions.querySelector('input[name="crsAdvancedAction"]:checked')?.value||null;
}
function syncCrsAdvancedFields(){
  const action=selectedCrsAdvancedAction();
  ui.crsAdvancedUtmFields.classList.toggle('hidden',!['manual-project','source-utm'].includes(action));
  ui.crsAdvancedReferenceField.classList.toggle('hidden',action!=='same-grid');
  const messages={
    'keep-automatic':'Maply will keep detecting location from georeferenced data or GPS.',
    'confirm-current':'This locks the current Project CRS for survey and export.',
    'manual-project':'Only use this when the project UTM zone is known.',
    'auto-geopdf':'Maply compares GeoPDF candidates with a verified vector reference.',
    'keep-raw':'The file remains visible at its original numbers; global location stays unverified.',
    'assign-project':'Coordinates do not change. Use only when the file already uses the Project CRS.',
    'source-utm':'Maply converts the original UTM coordinates into the Project CRS.',
    'source-geographic':'Maply treats X as longitude and Y as latitude in WGS84.',
    'same-grid':'Files remain in their shared numeric grid. This does not invent a global location.'
  };
  ui.crsAdvancedMessage.classList.remove('error');
  ui.crsAdvancedMessage.textContent=messages[action]||'';
}
function openCrsAdvancedDialog({scope,layer=null}={}){
  if(crsAdvancedResolver)finishCrsAdvanced(null);
  crsAdvancedContext={scope,layer};
  const projectCrs=currentProjectCrs();
  const current=projectCrs||utmCrs(50,'S');
  ui.crsAdvancedZone.value=String(layer?.sourceCrs?.zone||current.zone);
  ui.crsAdvancedHemisphere.value=layer?.sourceCrs?.hemisphere||current.hemisphere;
  ui.crsAdvancedActions.replaceChildren();ui.crsAdvancedReference.replaceChildren();
  let options=[];let defaultAction='keep-automatic';
  if(scope==='project'){
    ui.crsAdvancedTitle.textContent='Advanced project location';
    ui.crsAdvancedSubtitle.textContent=projectCrs?`${projectCrs.name} · EPSG:${projectCrs.epsg} · ${projectCrsStatus()}`:'No Project CRS yet. Normal import remains automatic.';
    options=[
      {value:'keep-automatic',title:'Keep automatic',description:'Let georeferenced data or GPS determine project location.'},
      {value:'confirm-current',title:'Confirm current suggestion',description:projectCrs?`Lock ${projectCrs.name} for survey and export.`:'No current suggestion is available.',disabled:!projectCrs},
      {value:'manual-project',title:'Set WGS84 / UTM manually',description:'Advanced override when the correct zone is known.'},
      {value:'auto-geopdf',title:'AUTO match GeoPDF',description:'Compare GeoPDF placement with a verified vector reference.',disabled:!state.pdfLayers.length}
    ];
    if(projectCrs&&projectCrsStatus()==='suggested')defaultAction='confirm-current';
  }else{
    const format=layer?.result?.format||'file';
    const projectLabel=projectCrs?`${projectCrs.name} · EPSG:${projectCrs.epsg}`:'not set';
    ui.crsAdvancedTitle.textContent=`Advanced location · ${format}`;
    ui.crsAdvancedSubtitle.textContent=`${layer?.result?.name||'Map data'} · Project CRS ${projectLabel}`;
    const references=availableSameGridLayers(layer);
    for(const reference of references){const option=document.createElement('option');option.value=reference.id;option.textContent=`${reference.result.name} · ${reference.sourceCrsStatus==='resolved'?'verified CRS':'location unverified'}`;ui.crsAdvancedReference.append(option);}
    options=[
      {value:'keep-raw',title:'Keep raw coordinates',description:'Continue showing the file without claiming a global location.'},
      {value:'assign-project',title:'Assign Project CRS',description:'Coordinates stay unchanged because they already use the Project CRS.',disabled:!projectCrs},
      {value:'source-utm',title:'Original coordinates are WGS84 / UTM',description:'Select the original zone and let Maply reproject when needed.'},
      {value:'source-geographic',title:'Original coordinates are WGS84 longitude/latitude',description:'Use for numeric X/Y values stored in degrees.'},
      {value:'same-grid',title:'Same local grid as another file',description:'Link files that share the same numeric coordinate system.',disabled:references.length===0}
    ];
    defaultAction='keep-raw';
  }
  options.forEach(option=>ui.crsAdvancedActions.append(crsAdvancedOption(option)));
  const defaultInput=ui.crsAdvancedActions.querySelector(`input[value="${defaultAction}"]:not(:disabled)`)||ui.crsAdvancedActions.querySelector('input:not(:disabled)');
  if(defaultInput)defaultInput.checked=true;
  syncCrsAdvancedFields();ui.crsAdvancedModal.classList.remove('hidden');
  return new Promise(resolve=>{crsAdvancedResolver=resolve;});
}
ui.crsAdvancedActions.addEventListener('change',syncCrsAdvancedFields);
ui.crsAdvancedCancel.addEventListener('click',()=>finishCrsAdvanced(null));
ui.crsAdvancedForm.addEventListener('submit',event=>{
  event.preventDefault();const action=selectedCrsAdvancedAction();if(!action)return;
  const value={action};
  if(['manual-project','source-utm'].includes(action)){
    const zone=Number(ui.crsAdvancedZone.value);if(!Number.isInteger(zone)||zone<1||zone>60){ui.crsAdvancedMessage.textContent='UTM zone must be an integer from 1 to 60.';ui.crsAdvancedMessage.classList.add('error');return;}
    value.zone=zone;value.hemisphere=ui.crsAdvancedHemisphere.value;
  }
  if(action==='same-grid'){
    if(!ui.crsAdvancedReference.value){ui.crsAdvancedMessage.textContent='Choose a reference layer.';ui.crsAdvancedMessage.classList.add('error');return;}
    value.referenceLayerId=ui.crsAdvancedReference.value;
  }
  finishCrsAdvanced(value);
});
async function promptProjectCrs(){
  const decision=await openCrsAdvancedDialog({scope:'project'});if(!decision||decision.action==='keep-automatic')return;
  if(decision.action==='auto-geopdf'){
    const pdf=state.pdfLayers[0];if(!pdf)return showToast('Open a GeoPDF before using AUTO.',4500);const choice=bestGeoPdfProjection(pdf.metadata);if(!choice.aligned)return showToast('AUTO could not match the GeoPDF with a verified vector reference. Use control-point alignment.',6000);const applied=await assignProjectCrs(choice.metadata.projectCrs,'manual');if(!applied)return showToast('Project CRS is confirmed and locked. AUTO did not change it.',5500);renderLayerList();fitView();return showToast(`AUTO matched ${choice.metadata.projectCrs.name}.`,5200);
  }
  const target=decision.action==='confirm-current'?currentProjectCrs():utmCrs(decision.zone,decision.hemisphere);if(!target)return;
  const applied=await assignProjectCrs(target,'manual',{status:'confirmed'});if(!applied)return showToast('Project CRS is already confirmed and cannot be changed without reprojecting project data.',6000);renderLayerList();showToast(`Project CRS confirmed as ${target.name}.`,5000);
}
async function promptDxfSourceCrs(layer) {
  let projectCrs = currentProjectCrs();
  const choice=await openCrsAdvancedDialog({scope:'layer',layer});
  if(!choice)return null;
  if(choice.action==='keep-raw'){
    return {
      action: 'keep-raw',
      sourceCrs: null,
      sourceCrsStatus: 'unknown',
      projectCrs
    };
  }

  if(choice.action==='same-grid')return choice;
  if(choice.action==='assign-project'){
    if (!projectCrs) {
      showToast(
        'Project CRS is not set yet. Select the known Source CRS instead.',
        5500
      );
      return null;
    }

    if (!isProjectCrsConfirmed()) {
      await assignProjectCrs(projectCrs, 'manual', { status: 'confirmed' });
      projectCrs = currentProjectCrs();
    }

    return {
      action: 'assign',
      sourceCrs: projectCrs,
      sourceCrsStatus: 'resolved',
      projectCrs
    };
  }

  if(choice.action==='source-utm'||choice.action==='source-geographic'){
    const sourceCrs=choice.action==='source-geographic'
      ? {type:'geographic',datum:'WGS84',epsg:4326,name:'WGS 84',unit:'degree',status:'resolved'}
      : utmCrs(choice.zone,choice.hemisphere);

    if (!projectCrs) {
      let suggestedProject=sourceCrs;
      if(sourceCrs.type==='geographic'){
        const bounds=layer.result.sourceBounds||layer.result.bounds;const centerLon=(bounds.minX+bounds.maxX)/2,centerLat=(bounds.minY+bounds.maxY)/2,natural=latLonToUtm(centerLat,centerLon);suggestedProject=utmCrs(natural.zone,natural.hemisphere);
      }
      await assignProjectCrs(suggestedProject, 'manual', { status: 'confirmed' });
      projectCrs = currentProjectCrs();
    } else if (!isProjectCrsConfirmed()) {
      await assignProjectCrs(projectCrs, 'manual', { status: 'confirmed' });
      projectCrs = currentProjectCrs();
    }

    return {
      action:
        sourceCrs.epsg === projectCrs.epsg
          ? 'assign'
          : 'reproject',

      sourceCrs,
      sourceCrsStatus: 'resolved',
      projectCrs
    };
  }
  return null;
}

function restoredDxfSourceCrsDecision(fileName) {
  const setting = savedLayerSetting(fileName);

  if (
    !setting ||
    !Object.prototype.hasOwnProperty.call(
      setting,
      'sourceCrsStatus'
    )
  ) {
    return null;
  }

  if (
    setting.sourceCrsStatus !== 'resolved' ||
    !setting.sourceCrs
  ) {
    return {
      action: 'keep-raw',
      sourceCrs: null,
      sourceCrsStatus: 'unknown',
      projectCrs:
        isProjectCrsConfirmed()
          ? currentProjectCrs()
          : null
    };
  }

  const sourceCrs =
    setting.sourceCrs?.type === 'geographic' ||
    Number(setting.sourceCrs?.epsg) === 4326
      ? {
          type: 'geographic',
          datum: 'WGS84',
          epsg: 4326,
          name: 'WGS 84',
          unit: 'degree',
          status: 'resolved'
        }
      : normalizeProjectCrs(setting.sourceCrs);

  const projectCrs = currentProjectCrs();

  if (
    !sourceCrs ||
    !projectCrs ||
    !isProjectCrsConfirmed()
  ) {
    return null;
  }

  return {
    action:
      sourceCrs.epsg === projectCrs.epsg
        ? 'assign'
        : 'reproject',
    sourceCrs,
    sourceCrsStatus: 'resolved',
    projectCrs
  };
}
function resolvedSpatialReferenceBounds() {
  const candidates = [
    ...state.layers
      .filter(layer => layer.sourceCrsStatus === 'resolved')
      .map(layer => layer.result?.bounds),
    ...state.pdfLayers.map(layer => layer.bounds)
  ]
    .filter(Boolean)
    .map(bounds => ({
      bounds,
      span: Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY)
    }))
    .filter(item => Number.isFinite(item.span) && item.span <= 100000)
    .sort((a, b) => a.span - b.span);

  // A national/continental extent is not strong enough evidence for assigning
  // an unknown DXF to the Project CRS merely because its numbers fall inside it.
  return candidates[0]?.bounds || null;
}
function setCurrentProject(project){
  state.currentProject=project||null;
  refreshProjectSpatialFrame();
  if(project){localStorage.setItem(ACTIVE_PROJECT_KEY,project.id);ui.currentProjectName.textContent=project.name;}
  else{localStorage.removeItem(ACTIVE_PROJECT_KEY);ui.currentProjectName.textContent='No active project';}
  updateProjectCrsUi();
}
// ==========================================
// ENGINE BASEMAP SATELIT DINAMIS (SLIPPY MAP)
// ==========================================

// Rumus matematika standar web mapping
function lon2tile(lon, zoom) { return Math.floor((lon + 180) / 360 * Math.pow(2, zoom)); }
function lat2tile(lat, zoom) { return Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom)); }
function tile2lon(x, z) { return (x / Math.pow(2, z) * 360 - 180); }
function tile2lat(y, z) {
  const n = Math.PI - 2 * Math.PI * y / Math.pow(2, z);
  return (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
}

// Sistem Penyimpanan Offline (Cache API)
const BASEMAP_CACHE = 'eagle-basemap-v1';
async function fetchTile(url) {
  if (!('caches' in window)) return url;
  try {
    const cache = await caches.open(BASEMAP_CACHE);
    const cached = await cache.match(url);
    if (cached) return URL.createObjectURL(await cached.blob()); // Jika pernah dilihat, ambil dari offline

    const res = await fetch(url, { mode: 'cors' });
    if (res.ok) {
      cache.put(url, res.clone()); // Simpan ke HP agar besok bisa offline
      return URL.createObjectURL(await res.blob());
    }
  } catch (e) { }
  return url;
}

let basemapGroup = null;
let isBasemapVisible = false;
const loadedTiles = new Set(); // Mengingat kotak mana saja yang sudah dimuat di layar
const textureLoader = new THREE.TextureLoader();

// Fungsi untuk memuat 1 kotak gambar saja
function loadSingleTile(x, y, zoom, crs) {
  const tileKey = `${zoom}/${x}/${y}`;
  if (loadedTiles.has(tileKey)) return; // Jangan muat ulang jika sudah ada
  loadedTiles.add(tileKey); // Tandai sedang/sudah dimuat

  const url = `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`;
  
  fetchTile(url).then(blobUrl => {
    if (!isBasemapVisible || !basemapGroup) return;

    textureLoader.load(blobUrl, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      
      const tileNwLat = tile2lat(y, zoom);
      const tileNwLon = tile2lon(x, zoom);
      const tileSeLat = tile2lat(y + 1, zoom);
      const tileSeLon = tile2lon(x + 1, zoom);

      const nwUtm = latLonToUtm(tileNwLat, tileNwLon, crs.zone, crs.hemisphere);
      const seUtm = latLonToUtm(tileSeLat, tileSeLon, crs.zone, crs.hemisphere);

      const tileW = seUtm.easting - nwUtm.easting;
      const tileH = nwUtm.northing - seUtm.northing; 
      const centerX = nwUtm.easting + tileW / 2 - state.projectOrigin.x;
      const centerY = seUtm.northing + tileH / 2 - state.projectOrigin.y;

      const bounds = projectBounds();
      const minZ = bounds ? (bounds.minZ * state.exaggeration) - 5 : -5;

      const geo = new THREE.PlaneGeometry(tileW, tileH);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, depthWrite: false, transparent: true, opacity: 0.85 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(centerX, centerY, minZ);
      mesh.renderOrder = -100;
      
      if (basemapGroup) basemapGroup.add(mesh);
    });
  });
}

// Fungsi pendeteksi pergerakan kamera layar
function updateDynamicBasemap() {
  if (!isBasemapVisible || !state.projectOrigin) return;
  const crs = currentProjectCrs();
  if (!crs) return;

  const target = activeControls.target; // Titik tengah layar
  
  // Kalkulasi seberapa luas area yang sedang dilihat kamera (Viewport Bounds)
  let viewRadius = 1000; 
  if (state.mode === '2d') {
    viewRadius = ((ortho.top - ortho.bottom) / Math.max(0.001, ortho.zoom)) * 0.7; // Buffer 70% di luar layar
  } else {
    viewRadius = perspective.position.distanceTo(target) * Math.tan(THREE.MathUtils.degToRad(perspective.fov / 2)) * 1.5;
  }
  
  // Batasi agar tidak lag jika di zoom-out ke seluruh provinsi
  if (viewRadius > 30000) return; 

 // Level Zoom Dinamis (Ditingkatkan ke Resolusi Tinggi)
  let zoom = 18; 
  if (viewRadius > 10000) zoom = 13;      
  else if (viewRadius > 5000) zoom = 14;  
  else if (viewRadius > 2000) zoom = 15;  
  else if (viewRadius > 800) zoom = 17;   
  else zoom = 19;

  // Hitung batas UTM yang tampil di layar
  const minUtmX = target.x - viewRadius + state.projectOrigin.x;
  const maxUtmX = target.x + viewRadius + state.projectOrigin.x;
  const minUtmY = target.y - viewRadius + state.projectOrigin.y;
  const maxUtmY = target.y + viewRadius + state.projectOrigin.y;

  const sw = utmToLatLon(minUtmX, minUtmY, crs.zone, crs.hemisphere);
  const ne = utmToLatLon(maxUtmX, maxUtmY, crs.zone, crs.hemisphere);

  const txMin = lon2tile(sw.longitude, zoom);
  const txMax = lon2tile(ne.longitude, zoom);
  const tyMin = Math.min(lat2tile(sw.latitude, zoom), lat2tile(ne.latitude, zoom));
  const tyMax = Math.max(lat2tile(sw.latitude, zoom), lat2tile(ne.latitude, zoom));

  // Cegah unduhan masif jika layar dipaksa terlalu jauh (Safety Switch)
  if ((txMax - txMin) * (tyMax - tyMin) > 60) return;

  // Render kotak-kotak yang ada di layar
  for (let x = txMin; x <= txMax; x++) {
    for (let y = tyMin; y <= tyMax; y++) {
      loadSingleTile(x, y, zoom, crs);
    }
  }
}

// Fungsi Eksekusi Tombol Basemap
function toggleBasemap() {
  if (!state.projectOrigin || !currentProjectCrs()) return showToast('Buka data spasial dan set CRS untuk memuat satelit.');

  if (isBasemapVisible) {
    // HANYA SEMBUNYIKAN (Tidak dihapus dari RAM agar bisa dipanggil instan)
    if (basemapGroup) basemapGroup.visible = false;
    isBasemapVisible = false;
    
    controls2d.removeEventListener('change', updateDynamicBasemap);
    controls3d.removeEventListener('change', updateDynamicBasemap);
    $('#basemapBtn').style.opacity = "1";
    showToast('Basemap disembunyikan.');
  } else {
    // NYALAKAN KEMBALI
    isBasemapVisible = true;
    $('#basemapBtn').style.opacity = "0.5";
    
    if (!basemapGroup) {
      // Jika baru pertama kali diklik, buat grup baru
      showToast('Memuat Basemap Dinamis...', 2000);
      basemapGroup = new THREE.Group();
      basemapGroup.renderOrder = -10;
      scene.add(basemapGroup);
    } else {
      // Jika sudah pernah dibuat, langsung munculkan!
      basemapGroup.visible = true; 
    }
    
    updateDynamicBasemap();
    controls2d.addEventListener('change', updateDynamicBasemap);
    controls3d.addEventListener('change', updateDynamicBasemap);
  }
}
async function refreshProjectHub(){
  const projects=await listProjects();
  ui.recentProjects.replaceChildren();
  ui.noRecentProjects.classList.toggle('hidden',projects.length>0);
  $('#recentProjectsSummary').textContent=projects.length?`${projects.length} project${projects.length===1?'':'s'} saved on this device`:'Saved on this device';
  projects.forEach(project=>{
    const card=document.createElement('article');card.className='recent-project-card';card.dataset.projectId=project.id;card.tabIndex=0;card.setAttribute('role','button');card.setAttribute('aria-label',`Open ${project.name}`);
    const icon=document.createElement('span');icon.className='recent-project-icon';icon.textContent=(project.name.trim()[0]||'E').toUpperCase();
    const copy=document.createElement('span');copy.className='recent-project-copy';const name=document.createElement('b');name.textContent=project.name;
    const detail=document.createElement('small');const mapFileCount =Number(project.fileCount) || 0;
    const trackCount =
  Array.isArray(project.tracks)
    ? project.tracks.length
    : 0;

const markCount =
  Array.isArray(project.marks)
    ? project.marks.length
    : 0;

const boundaryCount =
  Array.isArray(project.boundaries)
    ? project.boundaries.length
    : 0;

const parts = [];

parts.push(
  `${mapFileCount} map file${mapFileCount === 1 ? '' : 's'}`
);

if (trackCount) {
  parts.push(
    `${trackCount} track${trackCount === 1 ? '' : 's'}`
  );
}

if (markCount) {
  parts.push(
    `${markCount} mark${markCount === 1 ? '' : 's'}`
  );
}

if (boundaryCount) {
  parts.push(
    `${boundaryCount} boundar${boundaryCount === 1 ? 'y' : 'ies'}`
  );
}

parts.push(
  project.storageMode === 'hybrid'
    ? 'Cloud + offline'
    : 'Offline'
);

parts.push(
  projectDisplayDate(
    project.updatedAt
  )
);

detail.textContent =
  parts.join(' · ');copy.append(name,detail);
    const remove=document.createElement('button');remove.type='button';remove.className='recent-project-delete';remove.textContent='×';remove.title='Delete project from this device';remove.setAttribute('aria-label',`Delete ${project.name}`);
    remove.addEventListener('click',async event=>{event.stopPropagation();if(!confirm(`Delete “${project.name}” and its offline cache from this device?\n\nThe original files in iCloud, Drive, or OneDrive will not be deleted.`))return;await deleteProject(project.id);localStorage.removeItem(`eagle-terrain-boundaries-v1:${project.id}`);localStorage.removeItem(`eagle-terrain-tracks-v1:${project.id}`);localStorage.removeItem(`eagle-terrain-marks-v1:${project.id}`);if(state.currentProject?.id===project.id){clearProject(false);setCurrentProject(null);}await refreshProjectHub();});
    card.addEventListener('click',()=>activateProject(project));card.addEventListener('keydown',event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();activateProject(project);}});card.append(icon,copy,remove);ui.recentProjects.append(card);
  });
}
function isValidProjectOrigin(origin) {
  return !!origin &&
    Number.isFinite(Number(origin.x)) &&
    Number.isFinite(Number(origin.y));
}

function originFromBounds(bounds) {
  if (!bounds) return null;

  const minX = Number(bounds.minX);
  const minY = Number(bounds.minY);
  const maxX = Number(bounds.maxX);
  const maxY = Number(bounds.maxY);

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return null;
  }

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return {
    x: Math.floor(centerX / 1000) * 1000,
    y: Math.floor(centerY / 1000) * 1000,
    elevation: 0
  };
}

function adoptProjectOrigin(origin, source = 'unknown') {
  if (isValidProjectOrigin(state.projectOrigin)) {
    return state.projectOrigin;
  }

  if (!isValidProjectOrigin(origin)) {
    return null;
  }

  state.projectOrigin = {
    x: Number(origin.x),
    y: Number(origin.y),
    elevation: Number(origin.elevation) || 0
  };

  if (state.currentProject) {
    state.currentProject = {
      ...state.currentProject,

      projectOrigin: {
        ...state.projectOrigin
      },

      projectOriginSource: source,
      updatedAt: Date.now()
    };

    saveProject(state.currentProject).catch(error => {
      console.warn(
        '[Maply] Project origin could not be saved:',
        error
      );
    });
  }

  console.log(
    '[Maply] projectOrigin adopted:',
    state.projectOrigin,
    'source:',
    source
  );

  return state.projectOrigin;
}

function canRebaseProjectOrigin() {
  return state.gpsWatch === null &&
    !state.gpsFix &&
    !state.tracking.active &&
    state.tracking.points.length === 0 &&
    state.marks.length === 0 &&
    state.savedTracks.length === 0 &&
    state.savedBoundaries.length === 0 &&
    state.boundary.points.length === 0 &&
    !state.section.a &&
    !state.section.b &&
    state.measure.points.length === 0 &&
    !(basemapGroup?.children.length);
}

function rebaseProjectOrigin(origin, source = 'spatial-file') {
  if (!isValidProjectOrigin(state.projectOrigin) ||
      !isValidProjectOrigin(origin) ||
      !canRebaseProjectOrigin()) return false;

  const next = {
    x: Number(origin.x),
    y: Number(origin.y),
    elevation: Number(origin.elevation) || 0
  };
  const shiftX = state.projectOrigin.x - next.x;
  const shiftY = state.projectOrigin.y - next.y;
  if (Math.hypot(shiftX, shiftY) < 1) return false;

  state.layers.forEach(layer => {
    layer.group.position.x += shiftX;
    layer.group.position.y += shiftY;
    layer.sampler?.translate(shiftX, shiftY);
  });
  state.pdfLayers.forEach(layer => {
    layer.mesh.position.x += shiftX;
    layer.mesh.position.y += shiftY;
  });

  for (const [camera, controls] of [[ortho, controls2d], [perspective, controls3d]]) {
    camera.position.x += shiftX;
    camera.position.y += shiftY;
    controls.target.x += shiftX;
    controls.target.y += shiftY;
    controls.update();
  }

  state.projectOrigin = next;
  selectionOverlay.visible = false;
  selectionOverlay.userData.layer = null;
  if (state.currentProject) {
    state.currentProject = {
      ...state.currentProject,
      projectOrigin: { ...next },
      projectOriginSource: source,
      updatedAt: Date.now()
    };
    saveProject(state.currentProject).catch(error => {
      console.warn('[Maply] Rebased project origin could not be saved:', error);
    });
  }
  updateAdaptiveGrid();
  console.info('[Maply] projectOrigin safely rebased:', next, 'source:', source);
  return true;
}

function adoptWorldReference(reference) {
  if (!reference || !state.currentProject) return;
  state.currentProject = {
    ...state.currentProject,
    worldModel: {
      version: 1,
      canonical: 'WGS84-ECEF',
      local: 'ENU',
      renderFrame: 'PROJECTED-LOCAL',
      migrationStatus: 'canonical-ready'
    },
    worldAnchor: state.currentProject.worldAnchor || reference
  };
  refreshProjectSpatialFrame();
}

function syncUnlocatedVectorLayers() {
  state.layers
    .filter(layer => layer.sourceCrsStatus === 'unknown')
    .forEach(layer => {
      layer.unlocatedSuppressed = false;
      layer.group.visible = layer.visible;
    });
}
async function showProjectHub(){
  ui.exportMenu.classList.add('hidden');ui.projectHub.classList.remove('closed');ui.newProjectForm.classList.add('hidden');
  await refreshProjectHub();
}
function hideProjectHub(){ui.projectHub.classList.add('closed');}
async function activateProject(project, { restore = true } = {}) {
  console.log('[Maply Project] activate start', {
    id: project?.id,
    name: project?.name,
    restore
  });

  try {
    if (
      restore &&
      project?.crsStatus === 'suggested' &&
      (project?.crsSource === 'automatic' || project?.crsMode === 'automatic') &&
      Number(project?.crsAlgorithmVersion || 0) < 2
    ) {
      project = {
        ...project,
        crs: null,
        crsMode: 'automatic',
        crsStatus: 'pending',
        crsSource: null,
        crsAlgorithmVersion: 2
      };
      console.info('[Maply CRS] Reset legacy order-dependent CRS suggestion.');
    }

    console.log('[Maply Project] clearing current project');
    clearProject(false);

    console.log('[Maply Project] setting current project');
    setCurrentProject(project);

    if (isValidProjectOrigin(project?.projectOrigin)) {
      state.projectOrigin = {
        x: Number(project.projectOrigin.x),
        y: Number(project.projectOrigin.y),
        elevation: Number(project.projectOrigin.elevation) || 0
      };

      console.log(
        '[Maply Project] restored origin',
        state.projectOrigin
      );
    }

    hideProjectHub();

    if (restore) {
      setBusy(
        true,
        `Opening ${project.name}`,
        'Reading the offline working copy…',
        4
      );

      console.log('[Maply Project] restoring files');

      const files = await restoreFiles(project.id);

      console.log(
        '[Maply Project] restored files:',
        files.length
      );

      if (files.length) {
        await openFiles(
          files,
          {
            cache: false,
            touch: false
          }
        );
      } else {
        setBusy(false);
        openDrawer(true);

        showToast(
          'Project opened'
        );
      }

    } else {
      setBusy(false);
      openDrawer(true);
    }
      // Restore survey/project objects independently from source files.
      // Track, Point Mark, dan Boundary tidak boleh bergantung pada DXF/GeoPDF.
      if (state.projectOrigin) {
        loadSavedBoundaryRecords();
        loadSavedTrackRecords();
        loadSavedMarkRecords();
      }

      renderLayerList();
    state.currentProject = {
      ...state.currentProject,
      updatedAt: Date.now()
    };

    await saveProject(state.currentProject);

    ui.currentProjectName.textContent =
      state.currentProject.name;

    console.log(
      '[Maply Project] activate SUCCESS'
    );

  } catch (error) {
    console.error(
      '[Maply Project] ACTIVATE FAILED:',
      error
    );

    setBusy(false);

    showToast(
      `Project open failed: ${error.message}`,
      7000
    );
  }
}
async function createBlankProject(name) {

  const project =
    await createProject({
      name,
      storageMode: 'local',
      source: 'device'
    });

  project.crs = null;
  project.crsMode = 'automatic';
  project.crsStatus = 'pending';
  project.crsSource = null;


  await saveProject(project);

  await activateProject(
    project,
    { restore: false }
  );

  showToast(`${project.name} created · location will be detected from data or GPS`);
}
async function createCloudProject(files){
  const project=await createProject({name:projectNameFromFiles(files),storageMode:'hybrid',source:'user-cloud'});await activateProject(project,{restore:false});return project;
}
let projectAutosaveTimer = null;

function scheduleProjectAutosave(delay = 500) {
  if (!state.currentProject) return;

  clearTimeout(projectAutosaveTimer);

  projectAutosaveTimer = setTimeout(() => {
    touchActiveProject().catch(error => {
      console.error('[Maply Autosave] failed:', error);
      showToast('Autosave failed.', 3500);
    });
  }, delay);
}

async function touchActiveProject() {
  if (!state.currentProject) return;

  const tracks =
    (state.savedTracks || []).map(item => ({
      ...item.record,
      order: item.order
    }));

  const boundaries =
    (state.savedBoundaries || []).map(item => ({
      ...item.record,
      role: item.role,
      order: item.order
    }));

  const marks =
    (state.marks || []).map(markRecord);

  state.currentProject = {
    ...state.currentProject,

    fileCount:
      state.layers.length +
      state.pdfLayers.length,

    layerSettings:
      captureLayerSettings(),

    projectOrigin:
      state.projectOrigin
        ? { ...state.projectOrigin }
        : null,

    tracks,
    boundaries,
    marks,

    updatedAt:
      Date.now()
  };

  await saveProject(
    state.currentProject
  );

  ui.currentProjectName.textContent =
    state.currentProject.name;
}
async function migrateLegacyCache(){
  if((await listProjects()).length)return;
  const files=await restoreFiles();if(!files.length)return;
  const project=await createProject({name:'Recovered Project',storageMode:'local',source:'legacy-cache'});
  for(const file of files)await cacheFile(file,project.id);
  project.fileCount=files.length;project.updatedAt=Date.now();await saveProject(project);await clearCachedFiles();
}

function roleForName(name) {
  const lower = name.toLowerCase();
  if (lower.includes('kontur') || lower.includes('actual') || lower.includes('aktual') || lower.includes('top')) return 'top';
  if (lower.includes('dgn') || lower.includes('design') || lower.includes('rencana') || lower.includes('bottom')) return 'bottom';
  return 'reference';
}
function colorForRole(role) { return role === 'bottom' ? '#6fe7ff' : role === 'top' ? '#ffad5c' : '#a6b8c5'; }
function savedLayerSetting(name){return state.currentProject?.layerSettings?.find(item=>item.name===name)||null;}
function nextLayerOrder(){return ++state.layerOrderCounter;}
function boundsLikelySameArea(a,b){
  if(!a||!b)return false;const ix=Math.max(0,Math.min(a.maxX,b.maxX)-Math.max(a.minX,b.minX)),iy=Math.max(0,Math.min(a.maxY,b.maxY)-Math.max(a.minY,b.minY));if(ix*iy>0)return true;
  const acx=(a.minX+a.maxX)/2,acy=(a.minY+a.maxY)/2,bcx=(b.minX+b.maxX)/2,bcy=(b.minY+b.maxY)/2,scale=Math.max(Math.hypot(a.maxX-a.minX,a.maxY-a.minY),Math.hypot(b.maxX-b.minX,b.maxY-b.minY),1);return Math.hypot(acx-bcx,acy-bcy)<scale*3;
}
function resolveUtmLikeLayersFromGeoPdf(metadata) {
  const projectCrs = metadata?.projectCrs;
  const referenceBounds = metadata?.bounds;
  let resolved = 0;
  let unresolved = 0;

  for (const layer of state.layers) {
    if (layer.sourceCrsStatus === 'resolved') continue;

    if (!canAutoResolveUtmLikeLayer(layer, referenceBounds, projectCrs)) {
      unresolved += 1;
      layer.crsStatus = 'local-unresolved';
      continue;
    }

    const bounds = layer.result.bounds;
    const center = utmToLatLon(
      (bounds.minX + bounds.maxX) / 2,
      (bounds.minY + bounds.maxY) / 2,
      projectCrs.zone,
      projectCrs.hemisphere
    );
    const reference = makeWorldReference(
      center.latitude,
      center.longitude
    );

    layer.sourceCrs = projectCrs;
    layer.sourceCrsStatus = 'resolved';
    layer.worldReference = reference;
    layer.canonicalBounds = state.spatialFrame
      ? canonicalBoundsFromProjectBounds(bounds, state.spatialFrame)
      : null;
    layer.projectCrs = `EPSG:${projectCrs.epsg}`;
    layer.crsStatus = 'resolved';
    layer.crsAction = 'assign';
    layer.crsEvidence = 'geopdf-viewport-overlap';
    layer.locationStatus = 'verified';

    Object.assign(layer.result, {
      sourceCrs: projectCrs,
      sourceCrsStatus: 'resolved',
      projectCrs,
      crsAction: 'assign',
      crsEvidence: 'geopdf-viewport-overlap',
      locationStatus: 'verified',
      worldReference: reference
    });

    scheduleSpatialShadow(layer);
    resolved += 1;
  }

  return { resolved, unresolved };
}
function setLayerDisplayMode(layer,mode){
  layer.displayMode=mode;
  layer.lines.visible=mode==='lines'||mode==='lines-surface';
  if(layer.surfaceMesh)layer.surfaceMesh.visible=mode==='surface'||mode==='lines-surface';
  if(layer.surfaceWire)layer.surfaceWire.visible=mode==='tin';
}
function applyLayerColor(layer,value,{original=false}={}){
  layer.color=value;layer.colorMode=original?'dxf':'manual';
  layer.lines.material.vertexColors=original;layer.lines.material.color.set(original?0xffffff:value);layer.lines.material.needsUpdate=true;
  if(layer.surfaceMesh){layer.surfaceMesh.material.vertexColors=false;layer.surfaceMesh.material.color.set(value);layer.surfaceMesh.material.needsUpdate=true;}
  if(layer.surfaceWire){layer.surfaceWire.material.color.set(value);layer.surfaceWire.material.needsUpdate=true;}
  if(layer.pointsObject){layer.pointsObject.material.vertexColors=original;layer.pointsObject.material.color.set(original?0xffffff:value);layer.pointsObject.material.needsUpdate=true;}
}
function textSprite(item, color, origin) {
  const canvas = document.createElement('canvas'), context = canvas.getContext('2d');
  const size = 42; context.font = `700 ${size}px system-ui`; const width = Math.min(900, Math.max(100, context.measureText(item.text).width + 28));
  canvas.width = width; canvas.height = 62; context.font = `700 ${size}px system-ui`; context.fillStyle = '#071018aa'; context.fillRect(0, 0, width, 62); context.fillStyle = color; context.fillText(item.text, 14, 46);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true, depthTest: false }));
  const worldHeight = Math.max(2, item.height || 2.5); sprite.scale.set(worldHeight * width / 62, worldHeight, 1); sprite.position.set(item.x - origin.x, item.y - origin.y, item.z + worldHeight); sprite.userData.textLift=worldHeight;sprite.renderOrder = 10; return sprite;
}

function addLayer(result) {
  if (
    result.sourceCrsStatus === 'resolved' &&
    state.currentProject?.projectOriginSource === 'raw-unverified'
  ) {
    rebaseProjectOrigin(
      originFromBounds(result.bounds),
      'verified-spatial-rebase'
    );
  }

  if (!state.projectOrigin) {
    const origin =
      originFromBounds(result.bounds) ||
      {
        x: result.origin.x,
        y: result.origin.y,
        elevation: 0
      };

    adoptProjectOrigin(
      origin,
      result.sourceCrsStatus === 'resolved'
        ? 'spatial-file'
        : 'raw-unverified'
    );
  }

  adoptWorldReference(result.worldReference);

  const renderOrigin = state.projectOrigin || result.origin;

  const role = roleForName(result.name), color = colorForRole(role);
  const group = new THREE.Group();

  group.position.set(
    result.origin.x - renderOrigin.x,
    result.origin.y - renderOrigin.y,
    0
  );

  group.scale.z = state.exaggeration;
  const lineGeometry = new THREE.BufferGeometry(); lineGeometry.setAttribute('position', new THREE.BufferAttribute(result.linePositions, 3)); lineGeometry.setAttribute('color',new THREE.BufferAttribute(result.lineColors,3,true));
  const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.94,depthTest:false });
  const lines = new THREE.LineSegments(lineGeometry, lineMaterial); lines.renderOrder = 3; group.add(lines);
  let surfaceMesh = null, surfaceWire = null;
  if (result.surface) {
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(result.surface.positions, 3)); geometry.setIndex(new THREE.BufferAttribute(result.surface.indices, 1)); geometry.computeVertexNormals();
    const material =new THREE.MeshStandardMaterial({
    color,
    roughness: 0.88,
    metalness: 0.0,
    transparent: true,
    opacity: 0.94,
    side: THREE.DoubleSide,
    depthWrite: true,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1
  });
    
    surfaceMesh = new THREE.Mesh(geometry, material); surfaceMesh.visible = false; surfaceMesh.renderOrder = 1; group.add(surfaceMesh);
    const wireMaterial=new THREE.MeshBasicMaterial({color,wireframe:true,transparent:true,opacity:.48,side:THREE.DoubleSide,depthWrite:false});
    surfaceWire=new THREE.Mesh(geometry,wireMaterial);surfaceWire.visible=false;surfaceWire.renderOrder=2;group.add(surfaceWire);
  }
  let pointsObject=null;
  if (result.pointPositions.length) {
    const pointGeometry = new THREE.BufferGeometry(); pointGeometry.setAttribute('position', new THREE.BufferAttribute(result.pointPositions, 3)); pointGeometry.setAttribute('color',new THREE.BufferAttribute(result.pointColors,3,true));
    pointsObject=new THREE.Points(pointGeometry, new THREE.PointsMaterial({ color:0xffffff, vertexColors:true, size: 7, sizeAttenuation: false,depthTest:false,transparent:true,opacity:.94 }));group.add(pointsObject);
  }
  const textSprites=[];result.texts?.forEach(item => {const sprite=textSprite(item, `#${(item.color??0xffffff).toString(16).padStart(6,'0')}`, result.origin);textSprites.push(sprite);group.add(sprite);});
  scene.add(group);
  const projectCrs = currentProjectCrs();
  const sourceCrs =
    result.sourceCrs ??
    result.projectCrs ??
    null;
  const sourceCrsStatus =
    result.sourceCrsStatus ||
    (sourceCrs ? 'resolved' : 'unknown');
  const canonicalBounds = sourceCrsStatus === 'resolved' && state.spatialFrame
    ? canonicalBoundsFromProjectBounds(result.bounds, state.spatialFrame)
    : null;
  const setting=savedLayerSetting(result.name),layer = { id: crypto.randomUUID(), kind:'vector',result, group, lines,pointsObject,textSprites, surfaceMesh,surfaceWire, role:setting?.role||role, color:setting?.color||color, colorMode:setting?.colorMode||'dxf', visible:setting?.visible??true, unlocatedSuppressed:false, opacity:setting?.opacity??0.94,displayMode:result.surface?(setting?.displayMode||'lines'):'lines',order:setting?.order??nextLayerOrder(),sourceCrs,sourceCrsStatus,worldReference:result.worldReference||setting?.worldReference||null,canonicalBounds:canonicalBounds||setting?.canonicalBounds||null,spatialShadow:null,spatialShadowStatus:'pending',spatialShadowJobId:null,activeRenderFrame:RENDER_FRAME_PROJECTED,crsAction:result.crsAction||(sourceCrsStatus==='resolved'?'assign':'keep-raw'),coordinatePattern:result.coordinatePattern||setting?.coordinatePattern||null,locationStatus:result.locationStatus||setting?.locationStatus||(sourceCrsStatus==='resolved'?'verified':'unverified'),crsEvidence:result.crsEvidence||setting?.crsEvidence||null,localGridId:setting?.localGridId||null,localGridName:setting?.localGridName||null,
    projectCrs:
      projectCrs
        ? `EPSG:${projectCrs.epsg}`
        : null,
    crsStatus:
      sourceCrsStatus === 'resolved'
        ? 'resolved'
        : setting?.crsStatus || 'unknown',sampler: null};
  group.visible=layer.visible;lines.userData.layer = layer; if (surfaceMesh) surfaceMesh.userData.layer = layer;if(surfaceWire)surfaceWire.userData.layer=layer;
  if (surfaceMesh) layer.sampler = new SurfaceSampler(surfaceMesh.geometry.attributes.position.array, surfaceMesh.geometry.index.array, { x: group.position.x, y: group.position.y });
  applyLayerColor(layer,layer.color,{original:layer.colorMode==='dxf'});setLayerDisplayMode(layer,layer.displayMode);layer.lines.material.opacity=layer.opacity;if(layer.pointsObject)layer.pointsObject.material.opacity=layer.opacity;if(layer.surfaceMesh)layer.surfaceMesh.material.opacity=Math.min(.94,layer.opacity);state.layerOrderCounter=Math.max(state.layerOrderCounter,layer.order);state.layers.push(layer);
  for (const pdfLayer of state.pdfLayers) {
    resolveUtmLikeLayersFromGeoPdf(pdfLayer.metadata);
    if (layer.sourceCrsStatus === 'resolved') break;
  }
  if (layer.sourceCrsStatus !== 'resolved') scheduleSpatialShadow(layer);
  loadSavedBoundaryRecords();loadSavedTrackRecords();loadSavedMarkRecords();
  refreshGeoPdfDrapes();renderLayerList(); recolorComparison(); setStatus();
  if (state.layers.length + state.pdfLayers.length === 1 && !state.gpsWatch) {
    fitView();
}
  if (innerWidth < 960) openDrawer(false);
  const warning = result.warnings?.[0]; if (warning) showToast(warning, 4200);
  return layer;
}

function disposeLayer(layer) {
  layer.spatialShadowJobId = null;
  layer.spatialShadow = null;
  scene.remove(layer.group);
  layer.group.traverse(object => { object.geometry?.dispose(); if (Array.isArray(object.material)) object.material.forEach(m => m.dispose()); else object.material?.dispose(); });
}
function disposePdfLayer(layer) {
  scene.remove(layer.mesh); layer.mesh.geometry.dispose(); layer.mesh.material.dispose(); layer.texture.dispose();
}
function createSavedBoundaryLayer(record){
  if(!state.projectOrigin||state.savedBoundaries.some(layer=>layer.record.id===record.id)||!Array.isArray(record.coordinates)||record.coordinates.length<3)return null;
  if(!record.canonicalCoordinates)record.canonicalCoordinates=canonicalProjectCoordinates(record.coordinates);
  const positions=[];record.coordinates.forEach(([x,y,z=0])=>positions.push(x-state.projectOrigin.x,y-state.projectOrigin.y,z));positions.push(record.coordinates[0][0]-state.projectOrigin.x,record.coordinates[0][1]-state.projectOrigin.y,record.coordinates[0][2]||0);
  const geometry=new LineGeometry();geometry.setPositions(positions);const material=new LineMaterial({color:0xc72cff,linewidth:2.5,worldUnits:false,depthTest:false,transparent:true,opacity:.95});material.resolution.set(ui.viewport.clientWidth||1,ui.viewport.clientHeight||1);const line=new Line2(geometry,material);line.computeLineDistances();line.renderOrder=18;const group=new THREE.Group();group.scale.z=state.exaggeration;group.add(line);scene.add(group);
  const local=record.coordinates.map(([x,y,z=0])=>new THREE.Vector3(x-state.projectOrigin.x,y-state.projectOrigin.y,z)),faces=THREE.ShapeUtils.triangulateShape(local.map(point=>new THREE.Vector2(point.x,point.y)),[]),surfacePositions=new Float32Array(local.flatMap(point=>[point.x,point.y,point.z])),surfaceIndices=new Uint32Array(faces.flatMap(face=>face));
  const sampler=surfaceIndices.length?new SurfaceSampler(surfacePositions,surfaceIndices):null,xs=record.coordinates.map(point=>point[0]),ys=record.coordinates.map(point=>point[1]),zs=record.coordinates.map(point=>Number(point[2])||0),layer={id:record.id||crypto.randomUUID(),kind:'boundary',order:record.order??nextLayerOrder(),record,role:record.role||'reference',group,line,material,sampler,visible:true,activeRenderFrame:RENDER_FRAME_PROJECTED,bounds:{minX:Math.min(...xs),minY:Math.min(...ys),minZ:Math.min(...zs),maxX:Math.max(...xs),maxY:Math.max(...ys),maxZ:Math.max(...zs)}};layer.canonicalBounds=state.spatialFrame?canonicalBoundsFromProjectBounds(layer.bounds,state.spatialFrame):null;setSavedLineRenderFrame(layer,state.renderFrame,true);state.layerOrderCounter=Math.max(state.layerOrderCounter,layer.order);state.savedBoundaries.push(layer);return layer;
}
function disposeSavedBoundary(layer){scene.remove(layer.group);layer.line.geometry.dispose();layer.material.dispose();}
function boundaryStorageKey(){return state.currentProject?.id?`eagle-terrain-boundaries-v1:${state.currentProject.id}`:'eagle-terrain-boundaries-v1';}
function persistSavedBoundaries(){try{localStorage.setItem(boundaryStorageKey(),JSON.stringify(state.savedBoundaries.map(item=>({...item.record,role:item.role,order:item.order}))));scheduleProjectAutosave(250);}catch{showToast('Saved boundaries could not be updated.',4500);}}
function loadSavedBoundaryRecords(){
  if(state.savedBoundariesLoaded||!state.projectOrigin)return;state.savedBoundariesLoaded=true;
  try{const key=boundaryStorageKey(),records=JSON.parse(localStorage.getItem(key)||'[]');records.forEach(createSavedBoundaryLayer);}catch{localStorage.removeItem(boundaryStorageKey());}
}
function exportSavedBoundaryDxf(layer){
  const record=layer.record,rows=['0','SECTION','2','HEADER','0','ENDSEC','0','SECTION','2','ENTITIES','0','POLYLINE','8','BOUNDARY','66','1','70','9'];
  record.coordinates.forEach(([x,y,z=0])=>rows.push('0','VERTEX','8','BOUNDARY','10',Number(x).toFixed(3),'20',Number(y).toFixed(3),'30',Number(z).toFixed(3),'70','32'));rows.push('0','SEQEND','8','BOUNDARY','0','ENDSEC','0','EOF');downloadBlob(new Blob([rows.join('\r\n')],{type:'application/dxf'}),`${record.name.replace(/[^a-z0-9_-]+/gi,'-')}.dxf`);
}
function removeSavedBoundary(layer){
  disposeSavedBoundary(layer);state.savedBoundaries=state.savedBoundaries.filter(item=>item!==layer);persistSavedBoundaries();applySpatialRenderFrame();renderLayerList();showToast('Saved boundary removed.');
}
// --- SISTEM LAYER UNTUK GPS TRACKING ---
function trackStorageKey() { return state.currentProject?.id ? `eagle-terrain-tracks-v1:${state.currentProject.id}` : 'eagle-terrain-tracks-v1'; }
function persistSavedTracks() { try { localStorage.setItem(trackStorageKey(), JSON.stringify(state.savedTracks.map(item => ({ ...item.record, order: item.order })))); scheduleProjectAutosave(250);
} catch { showToast('Saved tracks could not be updated.', 4500); } }

function createSavedTrackLayer(record) {
  if (!state.projectOrigin || state.savedTracks.some(layer => layer.record.id === record.id) || !Array.isArray(record.coordinates) || record.coordinates.length < 2) return null;
  if(!record.canonicalCoordinates)record.canonicalCoordinates=canonicalProjectCoordinates(record.coordinates);
  const positions = [];
  record.coordinates.forEach(([x, y, z = 0]) => positions.push(x - state.projectOrigin.x, y - state.projectOrigin.y, z));
  
  const geometry = new LineGeometry(); geometry.setPositions(positions);
  // Warna biru terang dengan ketebalan 4px (sama dengan garis tracking aktif)
  const material = new LineMaterial({ color: 0x38a9ff, linewidth: 4, worldUnits: false, depthTest: false, transparent: true, opacity: 0.95 });
  material.resolution.set(ui.viewport.clientWidth || 1, ui.viewport.clientHeight || 1);
  const line = new Line2(geometry, material); line.computeLineDistances(); line.renderOrder = 22;
  
  const group = new THREE.Group(); group.scale.z = state.exaggeration; group.add(line); scene.add(group);
  
  const xs = record.coordinates.map(p => p[0]), ys = record.coordinates.map(p => p[1]), zs = record.coordinates.map(p => Number(p[2]) || 0);
  const layer = { id: record.id || crypto.randomUUID(), kind: 'track', order: record.order ?? nextLayerOrder(), record, group, line, material, visible: true, activeRenderFrame: RENDER_FRAME_PROJECTED, bounds: { minX: Math.min(...xs), minY: Math.min(...ys), minZ: Math.min(...zs), maxX: Math.max(...xs), maxY: Math.max(...ys), maxZ: Math.max(...zs) } };
  layer.canonicalBounds = state.spatialFrame
    ? canonicalBoundsFromProjectBounds(layer.bounds, state.spatialFrame)
    : null;
  setSavedLineRenderFrame(layer, state.renderFrame, false);
  
  state.layerOrderCounter = Math.max(state.layerOrderCounter, layer.order);
  state.savedTracks.push(layer); return layer;
}

function loadSavedTrackRecords() {
  if (
    state.savedTracksLoaded ||
    !state.projectOrigin
  ) {
    return;
  }

  state.savedTracksLoaded = true;

  let records = [];

  try {
    records =
      JSON.parse(
        localStorage.getItem(
          trackStorageKey()
        ) || '[]'
      );

    if (!Array.isArray(records)) {
      records = [];
    }

  } catch {
    localStorage.removeItem(
      trackStorageKey()
    );

    records = [];
  }


  // Fallback ke metadata project
  if (
    records.length === 0 &&
    Array.isArray(
      state.currentProject?.tracks
    )
  ) {
    records =
      state.currentProject.tracks;

    // Pulihkan cache lokal juga
    try {
      localStorage.setItem(
        trackStorageKey(),
        JSON.stringify(records)
      );
    } catch {
      // Project metadata tetap menjadi backup.
    }
  }
  records.forEach(
    createSavedTrackLayer
  );
}

function removeSavedTrack(layer) {
  scene.remove(layer.group); layer.line.geometry.dispose(); layer.material.dispose();
  state.savedTracks = state.savedTracks.filter(item => item !== layer); persistSavedTracks(); applySpatialRenderFrame(); renderLayerList(); showToast('GPS Track removed.');
}

// --- PENYIMPANAN MARK LOCATION PER PROJECT ---
function markStorageKey() {
  return state.currentProject?.id
    ? `eagle-terrain-marks-v1:${state.currentProject.id}`
    : 'eagle-terrain-marks-v1';
}

function markRecord(mark) {
  const { node, ...record } = mark;
  return record;
}

function persistMarks() {
  try {
    localStorage.setItem(markStorageKey(), JSON.stringify(state.marks.map(markRecord)));
    scheduleProjectAutosave(250);
  } catch {
    showToast('Position marks could not be saved.', 4500);
  }
}

function createSavedMark(record) {
  if (!state.projectOrigin || !record || state.marks.some(mark => mark.id === record.id)) return null;

  const easting = Number(record.easting);
  const northing = Number(record.northing);
  if (!Number.isFinite(easting) || !Number.isFinite(northing)) return null;

  const x = easting - state.projectOrigin.x;
  const y = northing - state.projectOrigin.y;
  const rl = Number.isFinite(Number(record.rl)) ? Number(record.rl) : topElevation(x, y);
  const mark = {
    ...record,
    id: String(record.id || `M${state.marks.length + 1}`),
    name:String(record.name ||record.id ||`M${state.marks.length + 1}`),
    easting,
    northing,
    x,
    y,
    rl,
    displayZ: rl * state.exaggeration + 8,
    time: Number(record.time) || Date.now()
  };

  mark.canonical = record.canonical ||
    canonicalProjectCoordinate(easting, northing, rl);
  setSurveyPointRenderFrame(mark, state.renderFrame);

  mark.node = createMarkerMesh(mark);
  marksGroup.add(mark.node);
  state.marks.push(mark);
  return mark;
}

function loadSavedMarkRecords() {
  if (
    state.marksLoaded ||
    !state.projectOrigin
  ) {
    return;
  }

  state.marksLoaded = true;

  let records = [];

  try {
    records =
      JSON.parse(
        localStorage.getItem(
          markStorageKey()
        ) || '[]'
      );

    if (!Array.isArray(records)) {
      records = [];
    }

  } catch {
    localStorage.removeItem(
      markStorageKey()
    );

    records = [];
  }

  // Fallback dari metadata project
  if (
    records.length === 0 &&
    Array.isArray(
      state.currentProject?.marks
    )
  ) {
    records =
      state.currentProject.marks;

    try {
      localStorage.setItem(
        markStorageKey(),
        JSON.stringify(records)
      );
    } catch {
      // metadata project tetap jadi backup
    }
  }

  records.forEach(
    createSavedMark
  );

  renderLayerList();
}

function optionalTrackNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizedTrackPoints(record) {
  if (Array.isArray(record?.points)) {
    return record.points.map((point, index) => ({
      easting: Number(point.easting),
      northing: Number(point.northing),
      rl: Number(point.rl),
      latitude: optionalTrackNumber(point.latitude),
      longitude: optionalTrackNumber(point.longitude),
      altitude: optionalTrackNumber(point.altitude),
      accuracy: optionalTrackNumber(point.accuracy),
      time: optionalTrackNumber(point.time),
      projectCrs: point.projectCrs || record.crs || null,
      canonical: point.canonical || record.canonicalCoordinates?.[index] || null
    }));
  }

  const coordinates = Array.isArray(record?.coordinates)
    ? record.coordinates
    : [];

  const wgs84 = Array.isArray(record?.wgs84)
    ? record.wgs84
    : [];

  return coordinates.map((coordinate, index) => {
    const geographic = wgs84[index] || [];

    return {
      easting: Number(coordinate?.[0]),
      northing: Number(coordinate?.[1]),
      rl: Number(coordinate?.[2]),
      longitude: optionalTrackNumber(geographic[0]),
      latitude: optionalTrackNumber(geographic[1]),
      altitude: optionalTrackNumber(geographic[2]),
      accuracy: null,
      time: null,
      projectCrs: record.crs || null,
      canonical: record.canonicalCoordinates?.[index] || null
    };
  });
}

function confirmedTrackCrs(record) {
  const storedCrs = normalizeProjectCrs(
    record?.crs
  );

  const currentCrs = currentProjectCrs();

  if (
    currentCrs &&
    isProjectCrsConfirmed() &&
    storedCrs?.epsg === currentCrs.epsg
  ) {
    return currentCrs;
  }

  if (
    storedCrs &&
    record?.crsStatus === 'confirmed'
  ) {
    return storedCrs;
  }

  return null;
}

async function exportSavedTrackDxf(layer) {
  try {
    const record = layer.record;
    const crs = confirmedTrackCrs(record);

    if (!crs) {
      throw new Error(
        'Confirm the Project CRS before exporting this track.'
      );
    }

    const points = normalizedTrackPoints(record);

    if (points.length < 2) {
      throw new Error(
        'The track does not contain enough points to export.'
      );
    }

    if (
      points.some(point =>
        !Number.isFinite(point.easting) ||
        !Number.isFinite(point.northing)
      )
    ) {
      throw new Error(
        'The track contains invalid absolute coordinates.'
      );
    }

    const elevations = points.map(point =>
      Number.isFinite(point.rl)
        ? point.rl
        : 0
    );

    const eastings = points.map(point => point.easting);
    const northings = points.map(point => point.northing);
    const safeName = (record.name || 'GPS_Track').replace(/[^a-zA-Z0-9_-]/g, '_');
    const rows = [
      '999', `MAPLY_PROJECT_CRS=EPSG:${crs.epsg}`,
      '999', 'MAPLY_COORDINATES=ABSOLUTE_PROJECT_CRS',
      '0', 'SECTION',
      '2', 'HEADER',
      '9', '$ACADVER',
      '1', 'AC1015',
      '9', '$INSUNITS',
      '70', '6',
      '9', '$EXTMIN',
      '10', Math.min(...eastings).toFixed(3),
      '20', Math.min(...northings).toFixed(3),
      '30', Math.min(...elevations).toFixed(3),
      '9', '$EXTMAX',
      '10', Math.max(...eastings).toFixed(3),
      '20', Math.max(...northings).toFixed(3),
      '30', Math.max(...elevations).toFixed(3),
      '0', 'ENDSEC',
      '0', 'SECTION',
      '2', 'ENTITIES',
      '0', 'POLYLINE',
      '8', 'GPS_TRACK',
      '66', '1',
      '70', '8'
    ];

    points.forEach(point => {
      const elevation = Number.isFinite(point.rl)
        ? point.rl
        : 0;

      rows.push(
        '0', 'VERTEX',
        '8', 'GPS_TRACK',
        '10', point.easting.toFixed(3),
        '20', point.northing.toFixed(3),
        '30', elevation.toFixed(3),
        '70', '32'
      );
    });

    rows.push(
      '0', 'SEQEND',
      '8', 'GPS_TRACK',
      '0', 'ENDSEC',
      '0', 'EOF'
    );

    const blob = new Blob([rows.join('\r\n')], { type: 'application/dxf' });
    await downloadBlob(
      blob,
      `${safeName}_EPSG-${crs.epsg}.dxf`
    );
    showToast(
      `Track DXF exported in EPSG:${crs.epsg}.`,
      4500
    );
  } catch (e) {
    showToast('Export failed: ' + e.message, 5500);
  }
}

function clearSurveyData() {
  stopGps();
  state.gpsFix = null;
  state.tracking = { active: false, points: [], startedAt: null, nativeActive: false, projectCrs: null, crsStatus: null };
  lastTrackRawFix = null;
  if (trackLine) trackLine.visible = false;
  if (marksGroup) {
    while (marksGroup.children.length) marksGroup.remove(marksGroup.children[0]);
  }
  state.marks = [];
  state.marksLoaded = false;
}
function clearProject(clearCache = false) {
  resetSpatialShadowWorker();
  if (state.section.press) clearTimeout(state.section.press.timer);
  state.section.press = null;
  state.measure = {
  mode: 'line',
  points: [],
  snaps: [],
  projectCoordinates: [],
  canonicalCoordinates: [],
  preview: null,
  finalized: false,
  result: null
};
  if (measureLine) measureLine.visible = false;
  if (measureHalo) measureHalo.visible = false;
  if (measurePoints) measurePoints.forEach(point => point.visible = false);
  
  // Panggil fungsi pembersihan (yang tadi error karena hilang)
  clearSurveyData();
  clearBoundaryData();
  
  // Bersihkan data layer dari memori 3D
  state.layers.forEach(disposeLayer);
  state.pdfLayers.forEach(disposePdfLayer);
  state.savedBoundaries.forEach(disposeSavedBoundary);
  
  // Bersihkan data layer GPS Track (modifikasi baru kita)
  if (state.savedTracks) {
    state.savedTracks.forEach(layer => { 
      scene.remove(layer.group); 
      layer.line.geometry.dispose(); 
      layer.material.dispose(); 
    });
  }
  
  // Reset semua array penampung data
  state.layers = [];
  state.pdfLayers = [];
  state.savedBoundaries = [];
  state.savedBoundariesLoaded = false;
  state.savedTracks = [];
  state.savedTracksLoaded = false;
  
  state.projectOrigin = null;
  state.spatialFrame = null;
  state.renderFrame = RENDER_FRAME_PROJECTED;
  state.renderFrameReason = 'project-cleared';
  state.layerOrderCounter = 0;
  state.section.a = state.section.b = null;
  state.section.last = [];
  state.section.metrics = null;
  
  [sectionHalo, sectionLine, markerA, markerB, markerM, labelA, labelB].forEach(item => { if(item) item.visible = false; });
  if (coordinateMarker) coordinateMarker.visible = false;
  if (selectionOverlay) selectionOverlay.visible = false;
  if (selectMarker) selectMarker.visible = false;
  if (boundaryAreaLabel) boundaryAreaLabel.visible = false;
  
  ui.sectionPanel.classList.add('collapsed');
  
  renderLayerList();
  setStatus();
  
  if (clearCache) {
    clearCachedFiles(state.currentProject?.id).catch(() => {});
    localStorage.removeItem(boundaryStorageKey());
    if (typeof trackStorageKey === 'function') localStorage.removeItem(trackStorageKey());
    if (typeof markStorageKey === 'function') localStorage.removeItem(markStorageKey());
  }
}

function renderLayerListLegacy() {
  ui.layerList.replaceChildren(); ui.empty.style.display = state.layers.length || state.pdfLayers.length || state.savedBoundaries.length ? 'none' : 'grid';
  state.layers.forEach((layer, index) => {
    const card = document.createElement('article'); card.className = 'layer-card';
    const row = document.createElement('div'); row.className = 'layer-row';
    const visible = document.createElement('input'); visible.type = 'checkbox'; visible.checked = layer.visible; visible.title = 'Show or hide layer';
    visible.addEventListener('change', () => { layer.visible = visible.checked; layer.group.visible = layer.visible; if(selectionOverlay.userData.layer===layer)selectionOverlay.visible=layer.visible; recolorComparison(); updateSection(); });
    const title = document.createElement('div'); title.className = 'layer-title';
    const label = document.createElement('b'); label.textContent = layer.result.name;
    const detail = document.createElement('small'); detail.textContent = `${layer.result.format || 'DXF'} · ${layer.result.segments.toLocaleString('en-US')} segments · ${layer.result.colorCount||1} colours`;
    title.append(label, detail);
    const color = document.createElement('input'); color.type = 'color'; color.value = layer.color; color.title = 'Choose a custom colour';
    const originalColor = document.createElement('button'); originalColor.className='dxf-color-button active'; originalColor.textContent=layer.result.format || 'DXF'; originalColor.title='Use original file colours';
    color.addEventListener('input', () => { layer.color = color.value; layer.colorMode='manual'; layer.lines.material.vertexColors=false; layer.lines.material.color.set(color.value); layer.lines.material.needsUpdate=true; originalColor.classList.remove('active'); });
    originalColor.addEventListener('click',()=>{layer.colorMode='dxf';layer.lines.material.vertexColors=true;layer.lines.material.color.set(0xffffff);layer.lines.material.needsUpdate=true;originalColor.classList.add('active');});
    row.append(visible, title, originalColor, color);
    const controls = document.createElement('div'); controls.className = 'layer-controls';
    const role = document.createElement('select');
    [['bottom','Bottom'],['top','Top'],['reference','Reference']].forEach(([value, text]) => { const option = document.createElement('option'); option.value = value; option.textContent = text; option.selected = layer.role === value; role.append(option); });
    role.addEventListener('change', () => { layer.role = role.value; recolorComparison(); updateSection(); });
    const order = document.createElement('div');
    const up = document.createElement('button'), down = document.createElement('button'); up.className = down.className = 'ghost'; up.textContent = '↑'; down.textContent = '↓'; up.title = 'Move layer up'; down.title = 'Move layer down';
    up.addEventListener('click', () => moveLayer(index, -1)); down.addEventListener('click', () => moveLayer(index, 1)); order.append(up, down);
    const opacity = document.createElement('input'); opacity.type = 'range'; opacity.min = 0.08; opacity.max = 1; opacity.step = 0.01; opacity.value = layer.opacity; opacity.title = 'Opacity';
    opacity.addEventListener('input', () => { layer.opacity = Number(opacity.value); layer.lines.material.opacity = layer.opacity; });
    controls.append(role, order, opacity); card.append(row, controls); ui.layerList.append(card);
  });
  state.pdfLayers.forEach(layer => {
    const card=document.createElement('article');card.className='layer-card pdf-layer-card';
    const row=document.createElement('div');row.className='layer-row';
    const visible=document.createElement('input');visible.type='checkbox';visible.checked=layer.visible;visible.title='Show or hide layer';
    visible.addEventListener('change',()=>{layer.visible=visible.checked;layer.mesh.visible=layer.visible;setStatus();});
    const title=document.createElement('div');title.className='layer-title';
    const label=document.createElement('b');label.textContent=layer.name;
    const detail=document.createElement('small');detail.textContent=`GeoPDF · EPSG:${layer.metadata.sourceEpsg} → EPSG:${layer.metadata.targetEpsg} · ${layer.pages} page${layer.pages===1?'':'s'}`;
    title.append(label,detail);
    const badge=document.createElement('button');badge.className='dxf-color-button active';badge.textContent='PDF';badge.disabled=true;
    row.append(visible,title,badge);
    const controls=document.createElement('div');controls.className='layer-controls';
    const fit=document.createElement('button');fit.className='ghost';fit.textContent='Fit Layer';fit.title='Move to this GeoPDF area';fit.addEventListener('click',()=>fitAbsoluteBounds(layer.bounds));
    const opacity=document.createElement('input');opacity.type='range';opacity.min=.08;opacity.max=1;opacity.step=.01;opacity.value=layer.opacity;opacity.title='Opacity';
    opacity.addEventListener('input',()=>{layer.opacity=Number(opacity.value);layer.mesh.material.opacity=layer.opacity;});
    controls.append(fit,opacity);card.append(row,controls);ui.layerList.append(card);
  });
  state.savedBoundaries.forEach(layer=>{
    const record=layer.record,card=document.createElement('article');card.className='layer-card saved-boundary-card';const row=document.createElement('div');row.className='layer-row';
    const visible=document.createElement('input');visible.type='checkbox';visible.checked=layer.visible;visible.title='Show or hide saved boundary';visible.addEventListener('change',()=>{layer.visible=visible.checked;layer.group.visible=layer.visible;});
    const title=document.createElement('div');title.className='layer-title';const label=document.createElement('b');label.textContent=record.name;const detail=document.createElement('small');detail.textContent=`Saved Boundary · ${Number(record.area||0).toLocaleString('en-US',{maximumFractionDigits:1})} m² · ${Number.isFinite(record.referenceElevation)?`Z ${record.referenceElevation.toFixed(2)} m`:'vertex elevations'}`;title.append(label,detail);
    const badge=document.createElement('button');badge.className='dxf-color-button active';badge.textContent='BDY';badge.disabled=true;row.append(visible,title,badge);
    const controls=document.createElement('div');controls.className='saved-boundary-controls';const fit=document.createElement('button');fit.className='ghost';fit.textContent='Fit';fit.addEventListener('click',()=>fitAbsoluteBounds(layer.bounds));const exportDxf=document.createElement('button');exportDxf.className='ghost';exportDxf.textContent='Export DXF';exportDxf.addEventListener('click',()=>exportSavedBoundaryDxf(layer));const remove=document.createElement('button');remove.className='ghost danger';remove.textContent='Remove';remove.addEventListener('click',()=>removeSavedBoundary(layer));controls.append(fit,exportDxf,remove);card.append(row,controls);ui.layerList.append(card);
  });
}
function moveLayer(index, delta) {
  const target = index + delta; if (target < 0 || target >= state.layers.length) return;
  [state.layers[index], state.layers[target]] = [state.layers[target], state.layers[index]];
  state.layers.forEach((layer, i) => { layer.group.renderOrder = i; }); renderLayerList();
}

// Timpa fungsi allProjectLayers() yang lama dengan ini:
function allProjectLayers(){return [...state.layers,...state.pdfLayers,...state.savedBoundaries,...(state.savedTracks || [])].sort((a,b)=>(b.order||0)-(a.order||0));}
function captureLayerSettings(){return allProjectLayers().filter(layer=>layer.kind!=='boundary').map(layer=>({name:layer.result?.name||layer.name,kind:layer.kind,role:layer.role,displayMode:layer.displayMode,visible:layer.visible,opacity:layer.opacity,order:layer.order,drapeToTop:layer.drapeToTop,color:layer.color,colorMode:layer.colorMode,sourceCrs:layer.sourceCrs??null,sourceCrsStatus:layer.sourceCrsStatus||'unknown',worldReference:layer.worldReference??null,canonicalBounds:layer.canonicalBounds??null,projectCrs:layer.projectCrs??null,crsStatus:layer.crsStatus||'unknown',crsAction:layer.crsAction||null,coordinatePattern:layer.coordinatePattern||null,locationStatus:layer.locationStatus||null,crsEvidence:layer.crsEvidence||null,localGridId:layer.localGridId||null,localGridName:layer.localGridName||null}));}
function applyLayerRenderOrder(){
  const layers=allProjectLayers();layers.forEach((layer,index)=>{const value=(layers.length-index)*4;if(layer.kind==='vector'){layer.lines.renderOrder=value+3;if(layer.pointsObject)layer.pointsObject.renderOrder=value+3;if(layer.surfaceMesh)layer.surfaceMesh.renderOrder=value+1;if(layer.surfaceWire)layer.surfaceWire.renderOrder=value+2;}else if(layer.kind==='pdf')layer.mesh.renderOrder=value;else layer.line.renderOrder=value+3;});
}
function enableLayerDrag(card,layer){
  const handle=card.querySelector('.layer-drag-handle');
  handle.addEventListener('pointerdown',event=>{
    if(event.button!==undefined&&event.button!==0)return;const pointerId=event.pointerId,startY=event.clientY;let dragging=false;
    const move=moveEvent=>{
      if(moveEvent.pointerId!==pointerId)return;if(!dragging&&Math.abs(moveEvent.clientY-startY)<5)return;dragging=true;card.classList.add('dragging');document.body.classList.add('layer-reordering');moveEvent.preventDefault();
      const listRect=ui.layerList.getBoundingClientRect();if(moveEvent.clientY<listRect.top+42)ui.layerList.scrollTop-=14;else if(moveEvent.clientY>listRect.bottom-42)ui.layerList.scrollTop+=14;
      const target=document.elementFromPoint(moveEvent.clientX,moveEvent.clientY)?.closest('.layer-card');if(!target||target===card||target.parentElement!==ui.layerList)return;const rect=target.getBoundingClientRect();ui.layerList.insertBefore(card,moveEvent.clientY<rect.top+rect.height/2?target:target.nextSibling);
    };
    const finish=finishEvent=>{
      if(finishEvent.pointerId!==pointerId)return;document.removeEventListener('pointermove',move);document.removeEventListener('pointerup',finish);document.removeEventListener('pointercancel',finish);card.classList.remove('dragging');document.body.classList.remove('layer-reordering');if(!dragging)return;
      const ids=[...ui.layerList.querySelectorAll('.layer-card')].map(node=>node.dataset.layerId);ids.forEach((id,index)=>{const item=[...state.layers,...state.pdfLayers,...state.savedBoundaries].find(value=>value.id===id);if(item)item.order=ids.length-index;});state.layerOrderCounter=Math.max(state.layerOrderCounter,ids.length);applyLayerRenderOrder();persistSavedBoundaries();touchActiveProject();showToast('Layer order updated.');
    };
    document.addEventListener('pointermove',move,{passive:false});document.addEventListener('pointerup',finish);document.addEventListener('pointercancel',finish);event.preventDefault();
  });
}
function layerCardBase(layer, titleText, detailText, badgeText) {
  const card = document.createElement('article'); card.className = 'layer-card'; card.dataset.layerId = layer.id;
  const row = document.createElement('div'); row.className = 'layer-row';
  
  // IKON DRAG
  const drag = document.createElement('button'); drag.type = 'button'; drag.className = 'layer-drag-handle'; drag.textContent = '⠿'; drag.title = 'Drag to change map drawing order';
  
  // IKON MATA (VISIBILITY)
  const visible = document.createElement('button');
  visible.className = 'ghost';
  visible.style.cssText = 'font-size:15px; padding:0 2px; cursor:pointer; min-width:28px;';
  visible.textContent = layer.visible ? '👁️' : '🙈';
  visible.title = 'Show or hide layer';

  // IKON KACA PEMBESAR (ZOOM TO LAYER)
  const zoomBtn = document.createElement('button');
  zoomBtn.className = 'ghost';
  zoomBtn.style.cssText = 'font-size:15px; padding:0 2px; cursor:pointer; min-width:28px;';
  zoomBtn.textContent = '🔍';
  zoomBtn.title = `Zoom to ${titleText}`;
  zoomBtn.setAttribute('aria-label',`Zoom to file ${titleText}`);

  // IKON TONG SAMPAH (REMOVE)
  const removeBtn = document.createElement('button');
  removeBtn.className = 'ghost danger';
  removeBtn.style.cssText = 'font-size:15px; padding:0 2px; cursor:pointer; min-width:28px;';
  removeBtn.textContent = '🗑️';
  removeBtn.title = 'Remove layer';
  
  const title = document.createElement('div'); title.className = 'layer-title';
  const label = document.createElement('b'); label.textContent = titleText;
  const detail = document.createElement('small'); detail.textContent = detailText;
  title.append(label, detail);
  
  const badge = document.createElement('button'); badge.className = 'dxf-color-button active'; badge.textContent = badgeText; badge.disabled = true;
  
  // Masukkan semua ikon berjejer rapi
  row.append(drag, visible, zoomBtn, removeBtn, title, badge);
  card.append(row);
  enableLayerDrag(card, layer);
  
  return { card, visible, zoomBtn, removeBtn, badge };
}

function renderLayerList() {
  ui.layerList.replaceChildren();
const layers = allProjectLayers();
const hasMarks =
  Array.isArray(state.marks) &&
  state.marks.length > 0;
  ui.empty.style.display =(layers.length || hasMarks)? 'none': 'grid';
for (const layer of layers) {
    if (layer.kind === 'vector') {
    const triangles = layer.result.surface?.indices?.length / 3 || 0, points = layer.result.pointPositions?.length / 3 || 0, geometryText = triangles ? `${layer.result.segments.toLocaleString('en-US')} segments · ${triangles.toLocaleString('en-US')} TIN triangles` : `${layer.result.segments.toLocaleString('en-US')} segments${points ? ` · ${points.toLocaleString('en-US')} points` : ''}`;
    let crsNote = '';
    if (layer.crsStatus === 'unknown') {
      const pattern = layer.coordinatePattern === 'utm-like'
        ? 'UTM-like'
        : layer.coordinatePattern === 'local-like'
          ? 'local coordinates'
          : 'raw coordinates';
      crsNote = ` · ${pattern} · location/CRS unverified`;
    } else if (layer.crsStatus === 'local-unresolved') {
      crsNote = ` · ${sameGridStatusLabel(layer)} · global location unverified`;
    } else if (layer.sourceCrs?.epsg) {
      crsNote =
        layer.crsAction === 'reproject'
          ? ` · EPSG:${layer.sourceCrs.epsg} → ${layer.projectCrs}`
          : ` · Source EPSG:${layer.sourceCrs.epsg}`;
    }
    const { card, visible, zoomBtn, removeBtn, badge } = layerCardBase(layer, layer.result.name, `${layer.result.format || 'DXF'} · ${geometryText}${crsNote}`, layer.result.format || 'DXF');
    badge.disabled = false; badge.title = 'Use original file colours';
    if (layer.sourceCrsStatus === 'unknown') {
      const locationBadge = document.createElement('button');
      locationBadge.type = 'button';
      locationBadge.className = 'ghost';
      locationBadge.textContent = '🌐?';
      locationBadge.title = 'Location/CRS is unverified. Open Advanced CRS options.';
      locationBadge.setAttribute('aria-label', 'Open Advanced CRS options');
      locationBadge.addEventListener('click', () => {
        if (layer.result.format === 'DXF') resolveDxfLayerCrs(layer);
        else if (layer.result.format === 'SHP') resolveShpLayerCrs(layer);
        else showToast('This file is shown at its raw coordinates. Open it with matching CRS metadata to verify its location.', 6000);
      });
      card.querySelector('.layer-row').append(locationBadge);
    }
      
      // LOGIKA TOMBOL-TOMBOL KECIL
      visible.addEventListener('click', () => { layer.visible = !layer.visible; visible.textContent = layer.visible ? '👁️' : '🙈'; layer.group.visible = layer.visible; if (selectionOverlay.userData.layer === layer) selectionOverlay.visible = layer.group.visible; recolorComparison(); refreshGeoPdfDrapes(); updateSection(); touchActiveProject(); });
      zoomBtn.addEventListener('click', () => fitVectorLayer(layer));
      removeBtn.addEventListener(
  'click',
  async () => {
    if (
      !confirm(
        `Hapus layer ${layer.result.name} dari project?`
      )
    ) {
      return;
    }

    try {
      if (state.currentProject?.id) {
        await deleteCachedFile(
          state.currentProject.id,
          layer.result.name
        );

        for (const companionName of layer.result.companionFileNames || []) {
          await deleteCachedFile(
            state.currentProject.id,
            companionName
          );
        }
      }

      disposeLayer(layer);

      state.layers =
        state.layers.filter(
          item => item !== layer
        );

      applySpatialRenderFrame();
      updateSpatialShadowProjectSummary();

      setStatus();
      renderLayerList();

      await touchActiveProject();

      showToast(
        `${layer.result.name} removed from project.`,
        4000
      );

    } catch (error) {
      console.error(
        '[Maply Project] File removal failed:',
        error
      );

      showToast(
        'Could not remove the source file from the project.',
        5000
      );
    }
  }
);
      
      const color = document.createElement('input'); color.type = 'color'; color.value = layer.color; color.title = 'Layer colour for lines, TIN, and surface'; card.querySelector('.layer-row').append(color); color.addEventListener('input', () => { applyLayerColor(layer, color.value); badge.classList.remove('active'); touchActiveProject(); });
      badge.classList.toggle('active', layer.colorMode === 'dxf'); badge.addEventListener('click', () => { const roleColor = colorForRole(layer.role); applyLayerColor(layer, roleColor, { original: true }); color.value = roleColor; badge.classList.add('active'); touchActiveProject(); });
      const controls = document.createElement('div'); controls.className = 'layer-controls'; const role = document.createElement('select'); [['bottom', 'Bottom'], ['top', 'Top'], ['reference', 'Reference']].forEach(([value, text]) => { const option = new Option(text, value, false, layer.role === value); role.append(option); });
      role.addEventListener('change', () => { layer.role = role.value; if (layer.colorMode === 'dxf') { layer.color = colorForRole(layer.role); if (layer.surfaceMesh) layer.surfaceMesh.material.color.set(layer.color); if (layer.surfaceWire) layer.surfaceWire.material.color.set(layer.color); color.value = layer.color; } recolorComparison(); refreshGeoPdfDrapes(); updateSection(); touchActiveProject(); });
      const display = document.createElement('select'), displayOptions = layer.surfaceMesh ? [['lines', 'DXF Lines'], ['surface', 'DTM Surface'], ['tin', 'TIN Triangles'], ['lines-surface', 'Lines + Surface']] : [['lines', 'Vector Lines']]; displayOptions.forEach(([value, text]) => display.append(new Option(text, value, false, layer.displayMode === value))); display.addEventListener('change', () => { setLayerDisplayMode(layer, display.value); touchActiveProject(); });
      const opacity = document.createElement('input'); opacity.type = 'range'; opacity.min = .08; opacity.max = 1; opacity.step = .01; opacity.value = layer.opacity; opacity.title = 'Opacity'; opacity.addEventListener('input', () => { layer.opacity = Number(opacity.value); layer.lines.material.opacity = layer.opacity; if (layer.pointsObject) layer.pointsObject.material.opacity = layer.opacity; if (layer.surfaceMesh) layer.surfaceMesh.material.opacity = Math.min(.94, layer.opacity); if (layer.surfaceWire) layer.surfaceWire.material.opacity = layer.opacity; scheduleProjectAutosave();});
      
      controls.append(role, display, opacity);
      card.append(controls); ui.layerList.append(card);
    
    } else if (layer.kind === 'pdf') {
      const coverageValue = Number(layer.drapeCoverage);
      const coverage = Number.isFinite(coverageValue)
        ? ` · Top drape ${coverageValue > 0 && coverageValue < 1 ? coverageValue.toFixed(1) : coverageValue.toFixed(0)}%`
        : '';
      const { card, visible, zoomBtn, removeBtn } = layerCardBase(layer, layer.name, `GeoPDF · EPSG:${layer.metadata.sourceEpsg} → EPSG:${layer.metadata.targetEpsg}${coverage}`, 'PDF');
      
      visible.addEventListener('click', () => { layer.visible = !layer.visible; visible.textContent = layer.visible ? '👁️' : '🙈'; layer.mesh.visible = layer.visible; touchActiveProject(); });
      zoomBtn.addEventListener('click', () => fitAbsoluteBounds(layer.bounds));
      removeBtn.addEventListener(
  'click',
  async () => {
    if (
      !confirm(
        `Hapus GeoPDF ${layer.name} dari project?`
      )
    ) {
      return;
    }

    try {
      if (state.currentProject?.id) {
        await deleteCachedFile(
          state.currentProject.id,
          layer.name
        );
      }

      disposePdfLayer(layer);

      state.pdfLayers =
        state.pdfLayers.filter(
          item => item !== layer
        );

      applySpatialRenderFrame();
      setStatus();
      renderLayerList();

      await touchActiveProject();

      showToast(
        `${layer.name} removed from project.`,
        4000
      );

    } catch (error) {
      console.error(
        '[Maply Project] GeoPDF removal failed:',
        error
      );

      showToast(
        'Could not remove the GeoPDF from the project.',
        5000
      );
    }
  }
);

      const controls = document.createElement('div'); controls.className = 'layer-controls';
      const drape = document.createElement('button'); drape.className = 'ghost'; drape.textContent = layer.drapeToTop ? 'Drape: Top' : 'Drape: Flat';
      drape.addEventListener('click', () => { layer.drapeToTop = !layer.drapeToTop; rebuildGeoPdfGeometry(layer); renderLayerList(); touchActiveProject(); });
      const opacity = document.createElement('input'); opacity.type = 'range'; opacity.min = .08; opacity.max = 1; opacity.step = .01; opacity.value = layer.opacity;
      opacity.addEventListener('input', () => { layer.opacity = Number(opacity.value); layer.mesh.material.opacity = layer.opacity; scheduleProjectAutosave();});
      
      controls.append(drape, opacity); card.append(controls); ui.layerList.append(card);
    
    } else if (layer.kind === 'track') {
      const record = layer.record;
      const { card, visible, zoomBtn, removeBtn } = layerCardBase(layer, record.name, `GPS Track · ${(record.distance || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} m long`, 'TRK');
      
      visible.addEventListener('click', () => { layer.visible = !layer.visible; visible.textContent = layer.visible ? '👁️' : '🙈'; layer.group.visible = layer.visible; });
      zoomBtn.addEventListener('click', () => fitAbsoluteBounds(layer.bounds));
      removeBtn.addEventListener('click', () => { if (confirm(`Hapus Track ${record.name}?`)) removeSavedTrack(layer); });
      
      const controls = document.createElement('div'); controls.className = 'saved-boundary-controls'; 
      const exportDxf = document.createElement('button'); exportDxf.className = 'ghost'; exportDxf.textContent = 'Export DXF'; exportDxf.addEventListener('click', () => exportSavedTrackDxf(layer));
      
      controls.append(exportDxf); card.append(controls); ui.layerList.append(card);
    
    } else {
      const record = layer.record;
      const { card, visible, zoomBtn, removeBtn } = layerCardBase(layer, record.name, `Saved Boundary · ${Number(record.area || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} m² · ${layer.role}`, 'BDY');
      
      visible.addEventListener('click', () => { layer.visible = !layer.visible; visible.textContent = layer.visible ? '👁️' : '🙈'; layer.group.visible = layer.visible; updateSection(); });
      zoomBtn.addEventListener('click', () => fitAbsoluteBounds(layer.bounds));
      removeBtn.addEventListener('click', () => { if (confirm(`Hapus Boundary ${record.name}?`)) removeSavedBoundary(layer); });
      
      const controls = document.createElement('div'); controls.className = 'saved-boundary-controls';
      const role = document.createElement('select'); [['bottom', 'Bottom'], ['top', 'Top'], ['reference', 'Reference']].forEach(([value, text]) => role.append(new Option(text, value, false, layer.role === value)));
      role.addEventListener('change', () => { layer.role = role.value; layer.record.role = layer.role; persistSavedBoundaries(); refreshGeoPdfDrapes(); updateSection(); renderLayerList(); });
      const exportDxf = document.createElement('button'); exportDxf.className = 'ghost'; exportDxf.textContent = 'Export DXF'; exportDxf.addEventListener('click', () => exportSavedBoundaryDxf(layer));
      
      controls.append(role, exportDxf); card.append(controls); ui.layerList.append(card);
    }
  }
  applyLayerRenderOrder();
      if (state.marks.length) {
      const markLayer = {
        id: 'point-mark-collection',
        kind: 'mark-collection',
        visible:
          marksGroup.visible !== false
      };

      const {
        card,
        visible,
        zoomBtn,
        removeBtn,
        badge
      } = layerCardBase(
        markLayer,
        'Point Mark',
        `${state.marks.length} point${state.marks.length === 1 ? '' : 's'}`,
        'PTS'
      );

      // Collection ini bukan drawing-order layer biasa.
      const dragHandle =
        card.querySelector(
          '.layer-drag-handle'
        );

      if (dragHandle) {
        dragHandle.disabled = true;
        dragHandle.style.opacity = '0.35';
      }

      visible.addEventListener(
        'click',
        () => {
          markLayer.visible =
            !markLayer.visible;

          marksGroup.visible =
            markLayer.visible;

          visible.textContent =
            markLayer.visible
              ? '👁️'
              : '🙈';
        }
      );

      zoomBtn.addEventListener(
        'click',
        () => {
          if (!state.marks.length) return;

          const xs =
            state.marks.map(
              mark => mark.easting
            );

          const ys =
            state.marks.map(
              mark => mark.northing
            );

          fitAbsoluteBounds({
            minX: Math.min(...xs),
            minY: Math.min(...ys),
            maxX: Math.max(...xs),
            maxY: Math.max(...ys)
          });
        }
      );

      removeBtn.addEventListener(
        'click',
        async () => {
          if (
            !confirm(
              `Hapus semua ${state.marks.length} Point Mark dari project?`
            )
          ) {
            return;
          }

          state.marks.forEach(mark => {
            if (mark.node) {
              marksGroup.remove(
                mark.node
              );
            }
          });

          state.marks = [];

          persistMarks();

          applySpatialRenderFrame();

          await touchActiveProject();

          renderLayerList();

          showToast(
            'Point Mark collection removed.'
          );
        }
      );
      ui.layerList.append(card);
    }
}

function deltaColor(delta) {
  if (delta < 0) { const t=Math.min(1,Math.abs(delta));return [0.08-t*.02,0.76-t*.47,0.95]; }
  if (delta > 0) { const t=Math.min(1,delta);return [1,0.75-t*.57,0.12]; }
  return [0.12,0.82,0.43];
}
function recolorComparison() {
  const bottom = state.layers.find(l => l.visible && l.role === 'bottom' && l.sampler);
  for (const layer of state.layers) {
    if (!layer.surfaceMesh) continue;
    const geometry = layer.surfaceMesh.geometry, material = layer.surfaceMesh.material;
    if (layer.role === 'top' && bottom && layer.colorMode!=='manual') {
      const positions = geometry.attributes.position.array, colors = new Float32Array(positions.length);
      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i] + layer.group.position.x, y = positions[i + 1] + layer.group.position.y;
        const renderZ = positions[i + 2] + layer.group.position.z;
        const projected = state.renderFrame === RENDER_FRAME_ENU
          ? renderCoordinateToProject(x, y, renderZ)
          : null;
        const sampleX = projected ? projected.x - state.projectOrigin.x : x;
        const sampleY = projected ? projected.y - state.projectOrigin.y : y;
        const topZ = projected ? projected.z : positions[i + 2];
        const bottomZ = bottom.sampler.sample(sampleX, sampleY), delta = bottomZ === null ? null : topZ - bottomZ;
        const c = delta === null ? [0.42, 0.52, 0.58] : deltaColor(delta);
        colors[i] = c[0]; colors[i + 1] = c[1]; colors[i + 2] = c[2];
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3)); material.vertexColors = true; material.color.set(0xffffff);
    } else {
      geometry.deleteAttribute('color'); material.vertexColors = false; material.color.set(layer.color);
    }
    material.needsUpdate = true;
  }
}

function projectBounds() {
  if (!state.projectOrigin || !(
    state.layers.length ||
    state.pdfLayers.length ||
    state.savedBoundaries.length ||
    state.savedTracks.length
  )) return null;
  return combineRenderBounds([
    ...state.layers.filter(layer => layer.visible).map(layer =>
      state.renderFrame === RENDER_FRAME_ENU &&
      layer.activeRenderFrame === RENDER_FRAME_ENU
        ? enuBoundsToRender(layer.spatialShadow?.bounds)
        : projectedBoundsToRender(layer.result.bounds, state.projectOrigin)
    ),
    ...state.pdfLayers.filter(layer => layer.visible).map(layer =>
      state.renderFrame === RENDER_FRAME_ENU &&
      layer.activeRenderFrame === RENDER_FRAME_ENU
        ? enuBoundsToRender(layer.canonicalBounds)
        : projectedBoundsToRender(layer.bounds, state.projectOrigin)
    ),
    ...state.savedBoundaries.filter(layer => layer.visible).map(layer =>
      state.renderFrame === RENDER_FRAME_ENU &&
      layer.activeRenderFrame === RENDER_FRAME_ENU
        ? enuBoundsToRender(layer.canonicalBounds)
        : projectedBoundsToRender(layer.bounds, state.projectOrigin)
    ),
    ...state.savedTracks.filter(layer => layer.visible).map(layer =>
      state.renderFrame === RENDER_FRAME_ENU &&
      layer.activeRenderFrame === RENDER_FRAME_ENU
        ? enuBoundsToRender(layer.canonicalBounds)
        : projectedBoundsToRender(layer.bounds, state.projectOrigin)
    )
  ]);
}
function fitBounds(b) {
  if (!b) return;
  if (![b.minX,b.minY,b.minZ,b.maxX,b.maxY,b.maxZ].every(Number.isFinite)) {
    console.warn('Cannot fit non-finite coordinates.');
    return;
  }
  let width = Math.max(20, b.maxX - b.minX);
  let height = Math.max(20, b.maxY - b.minY);
  let cx = (b.minX + b.maxX) / 2;
  let cy = (b.minY + b.maxY) / 2;
  let cz = (b.minZ + b.maxZ) / 2 * state.exaggeration;
  const vertical = Math.max(20, (b.maxZ - b.minZ) * state.exaggeration);
  
  if (state.mode === '2d') {
    const aspect = Math.max(0.2, ui.viewport.clientWidth / ui.viewport.clientHeight);
    const size = Math.max(height, width / aspect) * 1.15;
    ortho.left = -size * aspect / 2; ortho.right = size * aspect / 2; 
    ortho.top = size / 2; ortho.bottom = -size / 2; 
    ortho.near = 0.1; ortho.far = Math.max(100000, size * 4); 
    ortho.position.set(cx, cy, b.maxZ * state.exaggeration + size); controls2d.target.set(cx, cy, 0); 
    ortho.updateProjectionMatrix(); controls2d.update();
  } else {
    const d = Math.max(width, height, vertical) * 1.25; 
    perspective.position.set(cx + d * .75, cy - d * .75, cz + d * .62); controls3d.target.set(cx, cy, cz); 
    perspective.near = Math.max(0.1, d / 5000); perspective.far = Math.max(100000, d * 20); 
    perspective.updateProjectionMatrix(); controls3d.update();
  }
}
function fitView() { 
  const b = projectBounds(); 
  
  if (!b) {
    if (state.gpsFix) centerOnGps();
    return;
  }
  
  fitBounds(b); 
}
function fitAllProject(){
  const bounds=projectBounds();
  if(!bounds){
    if(state.gpsFix){centerOnGps();return;}
    showToast('No visible project data to fit.',3500);
    return;
  }
  fitBounds(bounds);
  showToast('Fit all visible project layers.',2600);
}
function fitVectorLayer(layer) {
  if (!layer?.result?.bounds) {
    return;
  }
  const bounds =
    state.renderFrame === RENDER_FRAME_ENU &&
    layer.activeRenderFrame === RENDER_FRAME_ENU
      ? enuBoundsToRender(layer.spatialShadow?.bounds)
      : projectedBoundsToRender(
          layer.result.bounds,
          state.projectOrigin || layer.result.origin
        );
  fitBounds(bounds);
}
function fitAbsoluteBounds(bounds) {
  if (!state.projectOrigin || !bounds) return;
  const renderBounds = state.renderFrame === RENDER_FRAME_ENU
    ? enuBoundsToRender(
        canonicalBoundsFromProjectBounds(bounds, state.spatialFrame)
      )
    : projectedBoundsToRender(bounds, state.projectOrigin);
  fitBounds(renderBounds);
}
function zoomView(direction){
  if(state.mode==='2d'){
    ortho.zoom=THREE.MathUtils.clamp(ortho.zoom*(direction>0?1.35:1/1.35),.02,500);ortho.updateProjectionMatrix();controls2d.update();
  }else{
    const offset=perspective.position.clone().sub(controls3d.target),scale=direction>0?.72:1.38;perspective.position.copy(controls3d.target).add(offset.multiplyScalar(scale));controls3d.update();
  }
}
function setMode(mode) {
  state.mode = mode; 
  activeCamera = mode === '2d' ? ortho : perspective; 
  activeControls = mode === '2d' ? controls2d : controls3d;
  controls2d.enabled = mode === '2d'; 
  controls3d.enabled = mode === '3d';
  
  configureControlsForTool();
  if (modeToggleBtn) {
  modeToggleBtn.textContent =
    mode === '2d' ? '3D' : '2D';

  modeToggleBtn.setAttribute(
    'aria-label',
    mode === '2d'
      ? 'Switch to 3D'
      : 'Switch to 2D'
  );
} 
  fitView();
  
  if(mode==='3d') showToast(state.tool==='pan' ? '3D Pan is active · choose Select, then drag one finger to rotate.' : '3D: drag one finger to rotate · choose Pan to move · pinch/two fingers to zoom.', 6000);
  if (typeof isBasemapVisible !== 'undefined' && isBasemapVisible) {
    updateDynamicBasemap();
  }
}
function configureControlsForTool() {
  controls2d.mouseButtons.LEFT = THREE.MOUSE.PAN;
  controls2d.touches.ONE = THREE.TOUCH.PAN;
  controls2d.touches.TWO = THREE.TOUCH.DOLLY_PAN;

  controls3d.mouseButtons.LEFT =
    state.tool === 'pan'
      ? THREE.MOUSE.PAN
      : THREE.MOUSE.ROTATE;

  controls3d.touches.ONE =
    state.tool === 'pan'
      ? THREE.TOUCH.PAN
      : THREE.TOUCH.ROTATE;

  controls3d.touches.TWO = THREE.TOUCH.DOLLY_PAN;
}
function preset(name) {
  const b = projectBounds(); if (!b) return;
  const c = new THREE.Vector3((b.minX+b.maxX)/2, (b.minY+b.maxY)/2, (b.minZ+b.maxZ)/2 * state.exaggeration), d = Math.max(b.maxX-b.minX,b.maxY-b.minY,100)*1.3;
  if (name === 'top') { setMode('2d'); fitView(); return; }
  if (state.mode !== '3d') setMode('3d');
  if (name === 'front') perspective.position.set(c.x, c.y-d, c.z + d*.05);
  else if (name === 'right') perspective.position.set(c.x+d, c.y, c.z + d*.05);
  else perspective.position.set(c.x+d*.72, c.y-d*.72, c.z+d*.62);
  controls3d.target.copy(c); controls3d.update();
}
function resetNorth() {
  const target=activeControls.target.clone(),distance=Math.max(100,activeCamera.position.distanceTo(target));
  if(state.mode==='2d'){
    ortho.up.set(0,1,0);ortho.position.set(target.x,target.y,target.z+distance);ortho.lookAt(target);controls2d.update();
  }else{
    perspective.up.set(0,0,1);perspective.position.set(target.x+distance*.62,target.y-distance*.62,target.z+distance*.48);perspective.lookAt(target);controls3d.update();
  }
  showToast('North orientation reset.');
}
function openCoordinateSearch() {
  if(state.projectOrigin){const target=activeControls.target,projected=renderCoordinateToProject(target.x,target.y,target.z);if(projected){ui.coordinateE.value=projected.x.toFixed(2);ui.coordinateN.value=projected.y.toFixed(2);}}
  ui.coordinateSearch.classList.remove('hidden');ui.contentSearch.focus();ui.contentSearch.select();
}
function goToCoordinate() {
  const query=ui.contentSearch.value.trim();
  if(query){
    const values=query.match(/-?\d+(?:\.\d+)?/g)?.map(Number)||[];
    if(values.length>=2){ui.coordinateE.value=values[0];ui.coordinateN.value=values[1];}
    else{
      const match=[...state.layers,...state.pdfLayers].find(layer=>(layer.result?.name||layer.name||'').toLowerCase().includes(query.toLowerCase()));
      if(!match)return showToast(`No layer or file matches “${query}”.`,4200);
      fitAbsoluteBounds(match.result?.bounds||match.bounds);ui.coordinateSearch.classList.add('hidden');showToast(`Found: ${match.result?.name||match.name}`,3500);return;
    }
  }
  if(!state.projectOrigin)return showToast('Open map data before searching coordinates.');
  const easting=Number(ui.coordinateE.value),northing=Number(ui.coordinateN.value);if(!Number.isFinite(easting)||!Number.isFinite(northing))return showToast('Enter valid Easting and Northing values.');
  const renderPoint=projectCoordinateToRender(easting,northing,0);if(!renderPoint)return showToast('Coordinate could not be transformed into the active view.',4500);const x=renderPoint.x,y=renderPoint.y,z=topElevation(x,y),displayZ=(state.renderFrame===RENDER_FRAME_ENU?renderPoint.z:0)+z*state.exaggeration+5;
  coordinateMarker.position.set(x,y,displayZ);coordinateMarker.visible=true;
  if(state.mode==='2d'){
    const offset=ortho.position.clone().sub(controls2d.target);controls2d.target.set(x,y,0);ortho.position.set(x+offset.x,y+offset.y,offset.z);controls2d.update();
  }else{
    const offset=perspective.position.clone().sub(controls3d.target);controls3d.target.set(x,y,displayZ);perspective.position.copy(controls3d.target).add(offset);controls3d.update();
  }
  ui.coordinateSearch.classList.add('hidden');showToast(`Location E ${easting.toFixed(2)} · N ${northing.toFixed(2)} · RL ${z.toFixed(2)}`,4500);
}
function boundaryDisplayZ(point){const reference=Number(ui.boundaryElevation.value),elevation=Number.isFinite(reference)&&ui.boundaryElevation.value.trim()!==''?reference:topElevation(point.x,point.y);return renderElevationZ(point.x,point.y,elevation);}
function boundaryPolygonPoints(){const points=state.boundary.points;if(state.boundary.closed||!state.boundary.preview)return points;return [...points,state.boundary.preview];}
function polygonMetrics(points){
  let twiceArea=0,cx=0,cy=0;for(let i=0;i<points.length;i++){const a=points[i],b=points[(i+1)%points.length],cross=a.x*b.y-b.x*a.y;twiceArea+=cross;cx+=(a.x+b.x)*cross;cy+=(a.y+b.y)*cross;}
  const signedArea=twiceArea/2,area=Math.abs(signedArea);if(Math.abs(twiceArea)<1e-8)return{area,centroid:points.reduce((sum,p)=>sum.add(p),new THREE.Vector2()).multiplyScalar(1/points.length)};
  return{area,centroid:new THREE.Vector2(cx/(3*twiceArea),cy/(3*twiceArea))};
}
function updateBoundaryAreaLabel(points,metrics){
  if(points.length<3||metrics.area<.01){boundaryAreaLabel.visible=false;return;}
  const context=boundaryAreaCanvas.getContext('2d');context.clearRect(0,0,boundaryAreaCanvas.width,boundaryAreaCanvas.height);context.fillStyle='rgba(24,8,8,.94)';context.strokeStyle='#ff736d';context.lineWidth=8;context.beginPath();context.roundRect(8,8,752,176,34);context.fill();context.stroke();context.fillStyle='#fff';context.font='800 68px system-ui';context.textAlign='center';context.textBaseline='middle';context.fillText(`${metrics.area.toLocaleString('en-US',{maximumFractionDigits:1})} m²`,384,100);boundaryAreaTexture.needsUpdate=true;
  boundaryAreaLabel.position.set(metrics.centroid.x,metrics.centroid.y,boundaryDisplayZ(metrics.centroid)+14);boundaryAreaLabel.visible=true;
}
function updateBoundaryAreaLabelSize(){
  if(!boundaryAreaLabel.visible)return;const height=Math.max(1,ui.viewport.clientHeight),point=boundaryAreaLabel.position;
  const unitsPerPixel=state.mode==='2d'?(ortho.top-ortho.bottom)/Math.max(.001,ortho.zoom)/height:2*Math.max(1,perspective.position.distanceTo(point))*Math.tan(THREE.MathUtils.degToRad(perspective.fov/2))/height;
  boundaryAreaLabel.scale.set(unitsPerPixel*190,unitsPerPixel*48,1);
}
function updateBoundaryVertexSizes() {
  if (!boundaryVertices.visible) return;

  const height = Math.max(1, ui.viewport.clientHeight);

  boundaryVertices.children.forEach(node => {
    const point = node.position;

    const unitsPerPixel = state.mode === '2d'
      ? (ortho.top - ortho.bottom) /
        Math.max(0.001, ortho.zoom) /
        height
      : 2 *
        Math.max(1, perspective.position.distanceTo(point)) *
        Math.tan(
          THREE.MathUtils.degToRad(perspective.fov / 2)
        ) /
        height;

    // 1.05 menghasilkan label sekitar 23 px
    node.scale.setScalar(
      Math.max(0.001, unitsPerPixel * 1.05)
    );
  });
}
function updateBoundaryFillAndHatch(){
  const points=boundaryPolygonPoints();if(points.length<3){boundaryFill.visible=false;boundaryHatch.visible=false;boundaryAreaLabel.visible=false;return;}
  const base=points[0],shape=new THREE.Shape();shape.moveTo(0,0);for(let i=1;i<points.length;i++)shape.lineTo(points[i].x-base.x,points[i].y-base.y);shape.closePath();
  const displayZ=points.reduce((sum,p)=>sum+boundaryDisplayZ(p),0)/points.length;
  boundaryFill.geometry.dispose();boundaryFill.geometry=new THREE.ShapeGeometry(shape);boundaryFill.position.set(base.x,base.y,displayZ+2);boundaryFill.visible=true;
  boundaryHatch.geometry.dispose();boundaryHatch.geometry=new THREE.ShapeGeometry(shape);boundaryHatch.position.set(base.x,base.y,displayZ+4);const width=Math.max(...points.map(p=>p.x))-Math.min(...points.map(p=>p.x)),height=Math.max(...points.map(p=>p.y))-Math.min(...points.map(p=>p.y));boundaryHatchTexture.repeat.set(Math.max(1,width/35),Math.max(1,height/35));boundaryHatch.visible=true;
  const metrics=polygonMetrics(points);updateBoundaryAreaLabel(points,metrics);
}
// ==========================================================
// 1. UPDATE BOUNDARY VISUAL (Live line langsung mengekor crosshair)
// ==========================================================
function updateBoundaryVisual(refreshVertices = true) {
  const points = state.boundary.points;
  if (typeof boundaryVertices !== 'undefined' && boundaryVertices) {
    boundaryVertices.visible = true;
    if (refreshVertices) {
      while (boundaryVertices.children.length) boundaryVertices.remove(boundaryVertices.children[0]);
      points.forEach((point, index) => {
        const z = boundaryDisplayZ(point) + 6, 
              node = new THREE.Group(), 
              marker = new THREE.Mesh(boundaryPointGeometry, boundaryPointMaterial), 
              label = endpointLabel(String(index + 1), '#ff736d');
        marker.renderOrder = 25; label.position.z = 14; 
        node.position.set(point.x, point.y, z); 
        node.add(marker, label); boundaryVertices.add(node);
      });
    }
  }

  // Susun path jalur garis: titik permanen + posisi crosshair saat ini (preview) + kembali ke titik [0]
  let path = [...points];
  if (state.boundary.preview && !state.boundary.closed) {
    path.push(state.boundary.preview);
  }

  // Jika sudah ada titik, paksa sambungkan kembali ke titik awal [0] agar live line berbentuk poligon tertutup
  if (path.length >= 1 && points.length > 0) {
    path.push(points[0]);
  }

  if (path.length >= 2) {
    const positions = [];
    path.forEach(point => {
      positions.push(point.x, point.y, boundaryDisplayZ(point) + 6);
    });

    boundaryLine.geometry.dispose();
    const newGeometry = new LineGeometry();
    newGeometry.setPositions(positions);
    boundaryLine.geometry = newGeometry;
    boundaryLine.computeLineDistances();
    boundaryLine.material.resolution.set(ui.viewport.clientWidth || 1, ui.viewport.clientHeight || 1);
    boundaryHalo.geometry.dispose();
    const newHaloGeometry = new LineGeometry();
    newHaloGeometry.setPositions(positions);
    boundaryHalo.geometry = newHaloGeometry;
    boundaryHalo.computeLineDistances();
    boundaryHalo.material.resolution.set(ui.viewport.clientWidth || 1, ui.viewport.clientHeight || 1);
    boundaryHalo.visible = true;
    boundaryLine.visible = true;
  } else {
    boundaryHalo.visible = false;
    boundaryLine.visible = false;
  }

  updateBoundaryFillAndHatch();
  
  const livePoints = boundaryPolygonPoints();
  const area = livePoints.length >= 3 ? polygonMetrics(livePoints).area : 0;
  
  ui.boundaryInstruction.textContent = state.boundary.closed 
    ? `${points.length} vertices · ${area.toLocaleString('en-US', { maximumFractionDigits: 1 })} m² · closed` 
    : `${points.length} vertices${area ? ` · ${area.toLocaleString('en-US', { maximumFractionDigits: 1 })} m²` : ''} · press Add Point`;
} 
function updateMeasureLiveLabelSize() {
  if (!measureCalloutEngine.label.visible) return;
  
  const height =
    Math.max(1, ui.viewport.clientHeight);

  const point =
    measureCalloutEngine.label.position;

  const unitsPerPixel =
    state.mode === '2d'
      ? (ortho.top - ortho.bottom) /
        Math.max(0.001, ortho.zoom) /
        height
      : 2 *
        Math.max(
          1,
          perspective.position.distanceTo(point)
        ) *
        Math.tan(
          THREE.MathUtils.degToRad(
            perspective.fov / 2
          )
        ) /
        height;

  measureCalloutEngine.label.scale.set(
    unitsPerPixel * 220,
    unitsPerPixel * 54,
    1
  );

  const pointScale =
    unitsPerPixel * 1.4;

  measureCalloutEngine.anchorPoint.scale.setScalar(
    Math.max(0.0001, pointScale)
  );
  measureCalloutEngine.labelPoint.scale.setScalar(
  Math.max(0.0001, pointScale)
  );
}
function hideBoundaryVisual(){boundaryHalo.visible=false;boundaryLine.visible=false;boundaryFill.visible=false;boundaryHatch.visible=false;boundaryAreaLabel.visible=false;boundaryVertices.visible=false;}
function clearBoundaryData(){state.boundary={points:[],closed:false,result:null,preview:null,referenceZ:null};ui.boundaryElevation.value='';hideBoundaryVisual();while(boundaryVertices.children.length)boundaryVertices.remove(boundaryVertices.children[0]);ui.volumeResults.textContent='Add at least three vertices, then close the boundary to calculate Top − Bottom.';ui.boundaryInstruction.textContent='Double-tap boundary vertices';}
function undoBoundaryPoint(){if(state.boundary.closed){state.boundary.closed=false;state.boundary.result=null;}state.boundary.preview=null;state.boundary.points.pop();updateBoundaryVisual();ui.volumeResults.textContent='Boundary updated. Close it again to calculate volume.';}
function calculateBoundaryVolume(points){
  const samplerPoints=state.renderFrame===RENDER_FRAME_ENU?renderPointsToSamplerSpace(points):points;if(samplerPoints.length!==points.length)throw new Error('Boundary coordinates could not be transformed into Project CRS.');
  const referenceText=ui.boundaryElevation.value.trim(),reference=Number(referenceText);if(referenceText!==''&&Number.isFinite(reference)){const surface=firstSampler('top')||firstSampler('bottom');if(!surface)throw new Error('An active Top or Bottom surface is required for the reference elevation calculation.');state.boundary.referenceZ=reference;return estimateBoundaryVolume(samplerPoints,{sample:()=>reference},surface);}
  const bottom=firstSampler('bottom'),top=firstSampler('top');if(!bottom||!top)throw new Error('Both Bottom and Top DXF layers must be active, or enter a Reference Elevation Z.');
  return estimateBoundaryVolume(samplerPoints,bottom,top);
}
function saveBoundaryReference(){
  if(state.boundary.points.length<3)return showToast('Create at least three boundary vertices before saving.');if(!state.projectOrigin)return showToast('Open map data before saving a boundary.');
  const value=ui.boundaryElevation.value.trim(),parsed=Number(value),reference=value!==''&&Number.isFinite(parsed)?parsed:null,metricPoints=state.renderFrame===RENDER_FRAME_ENU?renderPointsToSamplerSpace(state.boundary.points):state.boundary.points,metrics=polygonMetrics(metricPoints);
  state.boundary.closed=true;state.boundary.preview=null;state.boundary.referenceZ=reference;updateBoundaryVisual();const projectCrs=currentProjectCrs(),coordinates=state.boundary.points.map(point=>{const elevation=reference??topElevation(point.x,point.y),projected=renderHorizontalToProject(point.x,point.y);return[projected?.x??point.x+state.projectOrigin.x,projected?.y??point.y+state.projectOrigin.y,elevation];}),saved={id:crypto.randomUUID(),name:`Boundary ${new Date().toLocaleString('en-US')}`,crs:projectCrs?`EPSG:${projectCrs.epsg}`:'LOCAL',order:nextLayerOrder(),role:'reference',referenceElevation:reference,area:metrics.area,coordinates,canonicalCoordinates:canonicalProjectCoordinates(coordinates)};
  try{const key=boundaryStorageKey(),items=JSON.parse(localStorage.getItem(key)||'[]');items.push(saved);localStorage.setItem(key,JSON.stringify(items));createSavedBoundaryLayer(saved);renderLayerList();setTool('select');openDrawer(true);showToast(`Boundary added to Project Explorer · ${metrics.area.toFixed(1)} m²${reference===null?'':` · Z ${reference.toFixed(2)} m`}`,5200);}catch{showToast('The boundary could not be saved to local storage.',5000);}
}
async function finishBoundary(){
  if(state.boundary.points.length<3)return showToast('A boundary requires at least three vertices.');state.boundary.preview=null;state.boundary.closed=true;updateBoundaryVisual();setBusy(true,'Calculating volume','Sampling Top − Bottom inside the boundary…',55);
  await new Promise(resolve=>requestAnimationFrame(()=>setTimeout(resolve,20)));
  try{const result=calculateBoundaryVolume(state.boundary.points),reference=state.boundary.referenceZ,comparison=Number.isFinite(reference)?`Active surface vs Reference Z ${reference.toFixed(2)} m`:'Top vs Bottom surfaces';state.boundary.result=result;ui.volumeResults.innerHTML=`Comparison: ${comparison}<br>Boundary area: ${result.area.toLocaleString('en-US',{maximumFractionDigits:1})} m²<br>Calculated area: ${result.validArea.toLocaleString('en-US',{maximumFractionDigits:1})} m² (${result.coverage.toFixed(1)}%)<br>Fill volume: ${result.fill.toLocaleString('en-US',{maximumFractionDigits:1})} m³<br>Cut volume: ${result.cut.toLocaleString('en-US',{maximumFractionDigits:1})} m³<br>Net Cut − Fill: ${result.net.toLocaleString('en-US',{maximumFractionDigits:1})} m³<br>Estimated grid: ${result.cell.toFixed(2)} m`;showToast('Cut/Fill volume estimate completed.');}catch(error){state.boundary.closed=false;updateBoundaryVisual();showToast(error.message,5000);}finally{setBusy(false);}
}
function csvValue(value){if(value===null||value===undefined)return '';const text=String(value);return /[",\n\r]/.test(text)?`"${text.replace(/"/g,'""')}"`:text;}
function exportVolumeCsv(){const r=state.boundary.result;if(!r)return showToast('No boundary volume result is available.');const rows=['metric,value,unit',`boundary_area,${r.area.toFixed(3)},m2`,`calculated_area,${r.validArea.toFixed(3)},m2`,`coverage,${r.coverage.toFixed(3)},percent`,`volume_fill_top_below_bottom,${r.fill.toFixed(3)},m3`,`volume_cut_top_above_bottom,${r.cut.toFixed(3)},m3`,`net_cut_minus_fill,${r.net.toFixed(3)},m3`,`grid_size,${r.cell.toFixed(3)},m`,'','boundary_vertex,easting,northing'];state.boundary.points.forEach((p,i)=>{const projected=renderHorizontalToProject(p.x,p.y);rows.push(`${i+1},${(projected?.x??p.x+state.projectOrigin.x).toFixed(3)},${(projected?.y??p.y+state.projectOrigin.y).toFixed(3)}`);});downloadBlob(new Blob([rows.join('\n')],{type:'text/csv'}),`boundary-volume-${Date.now()}.csv`);}
async function exportTrackCsv() {
  try {
    const tracks = state.savedTracks.map(layer => layer.record);

    if (state.tracking.points.length) {
      const activeCrs = currentProjectCrs();
      tracks.push({
        id: 'active',
        name: 'Active GPS Track',
        crs: activeCrs ? `EPSG:${activeCrs.epsg}` : null,
        crsStatus: projectCrsStatus(),
        points: state.tracking.points
      });
    }

    if (!tracks.length) {
      return showToast('No GPS track points are available.');
    }

    const rows = [[
      'track_id',
      'track_name',
      'point_index',
      'project_crs',
      'crs_status',
      'easting',
      'northing',
      'rl',
      'latitude',
      'longitude',
      'altitude',
      'accuracy',
      'time'
    ].join(',')];

    for (const track of tracks) {
      const crs = confirmedTrackCrs(track);

      if (!crs) {
        throw new Error(
          `Confirm the Project CRS before exporting “${track.name || 'GPS Track'}”.`
        );
      }

      const points = normalizedTrackPoints(track);

      if (
        points.some(point =>
          !Number.isFinite(point.easting) ||
          !Number.isFinite(point.northing)
        )
      ) {
        throw new Error(
          `Track “${track.name || 'GPS Track'}” contains invalid absolute coordinates.`
        );
      }

      points.forEach((point, index) => {
        rows.push([
          track.id,
          track.name,
          index + 1,
          `EPSG:${crs.epsg}`,
          'confirmed',
          point.easting.toFixed(3),
          point.northing.toFixed(3),
          Number.isFinite(point.rl) ? point.rl.toFixed(3) : '',
          point.latitude ?? '',
          point.longitude ?? '',
          point.altitude ?? '',
          point.accuracy ?? '',
          point.time ?? ''
        ].map(csvValue).join(','));
      });
    }

    await downloadBlob(
      new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8' }),
      `gps-tracks-${Date.now()}.csv`
    );
    showToast('GPS track CSV exported with absolute coordinates and CRS metadata.', 5000);
  } catch (error) {
    showToast('Export failed: ' + error.message, 6000);
  }
}
function geoPdfPoint(corners,u,v){
  const [tl,bl,br,tr]=corners,leftX=THREE.MathUtils.lerp(bl.x,tl.x,v),leftY=THREE.MathUtils.lerp(bl.y,tl.y,v),rightX=THREE.MathUtils.lerp(br.x,tr.x,v),rightY=THREE.MathUtils.lerp(br.y,tr.y,v);
  return {x:THREE.MathUtils.lerp(leftX,rightX,u),y:THREE.MathUtils.lerp(leftY,rightY,u)};
}
function buildGeoPdfGeometry(layer,renderFrame=state.renderFrame){
  // Flat pages stay light. Top drapes need a denser grid so a small survey
  // surface is not skipped when the GeoPDF covers a much larger map extent.
  const divisions=layer.drapeToTop?160:40,positions=[],uvs=[],indices=[],top=layer.drapeToTop?firstSampler('top'):null;let sampled=0,total=0,minZ=Infinity,maxZ=-Infinity;
  for(let row=0;row<=divisions;row++)for(let column=0;column<=divisions;column++){
    const u=column/divisions,v=row/divisions,point=geoPdfPoint(layer.metadata.corners,u,v),sampleX=point.x-state.projectOrigin.x,sampleY=point.y-state.projectOrigin.y,sample=top?.sample(sampleX,sampleY),z=Number.isFinite(sample)?sample:-1,rendered=projectPointToRender({x:point.x,y:point.y,z},spatialRenderOptions(renderFrame));
    if(!rendered)throw new Error('GeoPDF coordinates could not be transformed into the active render frame.');
    if(Number.isFinite(sample))sampled++;total++;minZ=Math.min(minZ,z);maxZ=Math.max(maxZ,z);positions.push(rendered.x,rendered.y,rendered.z);uvs.push(u,v);
  }
  for(let row=0;row<divisions;row++)for(let column=0;column<divisions;column++){const a=row*(divisions+1)+column,b=a+1,c=a+divisions+1,d=c+1;indices.push(a,c,b,b,c,d);}
  const geometry=new THREE.BufferGeometry();geometry.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));geometry.setAttribute('uv',new THREE.Float32BufferAttribute(uvs,2));geometry.setIndex(indices);geometry.computeVertexNormals();layer.drapeCoverage=top?sampled/total*100:null;layer.bounds={...layer.metadata.bounds,minZ:Number.isFinite(minZ)?minZ:0,maxZ:Number.isFinite(maxZ)?maxZ:0};return geometry;
}
function rebuildGeoPdfGeometry(layer,renderFrame=state.renderFrame){if(!state.projectOrigin)return;const geometry=buildGeoPdfGeometry(layer,renderFrame);layer.mesh.geometry.dispose();layer.mesh.geometry=geometry;layer.mesh.scale.z=state.exaggeration;layer.activeRenderFrame=renderFrame;}
function refreshGeoPdfDrapes(){state.pdfLayers.filter(layer=>layer.drapeToTop).forEach(rebuildGeoPdfGeometry);}

async function addGeoPdf(file, {cache=true}={}) {
  if(state.pdfLayers.some(layer=>layer.name===file.name)){showToast(`${file.name} is already open`);return null;}
  setBusy(true,`Reading ${file.name}`,'Checking GeoPDF georeferencing…',8);
  if(cache)try{await cacheFile(file,state.currentProject?.id);}catch{showToast('Local cache is full; the file can still be opened.');}
  const buffer=await file.arrayBuffer();
  const existingCrs=currentProjectCrs();let metadata=extractGeoPdfMetadata(buffer,existingCrs),projection=null;
if (!existingCrs) {

  projection =
    bestGeoPdfProjection(
      metadata
    );

  if (projection.aligned) {
    metadata =
      projection.metadata;
  }

  if (metadata.projectCrs) {
    await assignProjectCrs(
      metadata.projectCrs,
      'automatic'
    );
  }

} else {

  if (
    metadata.projectCrs &&
    metadata.projectCrs.epsg !==
      existingCrs.epsg
  ) {
    metadata =
      reprojectGeoPdfMetadata(
        metadata,
        existingCrs
      );
  }
}
  if(!existingCrs||(projection?.aligned&&metadata.projectCrs.epsg!==existingCrs.epsg))await assignProjectCrs(metadata.projectCrs,'automatic');
  ui.progress.value=28;ui.processingDetail.textContent='Rendering GeoPDF page…';
      
      // PERBAIKAN: Gunakan Native Engine jika di HP, dan Web Engine jika di Browser PC
      const isNative = globalThis.Capacitor?.isNativePlatform?.() === true;
      const rendered = isNative ? await renderPdfNative(buffer, file) : await renderPdfWeb(buffer);
      
  const canvas=rendered.canvas;
  ui.progress.value=82;ui.processingDetail.textContent=`Positioning raster in ${metadata.projectCrs.name} coordinates…`;
  const geographicPoints = metadata.controlPoints.filter(point =>
    Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
  );
  const worldReference = geographicPoints.length
    ? makeWorldReference(
        geographicPoints.reduce((sum, point) => sum + point.latitude, 0) / geographicPoints.length,
        geographicPoints.reduce((sum, point) => sum + point.longitude, 0) / geographicPoints.length
      )
    : null;
  if (state.currentProject?.projectOriginSource === 'raw-unverified') {
    rebaseProjectOrigin(
      originFromBounds(metadata.bounds),
      'verified-geopdf-rebase'
    );
  }
if (!state.projectOrigin) {
  adoptProjectOrigin(
    originFromBounds(metadata.bounds),
    'geopdf'
  );
}
  adoptWorldReference(worldReference);
  const vectorResolution = resolveUtmLikeLayersFromGeoPdf(metadata);
  const canonicalBounds = state.spatialFrame
    ? canonicalBoundsFromProjectBounds(metadata.bounds, state.spatialFrame)
    : null;
  const setting=savedLayerSetting(file.name),shell={metadata,drapeToTop:setting?.drapeToTop??!!firstSampler('top')},geometry=buildGeoPdfGeometry(shell);
  const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;texture.anisotropy=Math.min(8,renderer.capabilities.getMaxAnisotropy());
  const material=new THREE.MeshBasicMaterial({map:texture,transparent:true,opacity:.9,side:THREE.DoubleSide,depthWrite:false,fog:false});
  const mesh=new THREE.Mesh(geometry,material);mesh.renderOrder=0;mesh.scale.z=state.exaggeration;scene.add(mesh);
  const layer={id:crypto.randomUUID(),kind:'pdf',name:file.name,mesh,texture,visible:setting?.visible??true,opacity:setting?.opacity??.9,metadata,pages:rendered.pages,bounds:shell.bounds,drapeToTop:shell.drapeToTop,drapeCoverage:shell.drapeCoverage,order:setting?.order??nextLayerOrder(),sourceCrs:{type:'geographic',datum:'WGS84',epsg:metadata.sourceEpsg,name:'WGS 84',unit:'degree',status:'resolved'},sourceCrsStatus:'resolved',worldReference:worldReference||setting?.worldReference||null,canonicalBounds:canonicalBounds||setting?.canonicalBounds||null,activeRenderFrame:state.renderFrame,projectCrs:`EPSG:${metadata.targetEpsg}`,crsStatus:'resolved',crsAction:'reproject'};mesh.visible=layer.visible;mesh.material.opacity=layer.opacity;
  state.layerOrderCounter=Math.max(state.layerOrderCounter,layer.order);state.pdfLayers.push(layer);applySpatialRenderFrame({refit:false});loadSavedBoundaryRecords();loadSavedTrackRecords();renderLayerList();setStatus();
  if(vectorResolution.resolved)showToast(`${vectorResolution.resolved} UTM-like DXF location verified from the GeoPDF viewport.`,5200);
  else if(vectorResolution.unresolved)showToast('The GeoPDF CRS was detected, but one or more vector layers use a different or local grid. Use Location → Advanced if alignment is required.',6500);
  await rendered.destroy();
  if(innerWidth<960)openDrawer(false);
  return layer;
}

async function resolveSameGridReference(layer,referenceLayerId){
  const reference=state.layers.find(item=>item.id===referenceLayerId&&item!==layer);
  if(!reference){showToast('The selected reference layer is no longer available.',4500);return null;}
  if(reference.sourceCrsStatus==='resolved'&&reference.sourceCrs){
    showToast('This reference already has a verified location. Use Assign Project CRS or select the known Source CRS instead.',6000);
    return null;
  }
  const gridId=reference.localGridId||layer.localGridId||crypto.randomUUID();
  const gridName=reference.localGridName||reference.result.name;
  for(const item of [reference,layer]){
    if(item.sourceCrsStatus==='resolved')continue;
    item.localGridId=gridId;item.localGridName=gridName;item.crsStatus='local-unresolved';item.crsAction='keep-raw';item.crsEvidence='same-local-grid';item.locationStatus='local-grid-unverified';
  }
  await touchActiveProject();renderLayerList();
  showToast(`${layer.result.name} linked to the same local grid as ${reference.result.name}. Global location is still unverified.`,6000);
  return {reference,linkedRaw:true};
}

async function resolveDxfLayerCrs(layer) {
  if (
    !layer ||
    layer.result?.format !== 'DXF' ||
    !state.currentProject?.id
  ) {
    return;
  }

  let decision = await promptDxfSourceCrs(layer);

  if (!decision) {
    return;
  }

  if (decision.action === 'keep-unknown' || decision.action === 'keep-raw') {
    showToast(
      `${layer.result.name} remains visible at its raw coordinates; location is still unverified.`,
      3500
    );
    return;
  }

  if(decision.action==='same-grid'){
    const sameGrid=await resolveSameGridReference(layer,decision.referenceLayerId);if(!sameGrid||sameGrid.linkedRaw)return;
    decision={action:sameGrid.sourceCrs.epsg===sameGrid.projectCrs.epsg?'assign':'reproject',sourceCrs:sameGrid.sourceCrs,sourceCrsStatus:'resolved',projectCrs:sameGrid.projectCrs,crsEvidence:'same-grid-reference'};
  }

  try {
    const files = await restoreFiles(
      state.currentProject.id
    );

    const sourceFile = files.find(
      file => file.name === layer.result.name
    );

    if (!sourceFile) {
      showToast(
        'The original DXF is not available in the project cache.',
        5500
      );
      return;
    }

    const replacement = await parseFile(
      sourceFile,
      {
        cache: false,
        dxfCrsDecisionOverride: decision,
        replaceLayer: layer
      }
    );

    setBusy(false);

    if (!replacement) {
      return;
    }

    await touchActiveProject();
    renderLayerList();
    fitVectorLayer(replacement);

    showToast(
      decision.action === 'reproject'
        ? `${layer.result.name} reprojected to the Project CRS.`
        : `${layer.result.name} assigned to the Project CRS.`,
      5200
    );
  } catch (error) {
    setBusy(false);
    console.error(
      '[Maply DXF CRS] resolution failed:',
      error
    );
    showToast(
      `DXF CRS resolution failed: ${error.message}`,
      6000
    );
  }
}

function prjTextForCrs(crs) {
  if (crs?.epsg === 4326 || crs?.type === 'geographic') {
    return 'GEOGCS["WGS 84",DATUM["WGS_1984"],AUTHORITY["EPSG","4326"]]';
  }
  return `PROJCS["${crs.name}",GEOGCS["WGS 84"],AUTHORITY["EPSG","${crs.epsg}"]]`;
}

async function resolveShpLayerCrs(layer) {
  if (!layer || layer.result?.format !== 'SHP' || !state.currentProject?.id) return;
  let projectCrs = currentProjectCrs();
  const choice=await openCrsAdvancedDialog({scope:'layer',layer});
  if(!choice||choice.action==='keep-raw')return;

  let sourceCrs = null;
  if(choice.action==='same-grid'){
    const sameGrid=await resolveSameGridReference(layer,choice.referenceLayerId);if(!sameGrid||sameGrid.linkedRaw)return;sourceCrs=sameGrid.sourceCrs;projectCrs=sameGrid.projectCrs;
  }else if(choice.action==='assign-project'){
    if (!projectCrs) return showToast('Set a Project CRS first, or choose the known Source CRS.', 5500);
    sourceCrs = projectCrs;
  }else if(choice.action==='source-utm'){
    sourceCrs=utmCrs(choice.zone,choice.hemisphere);
    if (!projectCrs) {
      await assignProjectCrs(sourceCrs, 'manual', { status: 'confirmed' });
      projectCrs = currentProjectCrs();
    }
  }else if(choice.action==='source-geographic'){
    sourceCrs = { type: 'geographic', datum: 'WGS84', epsg: 4326, name: 'WGS 84', unit: 'degree', status: 'resolved' };
    if(!projectCrs){const bounds=layer.result.sourceBounds||layer.result.bounds,centerLon=(bounds.minX+bounds.maxX)/2,centerLat=(bounds.minY+bounds.maxY)/2,natural=latLonToUtm(centerLat,centerLon);await assignProjectCrs(utmCrs(natural.zone,natural.hemisphere),'manual',{status:'confirmed'});projectCrs=currentProjectCrs();}
  }

  if(!sourceCrs)return;

  if (projectCrs && !isProjectCrsConfirmed()) {
    await assignProjectCrs(projectCrs, 'manual', { status: 'confirmed' });
  }

  try {
    const files = await restoreFiles(state.currentProject.id);
    const sourceFile = files.find(file => file.name === layer.result.name);
    if (!sourceFile) return showToast('The original SHP is not available in the project cache.', 5500);
    const replacement = await parseFile(sourceFile, {
      cache: false,
      shpPrjText: prjTextForCrs(sourceCrs),
      replaceLayer: layer
    });
    setBusy(false);
    if (!replacement) return;
    await touchActiveProject();
    renderLayerList();
    fitVectorLayer(replacement);
    showToast(`${layer.result.name} location/CRS resolved inside Maply.`, 5000);
  } catch (error) {
    setBusy(false);
    console.error('[Maply SHP CRS] resolution failed:', error);
    showToast(`SHP CRS resolution failed: ${error.message}`, 6000);
  }
}

async function parseFile(
  file,
  {
    cache = true,
    dxfCrsDecisionOverride = null,
    replaceLayer = null,
    shpPrjText = null,
    shpPrjFileName = null,
    dxfPrjText = null,
    dxfPrjFileName = null
  } = {}
) {
  const lower=file.name.toLowerCase();
  if(lower.endsWith('.pdf'))return addGeoPdf(file,{cache});
  const vectorExtension=['dxf','kml','kmz','shp'].find(extension=>lower.endsWith(`.${extension}`));
  if (!vectorExtension) { showToast(`${file.name}: unsupported format`); return null; }
  if (state.layers.some(layer => layer !== replaceLayer && layer.result.name === file.name)) { showToast(`${file.name} is already open`); return; }
  let dxfCrsDecision = dxfCrsDecisionOverride;
  if (vectorExtension === 'dxf') {
    dxfCrsDecision = dxfCrsDecision ||
      restoredDxfSourceCrsDecision(file.name);

    if (!dxfCrsDecision) {
      dxfCrsDecision = {
        action: 'auto',
        projectCrs: currentProjectCrs(),
        referenceBounds: resolvedSpatialReferenceBounds()
      };
    }
  }
  setBusy(true, `Reading ${file.name}`, 'Preparing file…', 1);
  if (cache) try { await cacheFile(file,state.currentProject?.id); } catch { showToast('Local cache is full; the file can still be opened.'); }
  const buffer = await file.arrayBuffer();
  if(vectorExtension!=='dxf'){
    ui.progress.value=38;ui.processingDetail.textContent=`Parsing ${vectorExtension.toUpperCase()} geometry…`;
    const parser=vectorExtension==='kml'?parseKml:vectorExtension==='kmz'?parseKmz:parseShp;
    const restoredShpSetting = vectorExtension === 'shp' ? savedLayerSetting(file.name) : null;
    const effectiveShpPrjText = shpPrjText || (
      restoredShpSetting?.sourceCrsStatus === 'resolved' && restoredShpSetting.sourceCrs
        ? prjTextForCrs(restoredShpSetting.sourceCrs)
        : null
    );
    const result=vectorExtension==='shp'
      ? parser(buffer,file.name,currentProjectCrs(),effectiveShpPrjText)
      : parser(buffer,file.name,currentProjectCrs());
    result.format=vectorExtension.toUpperCase();
    if (vectorExtension === 'shp' && shpPrjFileName) {
      result.companionFileNames = [shpPrjFileName];
    }
    if(!currentProjectCrs()&&result.projectCrs)await assignProjectCrs(result.projectCrs,'automatic');
    ui.progress.value=88;
    const layer = addLayer(result);
    if (replaceLayer) {
      disposeLayer(replaceLayer);
      state.layers = state.layers.filter(item => item !== replaceLayer);
      applySpatialRenderFrame();
      setStatus();
      renderLayerList();
    }
    return layer;
  }
  return await new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./dxf-worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = async event => {
      const message = event.data;
      if (message.type === 'progress') { ui.progress.value = message.percent; ui.processingDetail.textContent = message.detail; }
      else if (message.type === 'result') {
        message.result.format = 'DXF';
        if (dxfPrjFileName) message.result.companionFileNames = [dxfPrjFileName];
        if (!currentProjectCrs() && message.result.projectCrs) {
          await assignProjectCrs(message.result.projectCrs, 'automatic', { status: 'suggested' });
        }
        const layer = addLayer(message.result);
        if (replaceLayer) {
          disposeLayer(replaceLayer);
          state.layers = state.layers.filter(
            item => item !== replaceLayer
          );
          applySpatialRenderFrame();
          setStatus();
          renderLayerList();
        }
        worker.terminate();
        resolve(layer);
      }
      else if (message.type === 'error') { worker.terminate(); reject(new Error(message.message)); }
    };
    worker.onerror = error => { worker.terminate(); reject(error); };
    worker.postMessage(
      {
        type: 'parse',
        name: file.name,
        buffer,
        crsDecision: dxfCrsDecision,
        sidecarPrjText: dxfPrjText || ''
      },
      [buffer]
    );
  });
}
function splitProjectFileName(name){
  const match=String(name||'').match(/^(.*?)(\.[^.]+)?$/);return {base:match?.[1]||'map-data',extension:match?.[2]||''};
}
function cloneProjectFileWithName(file,name){
  if(file.name===name)return file;
  const clone=new File([file],name,{type:file.type,lastModified:file.lastModified});
  const nativeUrl=nativeDocumentUrls.get(file);if(nativeUrl)nativeDocumentUrls.set(clone,nativeUrl);return clone;
}
function uniqueProjectImportFiles(files){
  const used=new Set();
  for(const layer of [...state.layers,...state.pdfLayers]){
    const name=layer.result?.name||layer.name;if(name)used.add(name.toLowerCase());
    for(const companion of layer.result?.companionFileNames||[])used.add(companion.toLowerCase());
  }
  const output=new Map(),renamedBases=new Map();
  for(const file of files.filter(item=>!item.name.toLowerCase().endsWith('.prj'))){
    const {base,extension}=splitProjectFileName(file.name);let candidate=file.name,suffix=2;
    while(used.has(candidate.toLowerCase()))candidate=`${base} (${suffix++})${extension}`;
    used.add(candidate.toLowerCase());output.set(file,cloneProjectFileWithName(file,candidate));
    const key=base.toLowerCase();if(!renamedBases.has(key))renamedBases.set(key,[]);renamedBases.get(key).push(splitProjectFileName(candidate).base);
  }
  const baseUseIndex=new Map();
  for(const file of files.filter(item=>item.name.toLowerCase().endsWith('.prj'))){
    const {base}=splitProjectFileName(file.name),key=base.toLowerCase(),choices=renamedBases.get(key)||[base],index=baseUseIndex.get(key)||0,targetBase=choices[Math.min(index,choices.length-1)];let candidate=`${targetBase}.prj`,suffix=2;baseUseIndex.set(key,index+1);
    while(used.has(candidate.toLowerCase()))candidate=`${targetBase} (${suffix++}).prj`;
    used.add(candidate.toLowerCase());output.set(file,cloneProjectFileWithName(file,candidate));
  }
  return files.map(file=>output.get(file)||file);
}
async function openFiles(files, options={}) {
  let list = [...files]; if (!list.length) return;
  if(options.cache!==false)list=uniqueProjectImportFiles(list);
  const hadExistingMapData=state.layers.length+state.pdfLayers.length>0;
  try {
    if(!state.currentProject)await createCloudProject(list);
    const prjFiles = new Map(
      list
        .filter(file => file.name.toLowerCase().endsWith('.prj'))
        .map(file => [file.name.replace(/\.prj$/i, '').toLowerCase(), file])
    );
    const dataFiles = list.filter(file => !file.name.toLowerCase().endsWith('.prj'));

    if (options.cache !== false) {
      for (const prjFile of prjFiles.values()) {
        try {
          await cacheFile(prjFile, state.currentProject?.id);
        } catch {
          showToast('Local cache is full; the CRS companion file can still be used for this session.');
        }
      }
    }

    let lastLayer=null;
    const openedLayers = [];
    for (const file of dataFiles) {
      const lowerName = file.name.toLowerCase();
      const pairedPrj = /\.(shp|dxf)$/i.test(lowerName)
        ? prjFiles.get(file.name.replace(/\.(shp|dxf)$/i, '').toLowerCase())
        : null;
      let fileOptions = options;
      if (pairedPrj) {
        const prjText = await pairedPrj.text();
        fileOptions = lowerName.endsWith('.shp')
          ? { ...options, shpPrjText: prjText, shpPrjFileName: pairedPrj.name }
          : { ...options, dxfPrjText: prjText, dxfPrjFileName: pairedPrj.name };
      }
      const openedLayer = await parseFile(file, fileOptions);
      if (openedLayer) {
        openedLayers.push(openedLayer);
        lastLayer = openedLayer;
      }
    }
    recolorComparison(); setBusy(false);
    
    // The first import establishes the initial view. Adding data to an existing
    // project must not move the camera: users can explicitly Zoom to File or Fit All.
    if (!hadExistingMapData && !state.gpsWatch) {
      if (openedLayers.length > 1) {
        fitView();
      } else if (
        lastLayer?.kind === 'vector' &&
        lastLayer.sourceCrsStatus === 'unknown'
      ) {
        fitVectorLayer(lastLayer);
      } else if (lastLayer?.bounds || lastLayer?.result?.bounds) {
        fitAbsoluteBounds(lastLayer.bounds || lastLayer.result.bounds);
      } else {
        fitView();
      }
    }
    
    if (openedLayers.length && options.touch!==false) await touchActiveProject();
    if (openedLayers.length) {
      const pdfCount=openedLayers.filter(layer=>layer.kind==='pdf').length;
      const vectorCount=openedLayers.length-pdfCount;
      const parts=[];
      if(vectorCount)parts.push(`${vectorCount} vector file${vectorCount===1?'':'s'}`);
      if(pdfCount)parts.push(`${pdfCount} PDF${pdfCount===1?'':'s'}`);
      showToast(`${parts.join(' · ')} opened${hadExistingMapData?' · view kept':''}`);
    }
  } catch (error) { setBusy(false); showToast(`Failed: ${error.message}`, 6000); console.error(error); }
}

const recentNativeDocuments=new Map();
let nativeOpenChoiceResolver=null;
function fileNameFromUrl(url){
  try{return decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).at(-1)||'document');}
  catch{return 'document';}
}
function base64Bytes(value){
  const encoded=value.includes(',')?value.slice(value.indexOf(',')+1):value;
  const binary=atob(encoded),bytes=new Uint8Array(binary.length);
  for(let offset=0;offset<binary.length;offset+=32768){
    const end=Math.min(binary.length,offset+32768);
    for(let index=offset;index<end;index++)bytes[index]=binary.charCodeAt(index);
  }
  return bytes;
}
function finishNativeOpenChoice(choice){
  ui.nativeOpenTargetModal.classList.add('hidden');
  const resolve=nativeOpenChoiceResolver;
  nativeOpenChoiceResolver=null;
  resolve?.(choice);
}
async function chooseExternalFilesTarget(files){
  if(nativeOpenChoiceResolver)finishNativeOpenChoice(null);
  const list=[...files];
  const projects=await listProjects();
  const active=state.currentProject;
  ui.nativeOpenFileName.textContent=list.length===1
    ? list[0].name
    : `${list[0]?.name||'Map data'} + ${list.length-1} more file${list.length===2?'':'s'}`;
  ui.nativeAddCurrentProject.classList.toggle('hidden',!active);
  ui.nativeAddCurrentProject.textContent=active?`Add to current project · ${active.name}`:'';
  ui.nativeCreateProject.classList.toggle('primary',!active);
  ui.nativeCreateProject.classList.toggle('ghost',!!active);
  ui.nativeExistingProject.replaceChildren();
  for(const project of projects){
    const option=document.createElement('option');
    option.value=project.id;
    option.textContent=`${project.name} · ${project.fileCount||0} file${project.fileCount===1?'':'s'}`;
    ui.nativeExistingProject.append(option);
  }
  ui.nativeExistingProjectSection.classList.toggle('hidden',projects.length===0);
  ui.nativeUseExistingProject.disabled=projects.length===0;
  ui.nativeOpenTargetModal.classList.remove('hidden');
  return new Promise(resolve=>{nativeOpenChoiceResolver=resolve;});
}
async function addExternalFilesToTarget(files,choice){
  const list=[...files];
  if(!choice)return;
  if(choice.type==='new'){
    await createCloudProject(list);
  }else if(choice.type==='existing'){
    const project=await getProject(choice.projectId);
    if(!project)throw new Error('The selected project is no longer available.');
    if(state.currentProject?.id!==project.id)await activateProject(project);
  }else if(choice.type==='current'&&!state.currentProject){
    await createCloudProject(list);
  }
  hideProjectHub();
  openDrawer(true);
  await openFiles(list);
}
async function openExternalFilesWithProjectChoice(files){
  const list=[...files];
  if(!list.length)return;
  const choice=await chooseExternalFilesTarget(list);
  await addExternalFilesToTarget(list,choice);
}
async function openNativeDocument(url){
  if(!url?.startsWith('file:')){
    if(url?.startsWith('content:'))showToast('This Android document URI needs the latest Maply native bridge.',6500);
    return;
  }
  const name=fileNameFromUrl(url),lower=name.toLowerCase();
  const extensions=['.dxf','.pdf','.kml','.kmz','.shp'];
  if(!extensions.some(extension=>lower.endsWith(extension)))return showToast(`${name}: unsupported format`,5000);
  const now=Date.now(),last=recentNativeDocuments.get(url)||0;
  if(now-last<2000)return;
  recentNativeDocuments.set(url,now);
  setBusy(true,`Opening ${name}`,'Reading file from cloud or device storage…',3);
  try{
    const result=await Filesystem.readFile({path:url});
    const payload=result.data instanceof Blob?result.data:base64Bytes(result.data);
    const type=lower.endsWith('.pdf')?'application/pdf':lower.endsWith('.kml')?'application/vnd.google-earth.kml+xml':lower.endsWith('.kmz')?'application/vnd.google-earth.kmz':lower.endsWith('.shp')?'application/x-esri-shape':'image/vnd.dxf',file=new File([payload],name,{type,lastModified:now});
    nativeDocumentUrls.set(file,url);
    setBusy(false);
    await openExternalFilesWithProjectChoice([file]);
  }catch(error){
    setBusy(false);showToast(`Could not open ${name}: ${error.message}`,6500);console.error(error);
  }
}
async function enableNativeDocumentOpen(){
  await App.addListener('appUrlOpen',event=>openNativeDocument(event.url));
  const launch=await App.getLaunchUrl();
  if(launch?.url)await openNativeDocument(launch.url);
}
ui.nativeAddCurrentProject.addEventListener('click',()=>finishNativeOpenChoice({type:'current'}));
ui.nativeCreateProject.addEventListener('click',()=>finishNativeOpenChoice({type:'new'}));
ui.nativeUseExistingProject.addEventListener('click',()=>{
  const projectId=ui.nativeExistingProject.value;
  if(projectId)finishNativeOpenChoice({type:'existing',projectId});
});
ui.nativeCancelOpen.addEventListener('click',()=>finishNativeOpenChoice(null));

function firstSampler(role) { return allProjectLayers().find(layer => layer.visible && layer.role === role && layer.sourceCrsStatus !== 'unknown' && layer.sampler)?.sampler || null; }
function topElevation(x, y) {
  const projected = state.renderFrame === RENDER_FRAME_ENU
    ? renderCoordinateToProject(x, y, 0)
    : null;
  const sampleX = projected
    ? projected.x - state.projectOrigin.x
    : x;
  const sampleY = projected
    ? projected.y - state.projectOrigin.y
    : y;
  const values = allProjectLayers().filter(layer => layer.visible && layer.sourceCrsStatus !== 'unknown' && layer.sampler).map(layer => layer.sampler.sample(sampleX, sampleY)).filter(Number.isFinite);
  return values.length ? Math.max(...values) : 0;
}
function renderHorizontalToProject(x,y){
  return renderCoordinateToProject(Number(x),Number(y),0);
}
function renderElevationZ(x,y,elevation,lift=0){
  const projected=renderHorizontalToProject(x,y);if(!projected)return(Number(elevation)||0)*state.exaggeration+lift;
  const rendered=projectCoordinateToRender(projected.x,projected.y,Number(elevation)||0);return((rendered?.z??Number(elevation))||0)*state.exaggeration+lift;
}
function renderPointsToSamplerSpace(points){
  if(!state.projectOrigin||!Array.isArray(points))return[];
  return points.map(point=>{const projected=renderHorizontalToProject(point.x,point.y);return projected?new THREE.Vector2(projected.x-state.projectOrigin.x,projected.y-state.projectOrigin.y):null;}).filter(Boolean);
}
function startSectionAt(point) {
  state.section.a = new THREE.Vector2(point.x, point.y); state.section.b = null; state.section.last = [];
  const z = renderElevationZ(point.x,point.y,topElevation(point.x,point.y),5);
  markerA.position.set(point.x, point.y, z); markerA.visible = true; labelA.position.set(point.x, point.y, z + 16); labelA.visible = true;
  sectionHalo.visible = sectionLine.visible = markerB.visible = markerM.visible = labelB.visible = false;
  ui.sectionInfo.textContent = 'Point A selected · double-tap to set point B';
  ui.sectionPanel.classList.add('collapsed'); drawSection(); showToast('Point A created. Now select point B.');
}
function resetSectionDrawing() {
  state.section.a = state.section.b = null; state.section.last = [];
  [sectionHalo, sectionLine, markerA, markerB, markerM, labelA, labelB].forEach(item => item.visible = false);
  state.section.metrics=null;ui.sectionInfo.textContent = 'Double-tap to set point A, then point B';updateSectionMetricsUi();drawSection();
}
function integrateSectionMetrics(samples,width=1){
  let cutArea=0,fillArea=0,validLength=0;
  for(let index=1;index<samples.length;index++){
    const a=samples[index-1],b=samples[index],run=b.distance-a.distance;if(!Number.isFinite(a.delta)||!Number.isFinite(b.delta)||run<=0)continue;validLength+=run;
    if(a.delta*b.delta<0){const fraction=Math.abs(a.delta)/(Math.abs(a.delta)+Math.abs(b.delta)),first=run*fraction,second=run-first;if(a.delta>0){cutArea+=first*a.delta/2;fillArea+=second*Math.abs(b.delta)/2;}else{fillArea+=first*Math.abs(a.delta)/2;cutArea+=second*b.delta/2;}}
    else{const area=run*(Math.abs(a.delta)+Math.abs(b.delta))/2;if((a.delta+b.delta)/2>=0)cutArea+=area;else fillArea+=area;}
  }
  return {cutArea,fillArea,cutVolume:cutArea*width,fillVolume:fillArea*width,width,validLength};
}
function updateSectionMetricsUi(){
  const metrics=state.section.metrics;if(!ui.sectionMetrics)return;if(!metrics||metrics.validLength<=0){ui.sectionMetrics.textContent='No overlapping Top and Bottom surface along this section.';return;}
  ui.sectionMetrics.textContent=`Cut ${metrics.cutArea.toFixed(1)} m² / ${metrics.cutVolume.toFixed(1)} m³ · Fill ${metrics.fillArea.toFixed(1)} m² / ${metrics.fillVolume.toFixed(1)} m³`;
}
function updateSection() {
  const { a, b } = state.section; if (!a || !b) return;
  const za = renderElevationZ(a.x,a.y,topElevation(a.x,a.y),4), zb = renderElevationZ(b.x,b.y,topElevation(b.x,b.y),4);
  const sectionPositions=[a.x,a.y,za,b.x,b.y,zb];
  sectionHalo.geometry.setPositions(sectionPositions); sectionHalo.computeLineDistances();
  sectionLine.geometry.setPositions(sectionPositions); sectionLine.computeLineDistances();
  
  markerA.position.set(a.x,a.y,za); markerB.position.set(b.x,b.y,zb);
  labelA.position.set(a.x,a.y,za+16); labelB.position.set(b.x,b.y,zb+16);
  const midX=(a.x+b.x)/2,midY=(a.y+b.y)/2;markerM.position.set(midX,midY,renderElevationZ(midX,midY,topElevation(midX,midY),4));
  [sectionHalo,sectionLine,markerA,markerB,markerM,labelA,labelB].forEach(item => item.visible = true);
  
  const bottom = firstSampler('bottom'), top = firstSampler('top');
  const projectA=renderHorizontalToProject(a.x,a.y),projectB=renderHorizontalToProject(b.x,b.y);if(!projectA||!projectB)return;
  const length = Math.hypot(projectB.x-projectA.x,projectB.y-projectA.y);
  const count = Math.min(5000, Math.max(181, Math.ceil(length / 0.5))); 
  const samples = [];
  
  for (let i=0;i<count;i++) {
    const t=i/(count-1),easting=THREE.MathUtils.lerp(projectA.x,projectB.x,t),northing=THREE.MathUtils.lerp(projectA.y,projectB.y,t),sampleX=easting-(state.projectOrigin?.x||0),sampleY=northing-(state.projectOrigin?.y||0);
    const bz=bottom?.sample(sampleX,sampleY) ?? null, tz=top?.sample(sampleX,sampleY) ?? null;
    samples.push({ distance:length*t, easting, northing, bottom:bz, top:tz, delta:bz!==null&&tz!==null?tz-bz:null });
  }
  
  const dx=projectB.x-projectA.x, dy=projectB.y-projectA.y, azimuth=(Math.atan2(dx,dy)*180/Math.PI+360)%360;
  const deltas=samples.map(sample=>sample.delta).filter(Number.isFinite);
  const deltaText=deltas.length?` · Δ ${Math.min(...deltas).toFixed(2)}…${Math.max(...deltas).toFixed(2)} m`:'';
  
  state.section.last=samples;
  state.section.metrics=integrateSectionMetrics(samples,Math.max(.01,Number(ui.sectionWidth?.value)||1));
  updateSectionMetricsUi();
  ui.sectionInfo.textContent=`${length.toFixed(1)} m · Az ${azimuth.toFixed(1)}°${deltaText}`; 
  drawSection();
}
function drawSection() {
  const canvas=ui.sectionCanvas, rect=canvas.getBoundingClientRect(); if (!rect.width || !rect.height) return;
  const dpr=Math.min(devicePixelRatio,2); canvas.width=rect.width*dpr; canvas.height=rect.height*dpr;
  const ctx=canvas.getContext('2d'); ctx.scale(dpr,dpr); const w=rect.width,h=rect.height; ctx.clearRect(0,0,w,h);
  const data=state.section.last, values=data.flatMap(p=>[p.bottom,p.top]).filter(Number.isFinite); if (!data.length||!values.length) {ctx.fillStyle='#8ca2b2';ctx.font='11px system-ui';ctx.fillText('Open Bottom and Top DXF layers to display the chart.',16,26);return;}
  let min=Math.min(...values),max=Math.max(...values); const pad=Math.max(1,(max-min)*.12);min-=pad;max+=pad;
  const m={l:46,r:12,t:10,b:25}, x=i=>m.l+i/(data.length-1)*(w-m.l-m.r), y=z=>m.t+(max-z)/(max-min)*(h-m.t-m.b);
  ctx.strokeStyle='#2a3d4e';ctx.lineWidth=1;ctx.fillStyle='#7890a0';ctx.font='9px ui-monospace';
  for(let i=0;i<=4;i++){const yy=m.t+i*(h-m.t-m.b)/4;ctx.beginPath();ctx.moveTo(m.l,yy);ctx.lineTo(w-m.r,yy);ctx.stroke();ctx.fillText((max-i*(max-min)/4).toFixed(1),3,yy+3);}
  for(let i=1;i<data.length;i++){const p0=data[i-1],p1=data[i];if(p0.bottom===null||p0.top===null||p1.bottom===null||p1.top===null)continue;const c=deltaColor((p0.delta+p1.delta)/2);ctx.fillStyle=`rgba(${c[0]*255},${c[1]*255},${c[2]*255},.23)`;ctx.beginPath();ctx.moveTo(x(i-1),y(p0.bottom));ctx.lineTo(x(i),y(p1.bottom));ctx.lineTo(x(i),y(p1.top));ctx.lineTo(x(i-1),y(p0.top));ctx.closePath();ctx.fill();}
  const line=(key,color)=>{ctx.strokeStyle=color;ctx.lineWidth=1.8;ctx.beginPath();let started=false;data.forEach((p,i)=>{if(p[key]===null){started=false;return;}if(!started){ctx.moveTo(x(i),y(p[key]));started=true;}else ctx.lineTo(x(i),y(p[key]));});ctx.stroke();};
  line('bottom','#6fe7ff');line('top','#ffad5c');ctx.fillStyle='#7890a0';ctx.fillText('A  0 m',m.l,h-7);ctx.fillText(`B  ${data.at(-1).distance.toFixed(0)} m`,w-82,h-7);

  // --- KODE TAMBAHAN UNTUK ANOTASI INTERAKTIF (REVISI) ---
  if (state.section.hoverX !== undefined && state.section.hoverX !== null && data.length > 0) {
    const hoverX = state.section.hoverX; 
    if (hoverX >= m.l && hoverX <= w - m.r) {
      const index = Math.max(0, Math.min(data.length - 1, Math.round((hoverX - m.l) / (w - m.l - m.r) * (data.length - 1))));
      const p = data[index];
      const cx = x(index);

      if (p.top !== null && p.bottom !== null) {
        const yTop = y(p.top);
        const yBot = y(p.bottom);

        ctx.strokeStyle = '#ff3bea';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(cx, yTop);
        ctx.lineTo(cx, yBot);
        ctx.stroke();
        ctx.setLineDash([]);

// 2. Siapkan Teks Informasi (Ditambahkan Overcut / Overfill)
        const topRl = p.top.toFixed(2);
        const botRl = p.bottom.toFixed(2);
        let diffText = '-';
        if (p.delta !== null) {
          const val = Math.abs(p.delta).toFixed(2);
          if (p.delta > 0) {
            // Top di atas Bottom (Harus digali jika di Pit, atau kelebihan dumping)
            diffText = `Cut / Overfill: ${val} m`;
          } else if (p.delta < 0) {
            // Top di bawah Bottom (Kurang timbun, atau tergali terlalu dalam)
            diffText = `Fill / Overcut: ${val} m`;
          } else {
            diffText = `On Grade`;
          }
        }

        // 3. Posisi Kotak Tooltip Dinamis (Diperlebar karena teksnya lebih panjang)
        const boxW = 125, boxH = 50; // Lebar kotak ditambah dari 100 menjadi 125
        const boxX = cx + 10 + boxW > w - m.r ? cx - 15 - boxW : cx + 10;
        const boxY = Math.min(yTop, yBot) - boxH - 10 > m.t ? Math.min(yTop, yBot) - boxH - 10 : Math.max(yTop, yBot) + 10;

        ctx.fillStyle = 'rgba(11, 17, 24, 0.9)'; 
        ctx.beginPath(); ctx.roundRect(boxX, boxY, boxW, boxH, 6); ctx.fill();
        ctx.strokeStyle = '#486276'; ctx.stroke();

        ctx.font = '11px system-ui';
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffad5c'; ctx.fillText(`Top: ${topRl}`, boxX + 10, boxY + 16);
        ctx.fillStyle = '#6fe7ff'; ctx.fillText(`Bot: ${botRl}`, boxX + 10, boxY + 30);
        
        // Warna peringatan
        ctx.fillStyle = p.delta > 0 ? '#ff5c52' : (p.delta < 0 ? '#2c8dff' : '#35d782');
        ctx.fillText(diffText, boxX + 10, boxY + 44);
      }
    }
  }
}
// ==========================================
// FITUR EXPORT SECTION REPORT (PETA + GRAFIK)
// ==========================================
// Fungsi Helper untuk mendownload file
// ==========================================
// SMART DOWNLOADER (SUPPORTS MOBILE NATIVE & PC)
// ==========================================
async function downloadBlob(blob, filename) {
  // 1. JIKA APLIKASI BERJALAN DI HP NATIVE (Capacitor APK / iOS)
  if (globalThis.Capacitor?.isNativePlatform?.()) {
    try {
      // Ubah data file menjadi format Base64 agar bisa dikirim oleh HP
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      
      // Tulis file sementara di memori internal aplikasi
      const writeRes = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache
      });
      
      // Munculkan Pop-up Share/Save bawaan HP! 
      // (Bisa langsung kirim WA atau Save to Device)
      await Share.share({
        title: filename,
        url: writeRes.uri,
        dialogTitle: 'Simpan atau Bagikan File'
      });
      return; 
    } catch (e) {
      console.error('Native download failed:', e);
      showToast('Gagal membagikan file: ' + e.message, 4000);
      return;
    }
  }

  // 2. JIKA DI PC/LAPTOP MODERN (Akan memunculkan pop-up "Save As" Folder)
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({ suggestedName: filename });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return; 
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
      return; // Batal disave oleh user
    }
  }

  // 3. FALLBACK KLASIK (Browser HP biasa / PC Lama)
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
async function exportSectionReport() {
  showToast('Menyiapkan Laporan Section...', 2000);
  
  try {
    // 1. Tentukan kamera mana yang sedang aktif (2D atau 3D)
    const activeCamera = state.mode === '2d' ? ortho : perspective;
    
    // 2. Render layar secara paksa TEPAT SEBELUM difoto (Solusi Layar Hitam WebGL)
    renderer.render(scene, activeCamera);
    const mapCanvas = renderer.domElement;
    
    // 3. Ambil gambar dari Grafik Section
    const chartCanvas = ui.sectionCanvas;
    
    // 4. Buat Canvas Baru untuk Kertas Laporan (Proporsi A4)
    const reportCanvas = document.createElement('canvas');
    const ctx = reportCanvas.getContext('2d');
    
    reportCanvas.width = 1200;
    const padding = 40;
    
    // Menyesuaikan rasio gambar peta agar tidak gepeng
    const mapRatio = mapCanvas.height / mapCanvas.width;
    const mapHeight = (reportCanvas.width - (padding * 2)) * mapRatio;
    const chartHeight = 400;
    
    reportCanvas.height = mapHeight + chartHeight + (padding * 3) + 120;
    
    // Warna Background Kertas (Gelap)
    ctx.fillStyle = '#0b1118';
    ctx.fillRect(0, 0, reportCanvas.width, reportCanvas.height);
    
    // 5. Tulis Judul Laporan
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px system-ui';
    ctx.fillText('CROSS SECTION REPORT', padding, padding + 30);
    
    ctx.fillStyle = '#7890a0';
    ctx.font = '24px system-ui';
    const infoText = ui.sectionInfo.textContent || 'Cross Section Data';
    ctx.fillText(infoText, padding, padding + 70);
    
    // 6. Tempel Gambar Peta (Sekarang pasti muncul!)
    ctx.drawImage(mapCanvas, 0, 0, mapCanvas.width, mapCanvas.height, padding, padding + 100, reportCanvas.width - (padding*2), mapHeight);
    
    // 7. Tempel Gambar Grafik Section
    ctx.drawImage(chartCanvas, 0, 0, chartCanvas.width, chartCanvas.height, padding, padding + 120 + mapHeight, reportCanvas.width - (padding*2), chartHeight);
    
    // 8. Simpan sebagai PNG Kualitas Tinggi
    reportCanvas.toBlob(async (blob) => {
      const fileName = `Section_Report_${new Date().getTime()}.png`; 
      await downloadBlob(blob, fileName);
      showToast('Laporan berhasil disimpan!', 3000);
    }, 'image/png', 1.0);
    
  } catch (err) {
    showToast('Gagal membuat laporan.', 3000);
    console.error(err);
  }
}
/**
 * Fungsi utama untuk menangani impor file (DXF, SHP, KML/KMZ)
 * @param {File} file - File yang diunggah oleh pengguna
 * @param {string} canvasCurrentCRS - Sistem proyeksi yang sedang aktif di kanvas (contoh: "EPSG:32750" untuk UTM 50S)
 */
function handleFileImport(file, canvasCurrentCRS = "EPSG:32750") {
    const fileName = file.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();

    console.log(`Memproses file: ${fileName} (${fileExtension})`);

    // 1. Deteksi karakteristik file berdasarkan ekstensinya
    if (fileExtension === 'dxf') {
        // DXF adalah raw XYZ, tidak punya metadata CRS/Proyeksi bawaan
        detectAndValidateDXF(file, canvasCurrentCRS);
        
    } else if (fileExtension === 'shp') {
        // SHP biasanya datang berpasangan dengan file .prj (metadata proyeksi)
        // Jika file .prj tidak disertakan, sistem akan mendeteksi ketidakpastian
        checkForPrjMetadata(file, canvasCurrentCRS);
        
    } else if (fileExtension === 'kml' || fileExtension === 'kmz') {
        // KML/KMZ selalu menggunakan Geografis (WGS84 / EPSG:4326)
        // Aplikasi akan otomatis melakukan reprojeksi on-the-fly ke sistem kanvas (UTM)
        processGeographicFile(file, "EPSG:4326", canvasCurrentCRS);
        
    } else {
        alert("⚠️ Format file tidak didukung! Gunakan DXF, SHP, KML, atau KMZ.");
    }
}

/**
 * Penanganan khusus untuk file DXF (Raw Coordinate)
 */
function detectAndValidateDXF(file, canvasCRS) {
    // Membaca isi file DXF secara mentah untuk memeriksa rentang angka (Bounding Box)
    const reader = new FileReader();
    reader.onload = function(e) {
        const fileContent = e.target.result;
        const isLikelyUTM = checkCoordinateMagnitude(fileContent);

        if (!isLikelyUTM) {
            showProjectionWarning(
                file.name,
                "Koordinat Lokal / Tidak Valid",
                "File DXF ini terdeteksi menggunakan koordinat lokal atau rentang angka yang tidak cocok dengan standar UTM kanvas."
            );
        } else {
            // Lolos validasi awal UTM, render secara virtual di kanvas
            console.log("File DXF valid berbasis UTM. Merender ke kanvas...");
            renderToCanvasWithCRS(file, canvasCRS);
        }
    };
    reader.readAsText(file);
}

function showProjectionWarning(fileName, detectedType, message) {

    const warningHTML = `
        <div id="projection-warning-modal" style="position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.6); z-index:9999; display:flex; align-items:center; justify-content:center;">
            <div style="background:#fff; padding:24px; border-radius:8px; max-width:400px; width:90%; box-shadow:0 4px 12px rgba(0,0,0,0.3); font-family:sans-serif;">
                <h3 style="margin-top:0; color:#d9534f;">⚠️ Peringatan Sistem Proyeksi</h3>
                <p><strong>File:</strong> ${fileName}</p>
                <p><strong>Status:</strong> ${detectedType}</p>
                <p style="font-size:14px; color:#555;">${message}</p>
                <hr style="border:0; border-top:1px solid #eee; margin:15px 0;">
                <div style="display:flex; flex-direction:column; gap:8px;">
                    <button onclick="resolveProjection('canvas')" style="padding:10px; background:#0275d8; color:#fff; border:none; border-radius:4px; cursor:pointer;">Gunakan Standar Kanvas (${canvasCurrentCRSName()})</button>
                    <button onclick="resolveProjection('manual')" style="padding:10px; background:#f0ad4e; color:#fff; border:none; border-radius:4px; cursor:pointer;">Pilih Zona Manual</button>
                    <button onclick="resolveProjection('ignore')" style="padding:10px; background:#6c757d; color:#fff; border:none; border-radius:4px; cursor:pointer;">Abaikan & Render Apa Adanya</button>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', warningHTML);
}

/**
 * Fungsi dummy untuk pengecekan besar angka koordinat dalam DXF
 */
function checkCoordinateMagnitude(content) {
    // Logika mendasar: mencari angka di atas 100.000 (khas UTM)
    // Jika tidak ditemukan, dianggap koordinat lokal/kecil.
    return true; // Dibuat true sebagai default aman untuk Total Station Anda
}

function canvasCurrentCRSName() {
    return "UTM Zona 50S";
}
// Hubungkan ke tombol PDF di HTML
// Ganti '#exportSectionPdfBtn' dengan ID tombol PDF Anda di HTML
document.querySelector('#exportSectionPdfBtn')?.addEventListener('click', exportSectionReport);
function snapToDxf(point) {
  if (!state.layers.length) return { point, snapped: false };
  const height=Math.max(1,ui.canvas.clientHeight),distance=state.mode==='2d'
    ? (ortho.top-ortho.bottom)/Math.max(.001,ortho.zoom)
    : 2*perspective.position.distanceTo(new THREE.Vector3(point.x,point.y,topElevation(point.x,point.y)))*Math.tan(THREE.MathUtils.degToRad(perspective.fov/2));
  const threshold=Math.max(.15,Math.min(30,distance/height*18)),threshold2=threshold*threshold;
  let vertex2=threshold2,vertex=null,segment2=threshold2,segmentPoint=null;
  for(const layer of state.layers){
    if(!layer.visible)continue;const positions=activeLayerPositions(layer,'line'),ox=layer.group.position.x,oy=layer.group.position.y;if(!positions)continue;
    for(let i=0;i<positions.length;i+=6){
      const ax=positions[i]+ox,ay=positions[i+1]+oy,bx=positions[i+3]+ox,by=positions[i+4]+oy,dx=bx-ax,dy=by-ay,den=dx*dx+dy*dy;
      const da2=(point.x-ax)**2+(point.y-ay)**2,db2=(point.x-bx)**2+(point.y-by)**2;
      if(da2<vertex2){vertex2=da2;vertex=new THREE.Vector3(ax,ay,positions[i+2]);}
      if(db2<vertex2){vertex2=db2;vertex=new THREE.Vector3(bx,by,positions[i+5]);}
      const t=den?Math.max(0,Math.min(1,((point.x-ax)*dx+(point.y-ay)*dy)/den)):0,x=ax+t*dx,y=ay+t*dy,d2=(point.x-x)**2+(point.y-y)**2;
      if(d2<segment2){segment2=d2;segmentPoint=new THREE.Vector3(x,y,THREE.MathUtils.lerp(positions[i+2],positions[i+5],t));}
    }
  }
  if(vertex)return {point:vertex,snapped:true,kind:'point',distance:Math.sqrt(vertex2)};
  return segmentPoint?{point:segmentPoint,snapped:true,kind:'segment',distance:Math.sqrt(segment2)}:{point,snapped:false,kind:null};
}
function featureAtSegment(features,segment) {
  let low=0,high=features.length-1;while(low<=high){const mid=(low+high)>>1,item=features[mid];if(segment<item.start)high=mid-1;else if(segment>=item.start+item.count)low=mid+1;else return item;}return null;
}
function highlightFeature(layer, segment) {
  const feature = featureAtSegment(layer.result.features || [], segment);
  if (!feature) return showToast('DXF entity not found.');
  const selection = selectionForFeature(activeLayerPositions(layer,'line'), layer.result.features || [], feature);
  
  selectionGeometry.setPositions(selection.positions);
  selectionOverlay.position.copy(layer.group.position);
  selectionOverlay.scale.copy(layer.group.scale);
  selectionOverlay.visible = true;
  selectionOverlay.userData.layer = layer;
  selectionOverlay.userData.feature = feature;

  // --- LOGIKA BLOK DAN ARSIR JIKA POLYLINE TERTUTUP ---
  if (selection.closed && selection.positions.length >= 6) {
    const shape = new THREE.Shape();
    let avgZ = 0;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    // Ambil titik-titik vertex dari array posisi garis
    const pts = [];
    for (let i = 0; i < selection.positions.length; i += 6) {
      const x = selection.positions[i];
      const y = selection.positions[i + 1];
      const z = selection.positions[i + 2];
      pts.push({ x, y });
      avgZ += z;
      
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    avgZ /= (selection.positions.length / 6);
    
    // Gambar pola poligon
    shape.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      shape.lineTo(pts[i].x, pts[i].y);
    }
    shape.closePath();

    const geom = new THREE.ShapeGeometry(shape);
    
    // Terapkan ke Blok Warna (Fill)
    selectionFill.geometry.dispose();
    selectionFill.geometry = geom;
    selectionFill.position.copy(layer.group.position);
    selectionFill.position.z += (avgZ * layer.group.scale.z) + 1; // Angkat sedikit agar tidak tenggelam
    selectionFill.scale.copy(layer.group.scale);
    selectionFill.visible = true;

    // Terapkan ke Arsiran (Hatch)
    selectionHatch.geometry.dispose();
    selectionHatch.geometry = geom;
    selectionHatch.position.copy(selectionFill.position);
    selectionHatch.position.z += 1; // Angkat arsiran sedikit di atas blok fill
    selectionHatch.scale.copy(layer.group.scale);
    
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);
    selectionHatchTexture.repeat.set(w / 35, h / 35);
    selectionHatch.visible = true;
  } else {
    // Jika polyline berupa garis terbuka (jalan/sungai), matikan blok & arsiran
    selectionFill.visible = false;
    selectionHatch.visible = false;
  }
  
  const type = selection.features.length > 1 ? 'Connected LINE (polyline)' : feature.type;
  const closed = selection.closed ? ' · closed' : '';
  const area = selection.closed ? ` · area ${selection.area.toLocaleString('en-US', { maximumFractionDigits: 2 })} m²` : '';
  showToast(`${type} · layer ${feature.layer} · ${selection.segmentCount.toLocaleString('en-US')} segments${closed} · length ${selection.length2d.toFixed(2)} m${area}`, 6500);
}

function pointerWorld(event) {
  const rect=ui.canvas.getBoundingClientRect();pointer.x=(event.clientX-rect.left)/rect.width*2-1;pointer.y=-(event.clientY-rect.top)/rect.height*2+1;raycaster.setFromCamera(pointer,activeCamera);
  const result=new THREE.Vector3();return raycaster.ray.intersectPlane(ground,result)?result:null;
}
function screenDistance(world,event) {
  const p=new THREE.Vector3(world.x,world.y,0).project(activeCamera), rect=ui.canvas.getBoundingClientRect();
  return Math.hypot((p.x+1)/2*rect.width-(event.clientX-rect.left),(1-p.y)/2*rect.height-(event.clientY-rect.top));
}
function snapDescription(snap){return snap.snapped?(snap.kind==='point'?'point snap':'segment snap'):'free';}
function placeSectionPoint(point,interaction='tap'){
  const snap=snapToDxf(point),description=snapDescription(snap),prefix=interaction==='double-tap'?'Double-tap':interaction==='hold'?'Hold':'Tap';
  if(!state.section.a){startSectionAt(snap.point);showToast(`${prefix} · point A · ${description}. Now select point B.`);return;}
  if(!state.section.b){state.section.b=new THREE.Vector2(snap.point.x,snap.point.y);updateSection();showToast(`${prefix} · point B · ${description}. The section line is complete and can be moved.`);return;}
  startSectionAt(snap.point);showToast(`${prefix} · new line, point A · ${description}. Select point B.`);
}
function beginSectionDrag(event,p){
  if(!state.section.a||!state.section.b)return false;
  const aDist=screenDistance(state.section.a,event),bDist=screenDistance(state.section.b,event),mid=new THREE.Vector2().addVectors(state.section.a,state.section.b).multiplyScalar(.5),mDist=screenDistance(mid,event);
  if(aDist>=30&&bDist>=30&&mDist>=38)return false;
  state.section.drag=aDist<30?'a':bDist<30?'b':'both';state.section.dragPointerId=event.pointerId;state.section.dragLast={x:p.x,y:p.y};activeControls.enabled=false;ui.canvas.setPointerCapture(event.pointerId);ui.sectionPanel.classList.remove('collapsed');return true;
}
function refreshMeasureVisual() {
  if (!state.measure.projectCoordinates.length) {
    measureHalo.visible = false;
    measureLine.visible = false;
    measureAreaFill.visible = false;
    measureCalloutEngine.hide();
    hideUnusedMeasurePointMarkers(0);
    return;
  }

  const points =
    state.measure.projectCoordinates.map(point => {
      const rendered =
        projectCoordinateToRender(
          point.x,
          point.y,
          point.z
        );

      return rendered
        ? new THREE.Vector3(
            rendered.x,
            rendered.y,
            rendered.z * state.exaggeration + 3
          )
        : null;
    });

  if (points.some(point => !point)) return;

  state.measure.points = points;

  ensureMeasurePointMarkers(points.length);

  points.forEach((point, index) => {
    const marker = measurePoints[index];

    marker.position.copy(point);
    marker.visible = true;
  });

  hideUnusedMeasurePointMarkers(points.length);
  updateMeasurePointSizes();
  const mode =
    state.measure?.mode || 'line';

  if (points.length >= 2) {
    const positions = [];

    points.forEach(point => {
      positions.push(...point.toArray());
    });

    // Area ditutup kembali ke titik pertama.
    if (
      mode === 'area' &&
      points.length >= 3
    ) {
      positions.push(
        ...points[0].toArray()
      );
    }

    setMeasureLinePositions(positions);

    updateMeasureAreaFill(points);

  } else {
    measureHalo.visible = false;
    measureLine.visible = false;
    measureAreaFill.visible = false;
  }
  updateMeasureLiveLabel();
}
function placeMeasurePoint(point) {
  const snap = snapToDxf(point);
  const target = snap.point;

  const elevation =
    topElevation(target.x, target.y);

  const projected =
    renderHorizontalToProject(
      target.x,
      target.y
    );

  const measured =
    new THREE.Vector3(
      target.x,
      target.y,
      renderElevationZ(
        target.x,
        target.y,
        elevation,
        3
      )
    );

  state.measure.points.push(measured);
  state.measure.snaps.push(
    snapDescription(snap)
  );

  if (projected) {
    state.measure.projectCoordinates.push({
      x: projected.x,
      y: projected.y,
      z: elevation
    });

    state.measure.canonicalCoordinates.push(
      canonicalProjectCoordinate(
        projected.x,
        projected.y,
        elevation
      )
    );
  }

  ensureMeasurePointMarkers(
    state.measure.points.length
  );

  const marker =
    measurePoints[
      state.measure.points.length - 1
    ];

  marker.position.copy(measured);
  marker.visible = true;

  refreshMeasureVisual();

  if (state.measure.points.length === 1) {
    showToast(
      `Measurement point 1 · ${snapDescription(snap)}`
    );

    updateToolActionState();
    return;
  }

  let totalDistance = 0;

  const projectPoints =
    state.measure.projectCoordinates;

  if (
    projectPoints.length ===
    state.measure.points.length
  ) {
    for (
      let index = 1;
      index < projectPoints.length;
      index++
    ) {
      const a = projectPoints[index - 1];
      const b = projectPoints[index];

      totalDistance += Math.hypot(
        b.x - a.x,
        b.y - a.y
      );
    }
  } else {
    for (
      let index = 1;
      index < state.measure.points.length;
      index++
    ) {
      const a =
        state.measure.points[index - 1];

      const b =
        state.measure.points[index];

      totalDistance += Math.hypot(
        b.x - a.x,
        b.y - a.y
      );
    }
  }
const mode =
  state.measure?.mode || 'line';

if (
  mode === 'area' &&
  state.measure.projectCoordinates.length >= 3
) {
  const metricPoints =
    state.measure.projectCoordinates.map(
      point =>
        new THREE.Vector2(
          point.x,
          point.y
        )
    );

  const metrics =
    polygonMetrics(metricPoints);

  const perimeter =
    measurePerimeter(metricPoints);

  state.measure.result = {
    area: metrics.area,
    perimeter,
    pointCount:
      state.measure.points.length
  };

  showToast(
    `Area ${formatMeasureArea(metrics.area)} · Perimeter ${formatMeasureLength(perimeter)} · ${state.measure.points.length} vertices`,
    3500
  );

} else {
  state.measure.result = {
    distance: totalDistance,
    pointCount:
      state.measure.points.length
  };

  showToast(
    `Distance ${formatMeasureLength(totalDistance)} · ${state.measure.points.length} vertices`,
    3500
  );
}

updateToolActionState();
}
function confirmDraftPoint(tool,point,event){
  if(tool==='section')placeSectionPoint(point,'double-tap');else if(tool==='measure')placeMeasurePoint(point);else if(tool==='boundary')placeBoundaryPoint(point,event);
  navigator.vibrate?.(18);
}
function registerDraftTap(tool, point, event) {
  // Jika sedang menggunakan tool Boundary, langsung tambahkan titik dengan sekali ketuk!
  if (tool === 'boundary') {
    confirmDraftPoint(tool, point, event);
    return;
  }
  
  // Logika Double-tap tetap dipertahankan untuk Section dan Measure
  const now = performance.now();
  const previous = state.lastDraftTap;
  const close = previous && previous.tool === tool && now - previous.time <= 420 && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= 34;
  
  if (close) {
    state.lastDraftTap = null;
    confirmDraftPoint(tool, point, event);
    return;
  }
  
  state.lastDraftTap = { tool, time: now, x: event.clientX, y: event.clientY };
  showToast(`Double-tap to confirm the ${tool === 'section' ? 'section point' : 'measurement point'}.`, 1800);
}
ui.canvas.addEventListener('pointermove', event => {
  const p = pointerWorld(event); 
  if (!p) return; 
  
  const z = topElevation(p.x, p.y);
  const projected=renderCoordinateToProject(p.x,p.y,z);ui.readout.textContent = projected?`E ${projected.x.toFixed(2)} · N ${projected.y.toFixed(2)} · RL ${Number.isFinite(z) ? z.toFixed(2) : '—'}`:'E — · N — · RL —';
  
  // BAGIAN BOUNDARY PREVIEW DI SINI KITA HAPUS/ALIHKAN KE CROSSHAIR
  // (Karena preview boundary sekarang sudah dihandle secara terpusat oleh crosshair di bawah)

  if (state.draftGesture?.pointerId === event.pointerId && Math.hypot(event.clientX - state.draftGesture.startX, event.clientY - state.draftGesture.startY) > 9) {
    state.draftGesture.moved = true;
  }
  
  if (!state.section.drag || state.section.dragPointerId !== event.pointerId) return;
  const d = state.section.drag, last = state.section.dragLast; 
  if (d === 'a') state.section.a.set(p.x, p.y);
  else if (d === 'b') state.section.b.set(p.x, p.y);
  else {
    const dx = p.x - last.x, dy = p.y - last.y;
    state.section.a.add(new THREE.Vector2(dx, dy));
    state.section.b.add(new THREE.Vector2(dx, dy));
  }
  state.section.dragLast = { x: p.x, y: p.y };
  updateSection();
});
// --- SENSOR GESER UNTUK GRAFIK SECTION ---
ui.canvas.addEventListener('pointerdown', event => {
  const p = pointerWorld(event);
  if (!p) return;

  if (state.tool === 'section') {
    if (beginSectionDrag(event, p)) {
      event.preventDefault();
      event.stopPropagation();
    }
    return;
  }

  if (['boundary','measure'].includes(state.tool)) {
    return;
  }
  
  state.activePointers.add(event.pointerId);
  if (state.activePointers.size > 1) {
    if (state.draftGesture) state.draftGesture.moved = true;
    state.lastDraftTap = null;
  }
  
  if (state.tool === 'select') {
    raycaster.params.Line.threshold = 5; 
    const objects = state.layers.filter(l => l.visible).map(l => l.lines), hits = raycaster.intersectObjects(objects, false);
    if (hits.length) { 
      const hit = hits[0], layer = hit.object.userData.layer; 
      selectMarker.position.copy(hit.point); 
      selectMarker.position.z += 2; 
      selectMarker.visible = true; 
      highlightFeature(layer, Math.floor((hit.index ?? 0) / 2)); 
    }
  }
});
window.addEventListener('pointermove', (event) => {
  if (!state.projectOrigin) return;
  const p = getCenterCanvasCoordinates();

  // 1. Boundary Preview (Garis merah akurat mengekor crosshair & membentuk loop tertutup)
  if (state.tool === 'boundary' && !state.boundary.closed) {
    if (state.boundary.points.length > 0) {
      state.boundary.preview = new THREE.Vector2(p.x, p.y);
      const points = state.boundary.points;
      
      const workingPath = [...points, state.boundary.preview];
      if (points.length > 0) {
        workingPath.push(points[0]);
      }

      const positions = [];
      workingPath.forEach(pt => positions.push(pt.x, pt.y, boundaryDisplayZ(pt) + 6));
      
      if (typeof boundaryLine !== 'undefined' && boundaryLine) {
        boundaryLine.geometry.dispose();
        const boundaryGeo = new LineGeometry(); // Menggunakan nama variabel unik agar tidak konflik
        boundaryGeo.setPositions(positions);
        boundaryLine.geometry = boundaryGeo;
        boundaryLine.computeLineDistances();
        boundaryLine.material.resolution.set(ui.viewport.clientWidth || 1, ui.viewport.clientHeight || 1);
        boundaryHalo.geometry.dispose();
        const boundaryHaloGeo = new LineGeometry();
        boundaryHaloGeo.setPositions(positions);
        boundaryHalo.geometry = boundaryHaloGeo;
        boundaryHalo.computeLineDistances();
        boundaryHalo.material.resolution.set(ui.viewport.clientWidth || 1, ui.viewport.clientHeight || 1);
        boundaryHalo.visible = true;
        boundaryLine.visible = true;
      }

      if (typeof updateBoundaryFillAndHatch === 'function') {
        updateBoundaryFillAndHatch();
      }
    }
  }

  // 2. Section Preview
  if (state.tool === 'section' && state.section.a && !state.section.b) {
    const za = renderElevationZ(state.section.a.x,state.section.a.y,topElevation(state.section.a.x,state.section.a.y),4);
    const zb = renderElevationZ(p.x,p.y,topElevation(p.x,p.y),4);
    sectionHalo.geometry.setPositions([state.section.a.x, state.section.a.y, za, p.x, p.y, zb]);
    sectionHalo.computeLineDistances();
    sectionLine.geometry.setPositions([state.section.a.x, state.section.a.y, za, p.x, p.y, zb]);
    sectionLine.computeLineDistances();
    sectionHalo.visible = sectionLine.visible = markerB.visible = true;
    markerB.position.set(p.x, p.y, zb);
    labelB.position.set(p.x, p.y, zb + 16);
    labelB.visible = true;
  }

 // 3. Measure Preview
if (
  state.tool === 'measure' &&
  state.measure.points.length >= 1
) {
  const previewElevation =
    topElevation(p.x, p.y);

  const previewPoint =
    new THREE.Vector3(
      p.x,
      p.y,
      renderElevationZ(
        p.x,
        p.y,
        previewElevation,
        3
      )
    );

  const previewProject =
    renderCoordinateToProject(
      p.x,
      p.y,
      previewElevation
    );

  state.measure.preview =
    previewProject
      ? {
          x: previewProject.x,
          y: previewProject.y,
          z: previewElevation
        }
      : null;

  const positions = [];

  state.measure.points.forEach(point => {
    positions.push(...point.toArray());
  });

  positions.push(
    ...previewPoint.toArray()
  );

  // Area preview always closes back to the first vertex.
  if (
    state.measure?.mode === 'area' &&
    state.measure.points.length >= 2
  ) {
    positions.push(
      ...state.measure.points[0].toArray()
    );
  }

  setMeasureLinePositions(positions);

  if (state.measure?.mode === 'area') {
    updateMeasureAreaFill([
      ...state.measure.points,
      previewPoint
    ]);
  }

  updateMeasureLiveLabel();
}

}, true);

function finishPointerInteraction(event,cancelled=false){
  if(state.section.drag&&state.section.dragPointerId===event.pointerId){const moved=state.section.drag;if(moved==='a'||moved==='b'){const current=state.section[moved],snap=snapToDxf(current);if(snap.snapped)current.set(snap.point.x,snap.point.y);updateSection();}state.section.drag=null;state.section.dragPointerId=null;controls2d.enabled=state.mode==='2d';controls3d.enabled=state.mode==='3d';try{ui.canvas.releasePointerCapture(event.pointerId);}catch{}state.activePointers.delete(event.pointerId);return;}
  const gesture=state.draftGesture;if(gesture?.pointerId===event.pointerId){if(!cancelled&&!gesture.moved&&state.activePointers.size===1)registerDraftTap(gesture.tool,gesture.point,event);state.draftGesture=null;}
  state.activePointers.delete(event.pointerId);
}

ui.canvas.addEventListener('pointerup',event=>finishPointerInteraction(event,false));
ui.canvas.addEventListener('pointercancel',event=>finishPointerInteraction(event,true));

function clearMeasureDrawing() {
  const mode =
    state.measure?.mode || 'line';

  state.measure = {
    mode,
    points: [],
    snaps: [],
    projectCoordinates: [],
    canonicalCoordinates: [],
    preview: null,
    finalized: false,
    result: null
  };

  measureHalo.visible = false;
  measureLine.visible = false;
  measureAreaFill.visible = false;
  measureCalloutEngine.hide();
  measureCalloutEngine.resetPlacement();
  measurePoints.forEach(
    marker => marker.visible = false
  );

  updateToolActionState();
}

function showLiveSectionPanel(message) {
  if (!ui.sectionPanel) return;

  // Section window HANYA milik tool Section.
  if (state.tool !== 'section') return;

  ui.sectionPanel.classList.remove('collapsed');

  if (
    message &&
    !state.section.a &&
    !state.section.b &&
    ui.sectionInfo
  ) {
    ui.sectionInfo.textContent = message;
  }

  drawSection();
}
function setTool(tool) {
  const previous = state.tool, repeated = previous === tool;
  state.lastDraftTap = null; 
  state.draftGesture = null;
  
  if (tool !== 'section' && state.section.press) {
    clearTimeout(state.section.press.timer);
    state.section.press = null;
    controls2d.enabled = state.mode === '2d';
    controls3d.enabled = state.mode === '3d';
  }
  
  if (previous !== tool) {
    if (previous === 'section') resetSectionDrawing();
    if (previous === 'measure') clearMeasureDrawing();
    if (previous === 'boundary') {
      state.boundary.preview = null;
      state.boundary.points = []; // PENTING: Bersihkan titik lama agar tidak nyangkut saat ganti tool/buat baru
      state.boundary.closed = false;
      if (typeof boundaryLine !== 'undefined' && boundaryLine) {
        boundaryHalo.visible = false;
        boundaryLine.visible = false;
      }
      if (typeof boundaryLine !== 'undefined' && boundaryLine) { boundaryHalo.visible = false; boundaryLine.visible = false; }
      if (typeof boundaryVertices !== 'undefined' && boundaryVertices) {
        boundaryVertices.visible = false;
        while (boundaryVertices.children.length) boundaryVertices.remove(boundaryVertices.children[0]);
      }
      if (typeof hideBoundaryVisual === 'function') hideBoundaryVisual();
    }
    if (previous === 'select') {
      selectionOverlay.visible = false;
      selectMarker.visible = false;
      if (typeof selectionFill !== 'undefined') selectionFill.visible = false;
      if (typeof selectionHatch !== 'undefined') selectionHatch.visible = false;
    }
  }
  
  state.tool = tool;
  $$('[data-tool]').forEach(button => button.classList.toggle('active', button.dataset.tool === tool));
  const toolsUsingCrosshair = ['measure', 'boundary', 'section', 'marking', 'mark'];
  const needsCrosshair = toolsUsingCrosshair.includes(tool);
  toggleCrosshair(needsCrosshair);
  // Tool Focus Mode:
  // tool carousel bekerja di canvas fullscreen
  const focusTools = ['measure', 'section', 'boundary'];
  const toolFocusActive = focusTools.includes(tool);

  document.body.classList.toggle(
    'tool-focus-mode',
    toolFocusActive
  );

  if (toolFocusActive) {
    // Tutup drawer jika sedang terbuka
    if (typeof openDrawer === 'function') {
      openDrawer(false);
    }

    // Tutup carousel setelah tool dipilih
    if (typeof setFeatureMenu === 'function') {
      setFeatureMenu(false);
    }
  }

  // 1. Munculkan/Sembunyikan Crosshair di tengah layar
  const crosshairEl = document.getElementById('mapCrosshair');
  if (crosshairEl) {
    crosshairEl.style.display = needsCrosshair ? 'block' : 'none';
  }

  // 2. Munculkan/Sembunyikan Tombol Add Point Terapung di Layar
  const addPointBtn = document.getElementById('centerAddPointBtn');
  if (addPointBtn) {
    addPointBtn.classList.toggle('hidden', !needsCrosshair);
  }

  if (tool === 'section') {
    if (repeated && state.section.a) {
      resetSectionDrawing();
    }

  if (!state.section.a) {
    ui.sectionInfo.textContent =
      'Geser peta ke crosshair, lalu tekan Add Point';

    ui.sectionPanel.classList.add('hidden');
    ui.sectionPanel.classList.add('collapsed');
  }

  else if (!state.section.b) {
    ui.sectionPanel.classList.add('hidden');
    ui.sectionPanel.classList.add('collapsed');
  }

  else {
    ui.sectionPanel.classList.remove('hidden');
    ui.sectionPanel.classList.remove('collapsed');
  }
  } else {
    ui.sectionPanel.classList.add('collapsed');
    ui.sectionPanel.classList.add('hidden');
    ui.sectionPanel.style.position = '';
    ui.sectionPanel.style.left = '';
    ui.sectionPanel.style.top = '';
    ui.sectionPanel.style.transform = '';
    ui.sectionPanel.style.transition = '';
    ui.sectionPanel.style.width = '';
    ui.sectionPanel.style.zIndex = '';
    ui.sectionPanel.style.bottom = '';
    ui.sectionPanel.style.right = '';
  }
  
  if (tool === 'measure' && repeated) clearMeasureDrawing();
  if (tool === 'boundary') {
    boundaryVertices.visible = true;
    updateBoundaryVisual();
    ui.boundaryPanel.classList.remove('hidden');
    if (ui.boundaryInstruction) ui.boundaryInstruction.textContent = 'Geser peta ke crosshair, lalu tekan Add Point';
  } else {
    ui.boundaryPanel.classList.add('hidden');
  }
  updateToolActionState();
  updateMeasureModeUi();
  updateToolActionState();
  applySpatialRenderFrame();
}
function updateExaggeration(value){state.exaggeration=Number(value);ui.exagValue.textContent=`${state.exaggeration}×`;state.layers.forEach(l=>l.group.scale.z=state.exaggeration);state.pdfLayers.forEach(layer=>layer.mesh.scale.z=state.exaggeration);state.savedBoundaries.forEach(layer=>layer.group.scale.z=state.exaggeration);state.savedTracks.forEach(layer=>layer.group.scale.z=state.exaggeration);if(selectionOverlay.userData.layer)selectionOverlay.scale.copy(selectionOverlay.userData.layer.group.scale);state.tracking.points.forEach(point=>setSurveyPointRenderFrame(point,state.renderFrame));updateTrackVisual();state.marks.forEach(mark=>setSurveyPointRenderFrame(mark,state.renderFrame));if(state.gpsFix?.x!==undefined){setSurveyPointRenderFrame(state.gpsFix,state.renderFrame);updateGpsVisual();}if(state.tool==='boundary')updateBoundaryVisual();else hideBoundaryVisual();updateSection();refreshMeasureVisual();}

function ensureGpsMarker() {
  if (gpsGroup) return;
  
  gpsGroup = new THREE.Group();
  gpsGroup.renderOrder = 999;

  const dummyHalo = new THREE.Group();
  dummyHalo.name = 'accuracy';
  
  const screenMarker = new THREE.Group();
  screenMarker.name = 'screenMarker';
  
  const outline = new THREE.Mesh(
    new THREE.RingGeometry(8, 14, 32),
    new THREE.MeshBasicMaterial({ 
      color: 0xffffff, 
      side: THREE.DoubleSide, 
      depthTest: false, 
      depthWrite: false, 
      transparent: true 
    })
  );
  outline.renderOrder = 999;
  const dot = new THREE.Mesh(
    new THREE.CircleGeometry(8, 32),
    new THREE.MeshBasicMaterial({ 
      color: 0x168cff, 
      side: THREE.DoubleSide, 
      depthTest: false, 
      depthWrite: false, 
      transparent: true 
    })
  );
  dot.renderOrder = 1000; // Paling atas!
  
  screenMarker.add(outline, dot);
  gpsGroup.add(dummyHalo, screenMarker);
  scene.add(gpsGroup);
}
function updateGpsMarkerSize() {
  if (!gpsGroup?.visible) return;

  const height = Math.max(1, ui.viewport.clientHeight);
  const point = gpsGroup.position;

  const unitsPerPixel = state.mode === '2d'
    ? (ortho.top - ortho.bottom) /
      Math.max(0.001, ortho.zoom) /
      height
    : 2 *
      Math.max(1, perspective.position.distanceTo(point)) *
      Math.tan(
        THREE.MathUtils.degToRad(perspective.fov / 2)
      ) /
      height;

  const screenMarker =
    gpsGroup.getObjectByName('screenMarker');

  if (screenMarker) {
    screenMarker.scale.setScalar(
      Math.max(0.01, unitsPerPixel * 1.15)
    );
  }
}
function updatePositionMarkSizes() {
  const height = Math.max(1, ui.viewport.clientHeight);

  state.marks.forEach(mark => {
    if (!mark.node) return;

    const point = mark.node.position;

    const unitsPerPixel = state.mode === '2d'
      ? (ortho.top - ortho.bottom) /
        Math.max(0.001, ortho.zoom) /
        height
      : 2 *
        perspective.position.distanceTo(point) *
        Math.tan(THREE.MathUtils.degToRad(perspective.fov / 2)) /
        height;

    mark.node.scale.setScalar(
      Math.max(0.01, unitsPerPixel * 1.35)
    );
  });
}
function outsideDistance(point,bounds){
  if(!bounds)return 0;const dx=point.x<bounds.minX?bounds.minX-point.x:point.x>bounds.maxX?point.x-bounds.maxX:0,dy=point.y<bounds.minY?bounds.minY-point.y:point.y>bounds.maxY?point.y-bounds.maxY:0;return Math.hypot(dx,dy);
}
function formatGpsDistance(meters){return meters>=1000?`${(meters/1000).toFixed(meters>=10000?0:1)} km`:`${meters.toFixed(0)} m`;}
function centerOnGps(){
  const fix=state.gpsFix;if(!fix||fix.x===undefined)return showToast('No GPS position has been received yet.');
  ensureGpsMarker();gpsGroup.visible=true;
  if(state.mode==='2d'){
    const z=fix.displayZ+Math.max(100,ortho.position.z-activeControls.target.z);ortho.position.set(fix.x,fix.y,z);controls2d.target.set(fix.x,fix.y,fix.displayZ);controls2d.update();
  }else{
    const offset=perspective.position.clone().sub(controls3d.target);controls3d.target.set(fix.x,fix.y,fix.displayZ);perspective.position.copy(controls3d.target).add(offset);perspective.far=Math.max(perspective.far,offset.length()*50);perspective.updateProjectionMatrix();controls3d.update();
  }
  showToast(`GPS · E ${fix.easting.toFixed(2)} · N ${fix.northing.toFixed(2)} · ±${fix.accuracy.toFixed(0)} m`,5000);
}
let liveTrackGeometry = null;
let lastTrackRawFix = null;

function updateTrackVisual() {
  const points = state.tracking.points;
  if (points.length < 2) {
    trackLine.visible = false;
    return;
  }
  const positions = [];
  points.forEach(point => positions.push(point.x, point.y, point.displayZ));
  
  // Perbarui geometri garis secara dinamis agar live line langsung nampak di layar
  trackGeometry.dispose();
  trackGeometry = new LineGeometry();
  trackGeometry.setPositions(positions);
  trackLine.geometry = trackGeometry;
  trackLine.computeLineDistances();
  trackLine.visible = true;
}
let wakeLock = null;

async function requestWakeLock() {
  try {
    if ('wakeLock' in navigator) {
      wakeLock = await navigator.wakeLock.request('screen');
    }
  } catch (err) {
    console.error(`Wake Lock error: ${err.name}, ${err.message}`);
  }
}

function releaseWakeLock() {
  if (wakeLock !== null) {
    wakeLock.release()
      .then(() => {
        wakeLock = null;
      })
      .catch(error => {
        console.warn(
          'Wake Lock release failed:',
          error
        );

        wakeLock = null;
      });
  }
}
// Pengaturan tracking: ubah nilai ini bila ingin lebih jarang/lebih responsif.
const TRACK_MIN_DISTANCE_METERS = 1.5;
const TRACK_MIN_INTERVAL_MS = 1000;
const TRACK_FORCE_SAMPLE_MS = 5000;

function appendTrackFix(fix) {
  const points = state.tracking.points;
  const last = points.at(-1);
  const lastRaw = lastTrackRawFix || last;
  const moved = lastRaw ? Math.hypot(fix.easting - lastRaw.easting, fix.northing - lastRaw.northing) : Infinity;
  const elapsed = lastRaw ? Math.max(0, Number(fix.time) - Number(lastRaw.time)) : Infinity;

  // Buang jitter GPS kecil dan update yang datang terlalu rapat.
  if (lastRaw) {
    const tooClose =
      moved < TRACK_MIN_DISTANCE_METERS;

    const tooSoon =
      elapsed < TRACK_MIN_INTERVAL_MS;

    const forceSample =
      elapsed >= TRACK_FORCE_SAMPLE_MS;

     if (
      (tooClose || tooSoon) &&
      !forceSample
    ) {
      return;
    }
  }

  lastTrackRawFix = fix;
  let rawRl =Number(fix.altitude);
  if (!Number.isFinite(rawRl) || rawRl === 0) {
    rawRl = topElevation(fix.x, fix.y);
  }
  
  const enhancedFix = {
    ...fix,
    rl: rawRl,
    displayZ: rawRl * state.exaggeration + 8
  };

  points.push(enhancedFix);
  updateTrackVisual(); // Update garis secara langsung setiap ada titik baru masuk
}
function updateGpsVisual() {
  const fix = state.gpsFix;

  if (
    !fix ||
    !Number.isFinite(fix.x) ||
    !Number.isFinite(fix.y)
  ) {
    return;
  }

  ensureGpsMarker();

  if (!gpsGroup) {
    return;
  }

  const displayZ =
    Number.isFinite(fix.displayZ)
      ? fix.displayZ
      : 8;

  // GPS marker WAJIB memakai render-local XY
  // yang sama dengan live track.
  gpsGroup.position.set(
    fix.x,
    fix.y,
    displayZ
  );

  gpsGroup.visible = true;

  const accuracyObj =
    gpsGroup.getObjectByName('accuracy');

  if (
    accuracyObj &&
    Number.isFinite(fix.accuracy)
  ) {
    accuracyObj.scale.setScalar(
      Math.max(3, fix.accuracy)
    );
  }

  if (
    typeof updateGpsMarkerSize === 'function'
  ) {
    updateGpsMarkerSize();
  }

  const bounds = projectBounds();

  const inside =
    !!bounds &&
    fix.x >= bounds.minX &&
    fix.x <= bounds.maxX &&
    fix.y >= bounds.minY &&
    fix.y <= bounds.maxY;

  if (ui.gpsIndicator) {
    ui.gpsIndicator.classList.toggle(
      'hidden',
      inside
    );
  }

  if (ui.gpsIndicatorText) {
    ui.gpsIndicatorText.textContent =
      Number.isFinite(fix.accuracy)
        ? `GPS: ±${Math.round(fix.accuracy)}m`
        : 'GPS active';
  }

  if (
    !inside &&
    bounds &&
    ui.gpsIndicatorArrow
  ) {
    const center = {
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2
    };

    const angle =
      Math.atan2(
        fix.x - center.x,
        fix.y - center.y
      ) *
      180 /
      Math.PI;

    ui.gpsIndicatorArrow.style.transform =
      `rotate(${angle}deg)`;
  }
}
async function handleGpsPosition(position) {
  if (!state.currentProject) {
    return;
  }

  const latitude =
    Number(position.coords.latitude);

  const longitude =
    Number(position.coords.longitude);

  const altitude =
    Number(position.coords.altitude);

  const accuracy =
    Number(position.coords.accuracy);

  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return;
  }

  let crs = state.tracking.active
    ? trackingProjectCrs()
    : null;

  if (!crs) {
    crs = currentProjectCrs();
  }


  // ==========================================
  // PROJECT CRS BELUM ADA
  // GPS HANYA MEMBERI SUGGESTION
  // ==========================================

  if (!crs) {
    const suggested =
      gpsSuggestedCrs(
        latitude,
        longitude
      );

    await assignProjectCrs(
      suggested,
      'gps',
      {
        status: 'suggested'
      }
    );

    crs = currentProjectCrs();

    showToast(
      `GPS suggests UTM ${suggested.zone}${suggested.hemisphere}. Confirm Project CRS before survey/export.`,
      5500
    );
  }


  if (!crs) {
    return;
  }

  adoptWorldReference(
    makeWorldReference(
      latitude,
      longitude,
      Number.isFinite(altitude) ? altitude : 0
    )
  );

  if (
    state.tracking.active &&
    !trackingProjectCrs()
  ) {
    state.tracking.projectCrs = crs;
    state.tracking.crsStatus = projectCrsStatus();
  }


  // ==========================================
  // GPS WGS84 → PROJECT CRS
  // ==========================================

  const utm =
    latLonToUtm(
      latitude,
      longitude,
      crs.zone,
      crs.hemisphere
    );


  // projectOrigin hanya render anchor
  if (!state.projectOrigin) {
    adoptProjectOrigin(
      {
        x:
          Math.floor(
            utm.easting / 1000
          ) * 1000,

        y:
          Math.floor(
            utm.northing / 1000
          ) * 1000,

        elevation: 0
      },
      'gps'
    );
  }


  if (!state.projectOrigin) {
    return;
  }


  loadSavedMarkRecords();


  const groundRender = projectCoordinateToRender(
    utm.easting,
    utm.northing,
    0
  );
  if (!groundRender) return;
  const x = groundRender.x;
  const y = groundRender.y;


  let rawRl = altitude;

  if (
    !Number.isFinite(rawRl) ||
    rawRl === 0
  ) {
    rawRl =
      topElevation(x, y);
  }


  const renderPoint = projectCoordinateToRender(
    utm.easting,
    utm.northing,
    rawRl
  );
  if (!renderPoint) return;
  const displayZ =
    renderPoint.z * state.exaggeration + 8;

  const canonical = state.spatialFrame
    ? projectToCanonical(
        utm.easting,
        utm.northing,
        rawRl,
        state.spatialFrame
      )
    : null;


  const fix = {
    position,

    // WGS84 asli dari device
    latitude,
    longitude,
    altitude:
      Number.isFinite(altitude)
        ? altitude
        : null,

    accuracy:
      Number.isFinite(accuracy)
        ? accuracy
        : null,

    // Absolute Project CRS
    easting:
      utm.easting,

    northing:
      utm.northing,

    projectCrs:
      `EPSG:${crs.epsg}`,

    canonical,

    // Render-local
    x: renderPoint.x,
    y: renderPoint.y,
    renderZ: renderPoint.z,

    rl: rawRl,
    displayZ,

    time:
      Number(position.timestamp) ||
      Date.now()
  };


  state.gpsFix = fix;


  if (state.tracking.active) {
    appendTrackFix(fix);
  }

  applySpatialRenderFrame({ refit: false });
  updateGpsVisual?.();
}

function stopGps() {
  if (state.gpsWatch !== null) {
    navigator.geolocation.clearWatch(
      state.gpsWatch
    );
  }

  state.gpsWatch = null;

  $('#gpsButton').classList.remove('active');

  if (gpsGroup) {
    gpsGroup.visible = false;
  }

  ui.gpsIndicator.classList.add('hidden');

  if (state.tracking.active) {
    state.tracking.active = false;
    $('#trackButton').classList.remove('active');
    lastTrackRawFix = null;
  }
}

function toggleGps() {
  if (state.gpsWatch !== null) {
    stopGps();
    showToast('GPS disabled.');
  } else {
    startGps();
  }
}

function totalTrackDistance() {
  let total = 0;
  for (let i = 1; i < state.tracking.points.length; i++) {
    total += Math.hypot(
      state.tracking.points[i].easting - state.tracking.points[i-1].easting,
      state.tracking.points[i].northing - state.tracking.points[i-1].northing
    );
  }
  return total;
}
function nativePointToPosition(point) {
  return {
    coords: {
      latitude: Number(point.latitude),
      longitude: Number(point.longitude),
      altitude: Number(point.altitude),
      accuracy: Number(point.accuracy),
      speed: Number(point.speed),
      heading: Number(point.course)
    },
    timestamp: Number(point.timestamp)
  };
}

async function prepareBackgroundLocationListeners() {
  if (backgroundLocationListener) return;

  backgroundLocationListener =
    await BackgroundLocation.addListener(
      'location',
      point => {
        handleGpsPosition(
          nativePointToPosition(point)
        );
      }
    );
  backgroundLocationErrorListener =
    await BackgroundLocation.addListener(
      'error',
      error => {
        console.error(
          'Background location:',
          error.message
        );

        showToast(
          `GPS Error: ${error.message}`,
          5000
        );
      }
    );
}

async function startNativeBackgroundTracking() {
  await prepareBackgroundLocationListeners();

  return BackgroundLocation.start({
    distanceFilter: 3
  });
}

async function stopNativeBackgroundTracking() {
  return BackgroundLocation.stop();
}

function backgroundLocationPluginAvailable() {
  return Capacitor.isNativePlatform()
    && (typeof Capacitor.isPluginAvailable !== 'function'
      || Capacitor.isPluginAvailable('BackgroundLocation'));
}

function backgroundLocationPluginMissing(error) {
  return /not implemented|unimplemented|not available|not installed|missing/i.test(String(error?.message || error || ''));
}

function nativeTrackPoints(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.points)) return result.points;
  if (Array.isArray(result?.locations)) return result.locations;
  return [];
}

async function clearNativeTrackCache() {
  if (!backgroundLocationPluginAvailable()) return;
  try {
    await BackgroundLocation.clear();
  } catch (error) {
    console.warn(
      'Native track cache could not be cleared.',
      error
    );
  }
}
async function toggleTracking() {
  if (state.tracking.active) {

    if (state.tracking.nativeActive) {
      try {
        const result =
          await stopNativeBackgroundTracking();
        const nativePoints = nativeTrackPoints(result);
    if (nativePoints.length) {
      // Native cache menjadi sumber track final.
      // Reset juga filter raw supaya replay dimulai bersih.
      state.tracking.points = [];
      lastTrackRawFix = null;

      for (const point of nativePoints) {
        await handleGpsPosition(
          nativePointToPosition(point)
        );
      }
    }
        await clearNativeTrackCache();
      } catch (error) {
        console.error(
          'Could not stop native tracking:',
          error
        );
      }
    }

    state.tracking.active = false;
    state.tracking.nativeActive = false;
    $('#trackButton').classList.remove('active');

    if (!Capacitor.isNativePlatform()) {
      releaseWakeLock();
    }
    const distance = totalTrackDistance();
    const pointsCount = state.tracking.points.length;
    
    if (pointsCount > 1) {
       const projectCrs = trackingProjectCrs() || currentProjectCrs();
       const currentCrs = currentProjectCrs();
       const savedCrsStatus =
         projectCrs &&
         currentCrs?.epsg === projectCrs.epsg &&
         isProjectCrsConfirmed()
           ? 'confirmed'
           : state.tracking.crsStatus || projectCrsStatus();
       const trackMarkIds = state.marks.filter(mark => mark.time >= (state.tracking.startedAt || 0)).map(mark => mark.id);
       persistMarks();
    const saved = {
      id: crypto.randomUUID(),

      name:
        `GPS Track ${new Date().toLocaleString(
          'en-US',
          {
            hour12: false,
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }
        )}`,

      crs:
        projectCrs
          ? `EPSG:${projectCrs.epsg}`
          : 'LOCAL',

      crsStatus:
        savedCrsStatus,

      order:
        nextLayerOrder(),

      distance,

      markIds:
        trackMarkIds,

      coordinates:
        state.tracking.points.map(
          p => [
            p.easting,
            p.northing,
            p.rl
          ]
        ),

      canonicalCoordinates:
        state.tracking.points.map(
          p => p.canonical ||
            canonicalProjectCoordinate(p.easting, p.northing, p.rl)
        ),

      points:
        state.tracking.points.map(
          p => ({
            latitude: optionalTrackNumber(p.latitude),
            longitude: optionalTrackNumber(p.longitude),
            altitude: optionalTrackNumber(p.altitude),
            accuracy: optionalTrackNumber(p.accuracy),
            easting: p.easting,
            northing: p.northing,
            rl: p.rl,
            canonical: p.canonical ||
              canonicalProjectCoordinate(p.easting, p.northing, p.rl),
            projectCrs:
              p.projectCrs ||
              (projectCrs
                ? `EPSG:${projectCrs.epsg}`
                : null),
            time: optionalTrackNumber(p.time)
          })
        ),

      wgs84:
        state.tracking.points.map(
          p => [
            p.longitude,
            p.latitude,
            Number.isFinite(p.altitude)
              ? p.altitude
              : null
          ]
        )
    };
       try {
         const items = JSON.parse(localStorage.getItem(trackStorageKey()) || '[]');
         items.push(saved); localStorage.setItem(trackStorageKey(), JSON.stringify(items));
         createSavedTrackLayer(saved);renderLayerList();await touchActiveProject();await clearNativeTrackCache();

  showToast(
    `Tracking saved to Project Explorer · ${distance.toFixed(1)} m${trackMarkIds.length ? ` · ${trackMarkIds.length} mark` : ''}`,
    6000
  );
} catch (error) {
  console.error(
    'Track save failed:',
    error
  );

  showToast(
    'Could not save track layer.',
    5000
  ); 
 }
    } else {
       showToast(`Tracking stopped. Not enough points to save.`, 4000);
    }
    
    state.tracking.points = [];
    state.tracking.projectCrs = null;
    state.tracking.crsStatus = null;
    lastTrackRawFix = null;
    updateTrackVisual();
    return;
  }

  const startingCrs = currentProjectCrs();

  state.tracking = {
  active: true,
  points: [],
  startedAt: Date.now(),
  nativeActive: false,
  projectCrs: startingCrs,
  crsStatus: startingCrs
    ? projectCrsStatus()
    : null
};
lastTrackRawFix = null;

if (Capacitor.isNativePlatform()) {
  if (backgroundLocationPluginAvailable()) {
    try {
      await startNativeBackgroundTracking();
      state.tracking.nativeActive = true;
    } catch (error) {
      if (!backgroundLocationPluginMissing(error) || !startGps()) {
        state.tracking.active = false;

        $('#trackButton').classList.remove('active');

        showToast(
          `Cannot start background tracking: ${error.message}`,
          6000
        );

        return;
      }
      showToast('Background tracking unavailable. Using foreground GPS tracking.', 6000);
    }
  } else if (!startGps()) {
    state.tracking.active = false;
    return;
  } else {
    showToast('Background tracking plugin is not installed. Using foreground GPS tracking.', 6000);
  }
} else {
  if (!startGps()) {
    state.tracking.active = false;
    return;
  }
}
  trackLine.visible = false;
  $('#trackButton').classList.add('active');
  
  showToast('GPS tracking started. Running in background...');
  if (!Capacitor.isNativePlatform()) {
  requestWakeLock();
}
}
// --- SISTEM BASEMAP SATELIT OFFLINE-FIRST ---
const BASEMAP_CACHE_NAME = 'eagle-basemap-cache-v1';

async function fetchTileOffline(url) {
  // 1. Cek apakah perangkat mendukung Cache API
  if (!('caches' in window)) return url; 

  try {
    const cache = await caches.open(BASEMAP_CACHE_NAME);
    const cachedResponse = await cache.match(url);

    // 2. Jika gambar sudah ada di memori HP (Offline mode) -> Langsung tampilkan
    if (cachedResponse) {
      const blob = await cachedResponse.blob();
      return URL.createObjectURL(blob);
    }

    // 3. Jika belum ada (Online mode) -> Download dari server dan simpan ke HP
    const fetchResponse = await fetch(url, { mode: 'cors' });
    if (fetchResponse.ok) {
      cache.put(url, fetchResponse.clone()); // Simpan ke Cache
      const blob = await fetchResponse.blob();
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    console.warn('Gagal memuat basemap tile:', error);
  }
  return url; // Fallback jika gagal
}
let basemapMesh = null;

async function loadProjectBasemap() {
  const bounds = projectBounds();
  if (!bounds || !state.projectOrigin) return showToast('Buka data spasial terlebih dahulu untuk memuat Basemap.');
  
  showToast('Memuat Basemap Satelit...', 2000);

  // 1. Hitung lebar dan panjang area proyek Anda
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const minZ = bounds.minZ * state.exaggeration; // Elevasi paling bawah

  // 2. URL Satelit Esri (Sebagai contoh kita gunakan gambar statis/skala besar dulu)
  // Catatan: Pada implementasi penuh, ini akan menggunakan kalkulasi rumit XYZ Tile (Zoom, X, Y) dari Lat/Lon.
  // Untuk saat ini, kita simulasikan dengan mengambil tekstur global/area luas.
  const tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/15/16432/27041'; // Contoh URL Tile

  try {
    // 3. Panggil sistem Offline/Cache kita
    const imageUrl = await fetchTileOffline(tileUrl);

    // 4. Buat Tekstur dan Material Three.js
    const loader = new THREE.TextureLoader();
    loader.load(imageUrl, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      
      // 5. Buat Geometri Lantai (Plane) yang ukurannya melebihi area proyek (misal dikali 2)
      const planeGeometry = new THREE.PlaneGeometry(width * 2, height * 2);
      const planeMaterial = new THREE.MeshBasicMaterial({ 
        map: texture, 
        side: THREE.DoubleSide, 
        depthWrite: false, // Agar tidak bentrok (z-fighting) dengan model
        transparent: true,
        opacity: 0.8
      });

      if (basemapMesh) scene.remove(basemapMesh); // Hapus basemap lama jika ada
      
      basemapMesh = new THREE.Mesh(planeGeometry, planeMaterial);
      
      // 6. Posisikan tepat di tengah proyek, namun elevasinya diturunkan 10 meter dari titik terdalam
      basemapMesh.position.set(centerX, centerY, minZ - 10);
      basemapMesh.renderOrder = -1; // Render paling bawah (background)
      
      scene.add(basemapMesh);
      showToast('Basemap berhasil dimuat!', 3000);
    });
  } catch (err) {
    showToast('Gagal memuat Basemap. Pastikan koneksi internet aktif untuk muatan pertama.');
  }
}
function screenshot(){renderer.render(scene,activeCamera);ui.canvas.toBlob(blob=>{if(blob)downloadBlob(blob,`eagle-terrain-${Date.now()}.png`);},'image/png');}

const featureDock=$('#featureDock'),featureCarousel=$('#featureCarousel'),featureTools=$('.feature-tools'),featureGroupTitle=$('#featureGroupTitle'),featureMenuButton=$('#featureMenuButton');
const featureItems=$$('[data-feature-name]');
let featureIndex=0,featureScrollTimer=null;
function featureItemCenter(button){const itemRect=button.getBoundingClientRect(),toolsRect=featureTools.getBoundingClientRect();return itemRect.left-toolsRect.left+featureTools.scrollLeft+itemRect.width/2;}
function updateFeatureArc(){const center=featureTools.scrollLeft+featureTools.clientWidth/2;featureItems.forEach(button=>{const distance=Math.abs(featureItemCenter(button)-center),ratio=Math.min(1.8,distance/66);button.style.setProperty('--arc-y',`${Math.min(30,ratio*ratio*10).toFixed(1)}px`);button.style.setProperty('--arc-scale',Math.max(.76,1.12-ratio*.18).toFixed(3));button.style.opacity=String(Math.max(.42,1-ratio*.25));});}
function selectFeature(index,{activate=false,smooth=true}={}){
  featureIndex=(index+featureItems.length)%featureItems.length;
  const button=featureItems[featureIndex];featureItems.forEach((item,itemIndex)=>item.classList.toggle('selected',itemIndex===featureIndex));featureGroupTitle.textContent=button.dataset.featureName;
  const left=featureItemCenter(button)-featureTools.clientWidth/2;featureTools.scrollTo({left,behavior:smooth?'smooth':'auto'});
  requestAnimationFrame(updateFeatureArc);
  if(!activate)return;
  if(button.dataset.tool)setTool(button.dataset.tool);
  else if(button.dataset.featureAction==='gps'){setTool('pan');toggleGps();}
  else if(button.dataset.featureAction==='track')toggleTracking();
  else if (button.dataset.featureAction === 'mark') {
    setTool('mark');
    addPositionMark();
  }
}
async function addPositionMark() {
  if (!state.currentProject) {
    showToast('Open or create a project first.');
    return;
  }

  if (!state.projectOrigin) {
    showToast(
      'Project origin is not available yet. Open spatial data first.',
      4500
    );
    return;
  }

  const defaultName = `M${state.marks.length + 1}`;

  const choice = await openMarkDialog({
    defaultName,
    defaultSource: state.gpsFix ? "gps" : "crosshair",
  });

  if (!choice) return;

  const point = choice.source === "gps" ? gpsMarkPoint() : crosshairMarkPoint();

  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    showToast("Point position is not available.", 4000);
    return;
  }

  const projectCrs = currentProjectCrs();

  let latitude = point.latitude;
  let longitude = point.longitude;

  if (projectCrs) {
    try {
      const geo = utmToLatLon(
        point.easting,
        point.northing,
        projectCrs.zone,
        projectCrs.hemisphere,
      );

      if (Number.isFinite(geo?.latitude) && Number.isFinite(geo?.longitude)) {
        latitude = geo.latitude;
        longitude = geo.longitude;
      }
    } catch (error) {
      console.warn("[Maply Mark] WGS84 conversion failed:", error);
    }
  }

  const mark = {
    id: crypto.randomUUID(),
    name: choice.name,
    source: choice.source,

    crs: projectCrs ? `EPSG:${projectCrs.epsg}` : "LOCAL",

    easting: point.easting,
    northing: point.northing,

    x: point.x,
    y: point.y,

    rl: point.rl,

    displayZ: point.rl * state.exaggeration + 8,

    accuracy: point.accuracy ?? null,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    altitude: point.altitude ?? null,

    canonical: canonicalProjectCoordinate(
      point.easting,
      point.northing,
      point.rl,
    ),

    time: Date.now(),
  };

  setSurveyPointRenderFrame(mark, state.renderFrame);

  const node = createMarkerMesh(mark);
  mark.node = node;

  marksGroup.add(node);
  state.marks.push(mark);

  persistMarks();

  await touchActiveProject();

  renderLayerList();

  showToast(
    `${choice.name} saved · E ${point.easting.toFixed(2)} · N ${point.northing.toFixed(2)}`,
    4500,
  );
}

function openMarkDialog({ defaultName, defaultSource = "crosshair" }) {
  return new Promise((resolve) => {
    const backdrop = document.createElement("div");
    backdrop.className = "mark-dialog-backdrop";

    backdrop.innerHTML = `
      <form class="mark-dialog">
        <div class="mark-dialog-head">
          <strong>Save Point Mark</strong>
          <small>Choose the point source and name.</small>
        </div>

        <label>
          <span>Mark Name</span>
          <input
            name="name"
            value="${escapeMarkHtml(defaultName)}"
            autocomplete="off"
          />
        </label>

        <div
          class="mark-source-toggle"
          role="group"
          aria-label="Point source"
        >
          <button type="button" data-source="crosshair">
            Crosshair
          </button>

          <button type="button" data-source="gps">
            GPS Position
          </button>
        </div>

        <div class="mark-dialog-actions">
          <button
            type="button"
            data-action="cancel"
            class="ghost"
          >
            Cancel
          </button>

          <button type="submit">
            Save
          </button>
        </div>
      </form>
    `;

    document.body.appendChild(backdrop);

    let source = defaultSource === "gps" && state.gpsFix ? "gps" : "crosshair";

    const form = backdrop.querySelector("form");

    const input = backdrop.querySelector('input[name="name"]');

    const sourceButtons = [...backdrop.querySelectorAll("[data-source]")];

    const updateSourceButtons = () => {
      sourceButtons.forEach((button) => {
        const isGps = button.dataset.source === "gps";

        button.classList.toggle("active", button.dataset.source === source);

        button.disabled = isGps && !state.gpsFix;

        button.title = isGps && !state.gpsFix ? "GPS fix belum tersedia" : "";
      });
    };

    const close = (value) => {
      backdrop.remove();
      resolve(value);
    };

    sourceButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) {
          showToast("GPS position is not available yet.", 3500);
          return;
        }

        source = button.dataset.source;
        updateSourceButtons();
      });
    });

    backdrop
      .querySelector('[data-action="cancel"]')
      .addEventListener("click", () => close(null));

    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) {
        close(null);
      }
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();

      close({
        name: input.value.trim() || defaultName,
        source,
      });
    });

    updateSourceButtons();

    input.focus();
    input.select();
  });
}

function escapeMarkHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character],
  );
}

function crosshairMarkPoint() {
  const point = getCenterCanvasCoordinates();

  if (!point) return null;

  const projectedPoint = renderCoordinateToProject(point.x, point.y, point.z);

  if (!projectedPoint) {
    showToast("Crosshair coordinate could not be transformed.", 4000);
    return null;
  }

  let rl = topElevation(point.x, point.y);

  if (!Number.isFinite(rl)) {
    rl = 0;
  }

  return {
    source: "crosshair",

    x: point.x,
    y: point.y,

    easting: projectedPoint.x,
    northing: projectedPoint.y,

    rl,

    latitude: null,
    longitude: null,
    altitude: null,
    accuracy: null,
  };
}

function gpsMarkPoint() {
  const fix = state.gpsFix;

  if (!fix || !Number.isFinite(fix.easting) || !Number.isFinite(fix.northing)) {
    showToast("GPS position is not available yet.", 4000);
    return null;
  }

  return {
    source: "gps",

    x: fix.x,
    y: fix.y,

    easting: fix.easting,
    northing: fix.northing,

    rl: Number.isFinite(fix.rl) ? fix.rl : 0,

    latitude: Number.isFinite(fix.latitude) ? fix.latitude : null,

    longitude: Number.isFinite(fix.longitude) ? fix.longitude : null,

    altitude: Number.isFinite(fix.altitude) ? fix.altitude : null,

    accuracy: Number.isFinite(fix.accuracy) ? fix.accuracy : null,
  };
}
// 2. Mengambil koordinat peta yang tepat berada di tengah layar (Crosshair)
function getCenterCanvasCoordinates() {
  const target = activeControls ? activeControls.target : { x: 0, y: 0 };
  const z = topElevation(target.x, target.y);
  return new THREE.Vector3(target.x, target.y, z);
}
function createMarkerMesh(mark) {
  const node = new THREE.Group();

  const pin = new THREE.Mesh(
    new THREE.SphereGeometry(6, 20, 14),
    new THREE.MeshBasicMaterial({
      color: 0xffc45d,
      depthTest: false,
      depthWrite: false
    })
  );

  pin.renderOrder = 1001;

  const label =
  endpointLabel(
    mark.name || mark.id,
    '#ffc45d'
  );
  label.position.z = 20;
  label.renderOrder = 1002;

  node.add(pin, label);
  node.position.set(mark.x, mark.y, mark.displayZ + 3);
  node.renderOrder = 1001;
  node.name = `position-mark-${mark.id}`;

  return node;
}
// ==========================================
// 1. TOOL: GPS MARKING POINT
// ==========================================
function addPositionMarkFromCenter() {
  const coords = getCenterCanvasCoordinates();
  
  const origin = state.projectOrigin || { x: 0, y: 0 };
  const newMark = {
    easting: origin.x + coords.x,
    northing: origin.y + coords.y,
    x: coords.x,
    y: coords.y,
    displayZ: coords.displayZ || 0,
    time: Date.now()
  };

  // Simpan ke array data marker aplikasi
  if (!state.markers) state.markers = [];
  state.markers.push(newMark);
  
  showToast('Marker ditandai dari titik tengah layar!');
  
  // Refresh tampilan peta/grafik
  if (typeof drawSection === 'function') drawSection();
}


// ==========================================
// 2, 3, & 4. TOOLS: MEASURE, BOUNDARY, & SECTION
// ==========================================
// Variabel penampung titik sementara untuk tool aktif (jika belum ada di file Anda)
window.activeToolPoints = window.activeToolPoints || [];

function addPointFromCenterToActiveTool() {
  const point = getCenterCanvasCoordinates();
  const activeTool = state.tool;

  if (activeTool === 'boundary') {
    // PENTING: Jika boundary sebelumnya sudah di-close, otomatis reset titik lama agar tidak nyangkut!
    if (state.boundary.closed) {
      state.boundary.points = [];
      state.boundary.closed = false;
    }

    // Masukkan titik baru ke array
    state.boundary.points.push(new THREE.Vector2(point.x, point.y));
    
    // Inisialisasi preview agar garis langsung mengekor crosshair seketika
    state.boundary.preview = new THREE.Vector2(point.x, point.y);
    
    updateBoundaryVisual(true);
    ui.boundaryPanel.classList.remove('hidden');
    showToast(`Boundary vertex ${state.boundary.points.length} added.`);

  } else if (activeTool === 'section') {
    if (!state.section.a) {
      startSectionAt(point);
      showToast('Point A set at crosshair. Move map to point B and press Add Point.');
    } else if (!state.section.b) {
      state.section.b = new THREE.Vector2(point.x, point.y);
      updateSection();
      ui.sectionPanel.classList.remove('hidden');
      ui.sectionPanel.classList.remove('collapsed');
      updateToolActionState();
      showToast('Point B set. Cross Section chart generated.');
    } else {
      resetSectionDrawing();
      startSectionAt(point);
      ui.sectionPanel.classList.add('collapsed');
      showToast('New section: Point A set at crosshair.');
    }

  } else if (activeTool === 'measure') {
    placeMeasurePoint(point);
    updateToolActionState();
  }
}

// 4. Fungsi Undo untuk Menghapus Titik Terakhir
function undoActiveToolPoint() {
  const activeTool = state.tool;

  if (activeTool === 'boundary') {
    if (state.boundary.points.length > 0) {
      undoBoundaryPoint();
      showToast('Vertex boundary terakhir dihapus.');
    }
  } else if (activeTool === 'section') {
    if (state.section.b) {
      state.section.b = null;
      sectionHalo.visible = sectionLine.visible = markerB.visible = labelB.visible = false;
      ui.sectionPanel.classList.add('collapsed');
      showToast('Point B dibatalkan.');
    } else if (state.section.a) {
      resetSectionDrawing();
      showToast('Section direset.');
    }
  } else if (activeTool === 'measure') {
    if (state.measure.points.length > 0) {
      state.measure.points.pop();
      state.measure.snaps.pop();
      state.measure.projectCoordinates.pop();
      state.measure.canonicalCoordinates.pop();

      refreshMeasureVisual();

      state.measure.result = null;

      showToast(
        state.measure.points.length
          ? `Measurement vertex removed · ${state.measure.points.length} remaining`
          : 'Measurement cleared.'
      );
    }
  }
}
  // Perbarui garis/visual poligon secara *real-time* di layar
  if (typeof updateActiveToolVisuals === 'function') {
    updateActiveToolVisuals();
  } else if (typeof drawSection === 'function') {
    drawSection();
  }
function setActiveTool(toolName) {
  state.activeTool = toolName; // 'measure', 'boundary', 'section', 'marking', dll.
  
  // Daftar tool yang membutuhkan crosshair di tengah layar
  const toolsUsingCrosshair = ['measure', 'boundary', 'section', 'marking'];
  const needsCrosshair = toolsUsingCrosshair.includes(toolName);

  // Nyalakan atau matikan elemen CSS #mapCrosshair
  const crosshairEl = document.getElementById('mapCrosshair');
  if (crosshairEl) {
    crosshairEl.style.display = needsCrosshair ? 'block' : 'none';
  }
  // 2. Munculkan/Sembunyikan Tombol Add Point Terapung
  const addPointBtn = document.getElementById('centerAddPointBtn');
  if (addPointBtn) {
    addPointBtn.classList.toggle('hidden', !needsCrosshair);
  }
  console.log(`Tool aktif: ${toolName}, Crosshair: ${needsCrosshair ? 'AKTIF' : 'MATI'}`);
}
function setFeatureMenu(open){
  featureDock.classList.toggle('open',open);ui.viewport.classList.toggle('feature-menu-open',open);
  featureMenuButton.setAttribute('aria-expanded',String(open));featureMenuButton.setAttribute('aria-label',open?'Close tool carousel':'Open tool carousel');featureCarousel.setAttribute('aria-hidden',String(!open));
  ui.viewport.scrollTop=0;ui.viewport.scrollLeft=0;
  if(open)requestAnimationFrame(()=>selectFeature(featureIndex,{smooth:false}));
}
featureMenuButton.addEventListener('click',()=>setFeatureMenu(!featureDock.classList.contains('open')));
$('#previousFeatureGroup').addEventListener('click',()=>selectFeature(featureIndex-1));$('#nextFeatureGroup').addEventListener('click',()=>selectFeature(featureIndex+1));
featureItems.forEach((button,index)=>button.addEventListener('click',()=>selectFeature(index,{activate:true})));
featureTools.addEventListener('scroll',()=>{updateFeatureArc();clearTimeout(featureScrollTimer);featureScrollTimer=setTimeout(()=>{const center=featureTools.scrollLeft+featureTools.clientWidth/2;let nearest=0,distance=Infinity;featureItems.forEach((button,index)=>{const d=Math.abs(featureItemCenter(button)-center);if(d<distance){nearest=index;distance=d;}});featureIndex=nearest;featureItems.forEach((button,index)=>button.classList.toggle('selected',index===nearest));featureGroupTitle.textContent=featureItems[nearest].dataset.featureName;updateFeatureArc();},70);},{passive:true});

$('#openButton').addEventListener('click',()=>{if(!state.currentProject)return showProjectHub();state.pendingOpenMode='add-data';ui.fileInput.click();});
ui.fileInput.addEventListener('change',async()=>{
  const files=[...ui.fileInput.files],mode=state.pendingOpenMode;state.pendingOpenMode=null;ui.fileInput.value='';if(!files.length)return;
  if(mode==='cloud-project')await createCloudProject(files);
  await openFiles(files);
});
$('#showNewProject').addEventListener('click',()=>{ui.newProjectForm.classList.remove('hidden');ui.newProjectName.value='';requestAnimationFrame(()=>ui.newProjectName.focus());});
$('#cancelNewProject').addEventListener('click',()=>ui.newProjectForm.classList.add('hidden'));
ui.newProjectForm.addEventListener('submit',async event=>{event.preventDefault();const name=ui.newProjectName.value.trim();if(!name)return ui.newProjectName.focus();await createBlankProject(name);});
$('#editProjectCrs').addEventListener('click',promptProjectCrs);
ui.sectionWidth?.addEventListener('input',()=>{if(state.section.last.length){state.section.metrics=integrateSectionMetrics(state.section.last,Math.max(.01,Number(ui.sectionWidth.value)||1));updateSectionMetricsUi();}});
// Open popup modal when button clicked
// Buka modal utama
// Open popup modal when button clicked
$('#openExistingProjectBtn').addEventListener('click', () => {
  $('#mainModalView').classList.remove('hidden');
  $('#cloudModalView').classList.add('hidden');
  $('#projectSourceModal').classList.remove('hidden');
});

// Local Folder selection
$('#btnLocalProject').addEventListener('click', () => {
  $('#projectSourceModal').classList.add('hidden');
  state.pendingOpenMode = 'local-project';
  ui.fileInput.accept = '.mply,.dxf,.pdf,.kml,.kmz,.shp,.prj';
  ui.fileInput.click();
});

// Switch to Cloud Sub-menu
$('#btnOpenCloudMenu').addEventListener('click', () => {
  $('#mainModalView').classList.add('hidden');
  $('#cloudModalView').classList.remove('hidden');
});

// Back to main modal view
$('#btnBackToMain').addEventListener('click', () => {
  $('#cloudModalView').classList.add('hidden');
  $('#mainModalView').classList.remove('hidden');
});

// Open Cloud URL in browser
async function openCloudBrowser(url) {
  $('#projectSourceModal').classList.add('hidden');
  if (globalThis.Capacitor?.isNativePlatform?.()) {
    await Browser.open({ url: url });
  } else {
    window.open(url, '_blank');
  }
}

$('#btnGDrive').addEventListener('click', () => openCloudBrowser('https://drive.google.com'));
$('#btnOneDrive').addEventListener('click', () => openCloudBrowser('https://onedrive.live.com'));
$('#btnICloud').addEventListener('click', () => openCloudBrowser('https://www.icloud.com'));

// Close modal buttons
$('#btnCloseModal').addEventListener('click', () => {
  $('#projectSourceModal').classList.add('hidden');
});

$('#projectSourceModal').addEventListener('click', (e) => {
  if (e.target.id === 'projectSourceModal') {
    $('#projectSourceModal').classList.add('hidden');
  }
});
$('#menuButton').addEventListener('click',()=>openDrawer(true));$('#closeDrawer').addEventListener('click',()=>openDrawer(false));ui.scrim.addEventListener('click',()=>openDrawer(false));
$('#projectsButton').addEventListener('click', async () => {
  openDrawer(false);
  await showProjectHub();
});
$('#fitButton').addEventListener('click',fitAllProject);$('#northButton').addEventListener('click',resetNorth);$('#captureButton').addEventListener('click',screenshot);$('#gpsButton').addEventListener('click',()=>{if(state.gpsWatch===null)setTool('pan');toggleGps();});$('#trackButton').addEventListener('click',()=>{if(!state.tracking.active)setTool('pan');toggleTracking();});$('#markButton').addEventListener('click',()=>{setTool('pan');addPositionMark();});ui.gpsIndicator.addEventListener('click',centerOnGps);ui.exag.addEventListener('input',()=>updateExaggeration(ui.exag.value));ui.compass.addEventListener('click',resetNorth);
$('#basemapBtn').addEventListener('click', () => {toggleBasemap();});
$('#coordinateButton').addEventListener('click',openCoordinateSearch);$('#closeCoordinate').addEventListener('click',()=>ui.coordinateSearch.classList.add('hidden'));$('#goCoordinate').addEventListener('click',goToCoordinate);[ui.contentSearch,ui.coordinateE,ui.coordinateN].forEach(input=>input.addEventListener('keydown',event=>{if(event.key==='Enter')goToCoordinate();}));
// WINDOW INTERACTION: BOUNDARY
// Drag header + pinch 2 jari
// ==========================================
const boundaryWindow = ui.boundaryPanel;
const boundaryWindowHandle =
  boundaryWindow?.querySelector('.field-panel-head');

const boundaryWindowPointers = new Map();

let boundaryWindowDrag = null;
let boundaryWindowPinch = null;

const BOUNDARY_WINDOW_MIN_W = 280;
const BOUNDARY_WINDOW_MIN_H = 260;
const BOUNDARY_WINDOW_MARGIN = 8;

function boundaryWindowPointerDistance(a, b) {
  return Math.hypot(
    b.x - a.x,
    b.y - a.y
  );
}

function floatBoundaryWindow() {
  const rect =
    boundaryWindow.getBoundingClientRect();

  boundaryWindow.style.position = 'fixed';
  boundaryWindow.style.left = `${rect.left}px`;
  boundaryWindow.style.top = `${rect.top}px`;
  boundaryWindow.style.width = `${rect.width}px`;
  boundaryWindow.style.height = `${rect.height}px`;

  boundaryWindow.style.right = 'auto';
  boundaryWindow.style.bottom = 'auto';
  boundaryWindow.style.transform = 'none';
  boundaryWindow.style.transition = 'none';
  boundaryWindow.style.zIndex = '9999';

  return rect;
}

function setBoundaryWindowRect(
  left,
  top,
  width,
  height
) {
  const maxWidth = Math.max(
    BOUNDARY_WINDOW_MIN_W,
    window.innerWidth -
      BOUNDARY_WINDOW_MARGIN * 2
  );

  const maxHeight = Math.max(
    BOUNDARY_WINDOW_MIN_H,
    window.innerHeight -
      BOUNDARY_WINDOW_MARGIN * 2
  );

  width = Math.max(
    BOUNDARY_WINDOW_MIN_W,
    Math.min(maxWidth, width)
  );

  height = Math.max(
    BOUNDARY_WINDOW_MIN_H,
    Math.min(maxHeight, height)
  );

  const maxLeft = Math.max(
    BOUNDARY_WINDOW_MARGIN,
    window.innerWidth -
      width -
      BOUNDARY_WINDOW_MARGIN
  );

  const maxTop = Math.max(
    BOUNDARY_WINDOW_MARGIN,
    window.innerHeight -
      height -
      BOUNDARY_WINDOW_MARGIN
  );

  left = Math.max(
    BOUNDARY_WINDOW_MARGIN,
    Math.min(maxLeft, left)
  );

  top = Math.max(
    BOUNDARY_WINDOW_MARGIN,
    Math.min(maxTop, top)
  );

  boundaryWindow.style.left = `${left}px`;
  boundaryWindow.style.top = `${top}px`;
  boundaryWindow.style.width = `${width}px`;
  boundaryWindow.style.height = `${height}px`;
}

if (boundaryWindow) {
  boundaryWindow.style.touchAction = 'none';

  boundaryWindow.addEventListener(
    'pointerdown',
    event => {
      if (event.pointerType !== 'touch') {
        return;
      }

      boundaryWindowPointers.set(
        event.pointerId,
        {
          x: event.clientX,
          y: event.clientY
        }
      );

      if (boundaryWindowPointers.size === 2) {
        const points =
          [...boundaryWindowPointers.values()];

        const rect =
          floatBoundaryWindow();

        boundaryWindowDrag = null;

        boundaryWindowPinch = {
          startDistance: Math.max(
            1,
            boundaryWindowPointerDistance(
              points[0],
              points[1]
            )
          ),

          width: rect.width,
          height: rect.height,

          centerX:
            rect.left + rect.width / 2,

          centerY:
            rect.top + rect.height / 2
        };
      }

      event.preventDefault();
    },
    { passive: false, capture: true }
  );

  boundaryWindow.addEventListener(
    'pointermove',
    event => {
      if (
        boundaryWindowPointers.has(
          event.pointerId
        )
      ) {
        boundaryWindowPointers.set(
          event.pointerId,
          {
            x: event.clientX,
            y: event.clientY
          }
        );
      }

      // PINCH 2 JARI
      if (
        boundaryWindowPinch &&
        boundaryWindowPointers.size >= 2
      ) {
        const points =
          [...boundaryWindowPointers.values()];

        const distance = Math.max(
          1,
          boundaryWindowPointerDistance(
            points[0],
            points[1]
          )
        );

        const scale =
          distance /
          boundaryWindowPinch.startDistance;

        const width =
          boundaryWindowPinch.width * scale;

        const height =
          boundaryWindowPinch.height * scale;

        setBoundaryWindowRect(
          boundaryWindowPinch.centerX -
            width / 2,

          boundaryWindowPinch.centerY -
            height / 2,

          width,
          height
        );

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // DRAG WINDOW
      if (
        boundaryWindowDrag &&
        boundaryWindowDrag.pointerId ===
          event.pointerId
      ) {
        const left =
          boundaryWindowDrag.left +
          event.clientX -
          boundaryWindowDrag.startX;

        const top =
          boundaryWindowDrag.top +
          event.clientY -
          boundaryWindowDrag.startY;

        setBoundaryWindowRect(
          left,
          top,
          boundaryWindowDrag.width,
          boundaryWindowDrag.height
        );

        event.preventDefault();
        event.stopPropagation();
      }
    },
    { passive: false }
  );

  const finishBoundaryWindowPointer =
    event => {
      boundaryWindowPointers.delete(
        event.pointerId
      );

      if (
        boundaryWindowDrag?.pointerId ===
        event.pointerId
      ) {
        boundaryWindowDrag = null;

        if (boundaryWindowHandle) {
          boundaryWindowHandle.style.cursor =
            'grab';
        }
      }

      if (
        boundaryWindowPointers.size < 2
      ) {
        boundaryWindowPinch = null;
      }
    };

  boundaryWindow.addEventListener(
    'pointerup',
    finishBoundaryWindowPointer
  );

  boundaryWindow.addEventListener(
    'pointercancel',
    finishBoundaryWindowPointer
  );

  // DRAG DARI HEADER
  if (boundaryWindowHandle) {
    boundaryWindowHandle.style.touchAction =
      'none';

    boundaryWindowHandle.style.cursor =
      'grab';

    boundaryWindowHandle.addEventListener(
      'pointerdown',
      event => {
        if (
          event.target.closest('button')
        ) {
          return;
        }

        const rect =
          floatBoundaryWindow();

        boundaryWindowDrag = {
          pointerId: event.pointerId,

          startX: event.clientX,
          startY: event.clientY,

          left: rect.left,
          top: rect.top,

          width: rect.width,
          height: rect.height
        };

        boundaryWindowHandle.style.cursor =
          'grabbing';

        try {
          boundaryWindowHandle
            .setPointerCapture(
              event.pointerId
            );
        } catch {}

        event.preventDefault();
        event.stopPropagation();
      }
    );
  }

}

$('#closeBoundaryPanel').addEventListener('click',()=>setTool('select'));$('#undoBoundary').addEventListener('click',undoBoundaryPoint);$('#finishBoundary').addEventListener('click',finishBoundary);$('#saveBoundary').addEventListener('click',saveBoundaryReference);$('#clearBoundary').addEventListener('click',clearBoundaryData);ui.boundaryElevation.addEventListener('input',()=>{const value=ui.boundaryElevation.value.trim();state.boundary.referenceZ=value===''?null:Number(value);if(state.tool==='boundary')updateBoundaryVisual();});
const modeToggleBtn = document.getElementById('modeToggleBtn');

modeToggleBtn?.addEventListener('click', () => {
  setMode(state.mode === '2d' ? '3d' : '2d');
});
$$('[data-preset]').forEach(button=>button.addEventListener('click',()=>preset(button.dataset.preset)));$$('.tool-rail [data-tool]').forEach(button=>button.addEventListener('click',()=>setTool(state.tool===button.dataset.tool?'none':button.dataset.tool)));
$('#volumeCsvButton').addEventListener('click',()=>{exportVolumeCsv();ui.exportMenu.classList.add('hidden');});$('#trackCsvButton').addEventListener('click',async()=>{await exportTrackCsv();ui.exportMenu.classList.add('hidden');});
$('#moreButton').addEventListener('click',()=>ui.exportMenu.classList.toggle('hidden'));$('#screenshotButton').addEventListener('click',()=>{screenshot();ui.exportMenu.classList.add('hidden');});
$('#clearButton').addEventListener('click',async()=>{if(!state.currentProject)return showProjectHub();if(!confirm(`Clear all offline data from “${state.currentProject.name}”?\n\nOriginal cloud files will not be deleted.`))return;clearProject(true);state.currentProject={...state.currentProject,fileCount:0,updatedAt:Date.now()};await saveProject(state.currentProject);showToast('Project data and offline cache cleared.');});
$('#installButton').addEventListener('click',async()=>{if(state.installPrompt){state.installPrompt.prompt();await state.installPrompt.userChoice;state.installPrompt=null;}else showToast('Use the browser menu and choose “Add to Home Screen”.',4000);ui.exportMenu.classList.add('hidden');});
$('#gridButton').addEventListener('click',toggleGrid);
ui.exag.addEventListener('input',()=>state.pdfLayers.forEach(layer=>layer.mesh.scale.z=Number(ui.exag.value)));
window.addEventListener('beforeinstallprompt',event=>{event.preventDefault();state.installPrompt=event;});
document.addEventListener('click',event=>{if(!ui.exportMenu.contains(event.target)&&event.target!==$('#moreButton'))ui.exportMenu.classList.add('hidden');});
const dropZone=$('#dropZone');dropZone.addEventListener('click',()=>{if(!state.currentProject)return showProjectHub();state.pendingOpenMode='add-data';ui.fileInput.click();});dropZone.addEventListener('keydown',event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();if(!state.currentProject)return showProjectHub();state.pendingOpenMode='add-data';ui.fileInput.click();}});['dragenter','dragover'].forEach(type=>dropZone.addEventListener(type,event=>{event.preventDefault();dropZone.classList.add('dragging');}));['dragleave','drop'].forEach(type=>dropZone.addEventListener(type,event=>{event.preventDefault();dropZone.classList.remove('dragging');}));dropZone.addEventListener('drop',event=>openFiles(event.dataTransfer.files));
if('launchQueue'in window)window.launchQueue.setConsumer(async params=>{const files=[];for(const handle of params.files)files.push(await handle.getFile());await openExternalFilesWithProjectChoice(files);});
const nativePlatform=globalThis.Capacitor?.isNativePlatform?.()===true;
if(nativePlatform){
  ui.fileInput.removeAttribute('accept');
  enableNativeDocumentOpen().catch(error=>console.error('Open With could not be enabled',error));
}
if(!nativePlatform&&'serviceWorker'in navigator){
  if(import.meta.env.DEV){
    window.addEventListener('load',async()=>{
      const registrations=await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration=>registration.unregister()));
      if('caches'in globalThis)await Promise.all((await caches.keys()).map(key=>caches.delete(key)));
    });
  }else window.addEventListener('load',()=>navigator.serviceWorker.register('./sw.js').catch(console.warn));
}
// SENSOR GESER UNTUK GRAFIK SECTION 
ui.sectionCanvas.style.touchAction = 'none'; // Cegah layar ikut tergeser
ui.sectionCanvas.addEventListener('pointermove', (e) => {
  state.section.hoverX = e.offsetX; // Menggunakan offsetX jauh lebih presisi
  drawSection();
});
ui.sectionCanvas.addEventListener('pointerleave', () => {
  state.section.hoverX = null;
  drawSection();
});
// LOGIKA INTERAKSI WINDOW LIVE SECTION
// Drag header + corner resize + pinch resize 2 jari.
const liveSectionPanel = ui.sectionPanel;
const liveSectionHandle =
  liveSectionPanel?.querySelector('.panel-handle');

const liveSectionPointers = new Map();

let liveSectionDrag = null;
let liveSectionResize = null;
let liveSectionPinch = null;

const LIVE_SECTION_MIN_W = 280;
const LIVE_SECTION_MIN_H = 180;
const LIVE_SECTION_MARGIN = 8;

function liveSectionPointerDistance(a, b) {
  return Math.hypot(
    b.x - a.x,
    b.y - a.y
  );
}

function floatLiveSectionPanel() {
  const rect =
    liveSectionPanel.getBoundingClientRect();

  liveSectionPanel.style.position = 'fixed';
  liveSectionPanel.style.left = `${rect.left}px`;
  liveSectionPanel.style.top = `${rect.top}px`;
  liveSectionPanel.style.width = `${rect.width}px`;
  liveSectionPanel.style.height = `${rect.height}px`;

  liveSectionPanel.style.right = 'auto';
  liveSectionPanel.style.bottom = 'auto';
  liveSectionPanel.style.transform = 'none';
  liveSectionPanel.style.transition = 'none';
  liveSectionPanel.style.zIndex = '9999';

  return rect;
}

function setLiveSectionPanelRect(
  left,
  top,
  width,
  height
) {
  const maxWidth = Math.max(
    LIVE_SECTION_MIN_W,
    window.innerWidth -
      LIVE_SECTION_MARGIN * 2
  );

  const maxHeight = Math.max(
    LIVE_SECTION_MIN_H,
    window.innerHeight -
      LIVE_SECTION_MARGIN * 2
  );

  width = Math.max(
    LIVE_SECTION_MIN_W,
    Math.min(maxWidth, width)
  );

  height = Math.max(
    LIVE_SECTION_MIN_H,
    Math.min(maxHeight, height)
  );

  const maxLeft = Math.max(
    LIVE_SECTION_MARGIN,
    window.innerWidth -
      width -
      LIVE_SECTION_MARGIN
  );

  const maxTop = Math.max(
    LIVE_SECTION_MARGIN,
    window.innerHeight -
      height -
      LIVE_SECTION_MARGIN
  );

  left = Math.max(
    LIVE_SECTION_MARGIN,
    Math.min(maxLeft, left)
  );

  top = Math.max(
    LIVE_SECTION_MARGIN,
    Math.min(maxTop, top)
  );

  liveSectionPanel.style.left = `${left}px`;
  liveSectionPanel.style.top = `${top}px`;
  liveSectionPanel.style.width = `${width}px`;
  liveSectionPanel.style.height = `${height}px`;

  drawSection();
}

if (liveSectionPanel) {
  $('#closeSectionBtn')?.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    setTool('select');
  });
  // Gesture di panel jangan diteruskan ke map.
  liveSectionPanel.style.touchAction = 'none';

  liveSectionPanel.addEventListener(
    'pointerdown',
    event => {
      // Allow header pointerdown to reach the drag handler.

      if (event.pointerType !== 'touch') {
        return;
      }

      liveSectionPointers.set(
        event.pointerId,
        {
          x: event.clientX,
          y: event.clientY
        }
      );

      // Mulai pinch setelah ada dua jari.
      if (liveSectionPointers.size === 2) {
        const points =
          [...liveSectionPointers.values()];

        const rect =
          floatLiveSectionPanel();

        liveSectionDrag = null;
        liveSectionResize = null;

        liveSectionPinch = {
          startDistance: Math.max(
            1,
            liveSectionPointerDistance(
              points[0],
              points[1]
            )
          ),

          width: rect.width,
          height: rect.height,

          centerX:
            rect.left + rect.width / 2,

          centerY:
            rect.top + rect.height / 2
        };
      }

      event.preventDefault();
    },
    { passive: false, capture: true }
  );

  liveSectionPanel.addEventListener(
    'pointermove',
    event => {
      if (
        liveSectionPointers.has(
          event.pointerId
        )
      ) {
        liveSectionPointers.set(
          event.pointerId,
          {
            x: event.clientX,
            y: event.clientY
          }
        );
      }

      // PINCH 2 JARI
      if (
        liveSectionPinch &&
        liveSectionPointers.size >= 2
      ) {
        const points =
          [...liveSectionPointers.values()];

        const distance = Math.max(
          1,
          liveSectionPointerDistance(
            points[0],
            points[1]
          )
        );

        const scale =
          distance /
          liveSectionPinch.startDistance;

        const width =
          liveSectionPinch.width * scale;

        const height =
          liveSectionPinch.height * scale;

        setLiveSectionPanelRect(
          liveSectionPinch.centerX -
            width / 2,

          liveSectionPinch.centerY -
            height / 2,

          width,
          height
        );

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // RESIZE HANDLE
      if (
        liveSectionResize &&
        liveSectionResize.pointerId ===
          event.pointerId
      ) {
        const width =
          liveSectionResize.width +
          event.clientX -
          liveSectionResize.startX;

        const height =
          liveSectionResize.height +
          event.clientY -
          liveSectionResize.startY;

        setLiveSectionPanelRect(
          liveSectionResize.left,
          liveSectionResize.top,
          width,
          height
        );

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // DRAG WINDOW
      if (
        liveSectionDrag &&
        liveSectionDrag.pointerId ===
          event.pointerId
      ) {
        const left =
          liveSectionDrag.left +
          event.clientX -
          liveSectionDrag.startX;

        const top =
          liveSectionDrag.top +
          event.clientY -
          liveSectionDrag.startY;

        setLiveSectionPanelRect(
          left,
          top,
          liveSectionDrag.width,
          liveSectionDrag.height
        );

        event.preventDefault();
        event.stopPropagation();
      }
    },
    { passive: false }
  );

  function finishLiveSectionPointer(event) {
    liveSectionPointers.delete(
      event.pointerId
    );

    if (
      liveSectionDrag?.pointerId ===
      event.pointerId
    ) {
      liveSectionDrag = null;

      if (liveSectionHandle) {
        liveSectionHandle.style.cursor =
          'grab';
      }
    }

    if (
      liveSectionResize?.pointerId ===
      event.pointerId
    ) {
      liveSectionResize = null;
    }

    if (liveSectionPointers.size < 2) {
      liveSectionPinch = null;
    }
  }

  liveSectionPanel.addEventListener(
    'pointerup',
    finishLiveSectionPointer
  );

  liveSectionPanel.addEventListener(
    'pointercancel',
    finishLiveSectionPointer
  );

  // ---------------------------
  // DRAG WINDOW DARI HEADER
  // ---------------------------
  if (liveSectionHandle) {
    liveSectionHandle.style.touchAction =
      'none';

    liveSectionHandle.style.cursor =
      'grab';

    liveSectionHandle.addEventListener(
      'pointerdown',
      event => {
        // PDF dan Close tetap bekerja normal.
        if (
          event.target.closest('button')
        ) {
          return;
        }

        const rect =
          floatLiveSectionPanel();

        liveSectionDrag = {
          pointerId: event.pointerId,

          startX: event.clientX,
          startY: event.clientY,

          left: rect.left,
          top: rect.top,

          width: rect.width,
          height: rect.height
        };

        liveSectionHandle.style.cursor =
          'grabbing';

        try {
          liveSectionHandle.setPointerCapture(
            event.pointerId
          );
        } catch {}

        event.preventDefault();
        event.stopPropagation();
      }
    );
  }

  // ---------------------------
  // Resize handle dihapus.
// Resize window dilakukan dengan pinch 2 jari.
// Grafik selalu redraw mengikuti ukuran panel.
  const liveSectionResizeObserver =
    new ResizeObserver(() => {
      if (
        !liveSectionPanel.classList.contains(
          'collapsed'
        ) &&
        !liveSectionPanel.classList.contains(
          'hidden'
        )
      ) {
        drawSection();
      }
    });

  liveSectionResizeObserver.observe(
    liveSectionPanel
  );
}
/**
 * Maply - Perbaikan Modul GPS Tracking & Live Line
 */

let trackingInterval = null;
let lastValidPosition = null;
let trackingCoordinates = []; // Menyimpan array titik [Easting, Northing]

// Konfigurasi jarak minimum untuk mencegah garis lompat (dalam meter)
const MAX_ALLOWED_JUMP_METERS = 100; 

/**
 * Memulai GPS Tracking dengan dukungan Background & Continuous Live Line
 */
function startGps() {
  if (state.gpsWatch !== null) {
    return true;
  }

  if (!navigator.geolocation) {
    showToast(
      'GPS is not available on this device.'
    );
    return false;
  }


  if (!state.currentProject) {
    showToast(
      'Open or create a project before using GPS.',
      4500
    );
    return false;
  }


  $('#gpsButton')
    .classList
    .add('active');


  state.gpsWatch =
    navigator.geolocation.watchPosition(
      position => {
        handleGpsPosition(position)
          .catch(error => {
            console.error(
              '[Maply GPS] position failed:',
              error
            );
          });
      },

      error => {
        showToast(
          `GPS Error: ${error.message}`,
          5000
        );

        $('#gpsButton')
          .classList
          .remove('active');

        state.gpsWatch = null;
      },

      {
        enableHighAccuracy: true,
        maximumAge: 2000,
        timeout: 15000
      }
    );


  return true;
}

/**
 * Memproses Titik Koordinat Masuk & Mencegah Garis Lurus Loncat
 */
function processTrackingPoint(newPoint) {
    if (!lastValidPosition) {
        // Titik pertama kali tracking
        lastValidPosition = newPoint;
        trackingCoordinates.push([newPoint.x, newPoint.y]);
        updateLiveLineCanvas();
        return;
    }

    // Hitung jarak dari titik terakhir ke titik baru (menggunakan rumus Pythagoras / Haversine)
    const distance = calculateDistance(lastValidPosition, newPoint);

    // Cek apakah ada jeda lompatan ekstrem (misal karena app baru dibuka dari background)
    if (distance > MAX_ALLOWED_JUMP_METERS) {
        console.warn(`[Maply] Terdeteksi jeda GPS ekstrem (${distance.toFixed(1)}m). Mengabaikan garis lurus langsung.`);
        
        // Opsi A: Putus garis (buat segment baru agar tidak menarik garis lurus melompat)
        // Kita bisa masukkan titik baru sebagai titik terputus (break line) tanpa menarik garis sambung
        lastValidPosition = newPoint;
        trackingCoordinates.push(null); // Indikator garis terputus
        trackingCoordinates.push([newPoint.x, newPoint.y]);
        return;
    }

    // Jika jarak normal, tambahkan ke jalur live line
    trackingCoordinates.push([newPoint.x, newPoint.y]);
    lastValidPosition = newPoint;

    // Render ulang live line di layar
    updateLiveLineCanvas();
}

/**
 * Fungsi menghitung jarak sederhana antar dua titik (meter)
 */
function calculateDistance(p1, p2) {
    // Estimasi kasar jarak meter (bisa disesuaikan dengan fungsi proyeksi UTM Anda)
    const dx = (p2.x - p1.x) * 111320; // konversi derajat ke meter (contoh)
    const dy = (p2.y - p1.y) * 110540;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Merender Live Line ke Kanvas Maply
 */
function updateLiveLineCanvas() {
    console.log("[Maply] Memperbarui Live Line di kanvas...", trackingCoordinates);
    // Masukkan logika rendering peta Anda di sini (misalnya memperbarui GeoJSON layer tracking)
}
selectFeature(0,{smooth:false});setFeatureMenu(false);setMode('2d');resize();
migrateLegacyCache().then(refreshProjectHub).catch(error=>{console.error('Project Hub could not be initialized',error);$('#recentProjectsSummary').textContent='Offline storage unavailable';});
document.documentElement.dataset.appReady='true';
// ==========================================
// SISTEM CROSSHAIR TENGAH LAYAR (MAPLY)
// ==========================================

// 1. Fungsi untuk memunculkan atau menyembunyikan crosshair
function toggleCrosshair(show) {
  const crosshairEl = document.getElementById('mapCrosshair');
  if (crosshairEl) {
    crosshairEl.style.display = show ? 'block' : 'none';
  }
  const controlBar = document.getElementById('crosshairControlBar');
  if (controlBar) {
    controlBar.classList.toggle('hidden', !show);
  }
}
// 5. Hubungkan Tombol Terapung (Add Point, Undo, Close)
const addPointBtn =
  document.getElementById('centerAddPointBtn');

const undoPointBtn =
  document.getElementById('centerUndoBtn');

const finishToolBtn =
  document.getElementById('centerFinishToolBtn');

const closeToolBtn =
  document.getElementById('centerCloseToolBtn');

const crosshairControlBar =
  document.getElementById('crosshairControlBar');

const measureModeSelector =
  document.getElementById('measureModeSelector');

const measureLineModeBtn =
  document.getElementById('measureLineModeBtn');

const measureAreaModeBtn =
  document.getElementById('measureAreaModeBtn');

function updateMeasureModeUi() {
  if (!measureModeSelector) return;

  const isMeasure =
    state.tool === 'measure';

  measureModeSelector.classList.toggle(
    'hidden',
    !isMeasure
  );

  if (!isMeasure) return;

  const mode =
    state.measure?.mode || 'line';

  measureLineModeBtn?.classList.toggle(
    'active',
    mode === 'line'
  );

  measureAreaModeBtn?.classList.toggle(
    'active',
    mode === 'area'
  );
}
function setMeasureMode(mode) {
  if (
    mode !== 'line' &&
    mode !== 'area'
  ) {
    return;
  }

  if (
    state.measure?.mode === mode
  ) {
    return;
  }

  clearMeasureDrawing();

  state.measure.mode = mode;

  updateMeasureModeUi();
  updateToolActionState();

  showToast(
    mode === 'line'
      ? 'Measure Line'
      : 'Measure Area'
  );
}
if (addPointBtn) {
  addPointBtn.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    addPointFromCenterToActiveTool();
    updateToolActionState();
  };
}

if (undoPointBtn) {
  undoPointBtn.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    undoActiveToolPoint();
    updateToolActionState();
  };
}

if (finishToolBtn) {
  finishToolBtn.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    finishActiveTool();
    updateToolActionState();
  };
}

if (closeToolBtn) {
  closeToolBtn.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setTool('select');
    toggleCrosshair(false);

    showToast('Tool dibatalkan.');
  };
}
function canFinishActiveTool() {
  if (state.tool === 'measure') {
    const mode = state.measure?.mode || 'line';

    if (mode === 'area') {
      return state.measure.points.length >= 3;
    }

    return state.measure.points.length >= 2;
  }

  if (state.tool === 'section') {
    return !!(
      state.section.a &&
      state.section.b
    );
  }

  return false;
}
function updateToolActionState() {
  if (!finishToolBtn) return;

  const supported =
    state.tool === 'measure' ||
    state.tool === 'section';

  finishToolBtn.classList.toggle(
    'hidden',
    !supported
  );

  finishToolBtn.disabled =
    !supported ||
    !canFinishActiveTool();
}
function finishActiveTool() {
  if (!canFinishActiveTool()) {
    showToast(
      'Belum cukup titik untuk menyelesaikan.'
    );
    return;
  }

  if (state.tool === 'measure') {
    showToast(
      'Measure siap diselesaikan.'
    );

    return;
  }

  if (state.tool === 'section') {
    showToast(
      'Section siap disimpan.'
    );

    return;
  }
}
// ==========================================
// CROSSHAIR CONTROL BAR DRAG
// Tap = button action
// Drag > 6px = move entire control group
// ==========================================
if (crosshairControlBar) {
  let crosshairBarDrag = null;
  let suppressCrosshairClick = false;

  crosshairControlBar.style.touchAction = 'none';

  crosshairControlBar.addEventListener('pointerdown', (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    const rect = crosshairControlBar.getBoundingClientRect();

    crosshairBarDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      moved: false
    };

    suppressCrosshairClick = false;

    event.preventDefault();
  });

  window.addEventListener('pointermove', (event) => {
    if (
      !crosshairBarDrag ||
      event.pointerId !== crosshairBarDrag.pointerId
    ) return;

    const dx = event.clientX - crosshairBarDrag.startX;
    const dy = event.clientY - crosshairBarDrag.startY;

    if (!crosshairBarDrag.moved) {
      if (Math.hypot(dx, dy) < 6) return;

      crosshairBarDrag.moved = true;
      suppressCrosshairClick = true;
      crosshairControlBar.classList.add('dragging');
    }

    const margin = 8;

    const viewportWidth =
      document.documentElement.clientWidth;

    const viewportHeight =
      document.documentElement.clientHeight;

    const rect = crosshairControlBar.getBoundingClientRect();

    let left = crosshairBarDrag.startLeft + dx;
    let top = crosshairBarDrag.startTop + dy;

    left = Math.max(
      margin,
      Math.min(
        left,
        viewportWidth - rect.width - margin
      )
    );

    top = Math.max(
      margin,
      Math.min(
        top,
        viewportHeight - rect.height - margin
      )
    );

    crosshairControlBar.style.left = `${left}px`;
    crosshairControlBar.style.top = `${top}px`;
    crosshairControlBar.style.right = 'auto';

    crosshairControlBar.style.setProperty(
      'bottom',
      'auto',
      'important'
    );

    crosshairControlBar.style.transform = 'none';

      event.preventDefault();
    }, { passive: false });

    const finishCrosshairBarDrag = (event) => {
      if (
        !crosshairBarDrag ||
        event.pointerId !== crosshairBarDrag.pointerId
      ) return;

      crosshairControlBar.classList.remove('dragging');
      crosshairBarDrag = null;
    };

    window.addEventListener(
      'pointerup',
      finishCrosshairBarDrag
    );

    window.addEventListener(
      'pointercancel',
      finishCrosshairBarDrag
    );

  crosshairControlBar.addEventListener(
    'click',
    (event) => {
      if (!suppressCrosshairClick) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      suppressCrosshairClick = false;
    },
    true
  );
}
function clampCrosshairControlBarToViewport() {
  if (!crosshairControlBar) return;

  const rect = crosshairControlBar.getBoundingClientRect();
  const margin = 8;

  const viewportWidth =
    document.documentElement.clientWidth;

  const viewportHeight =
    document.documentElement.clientHeight;

  let left = rect.left;
  let top = rect.top;

  left = Math.max(
    margin,
    Math.min(
      left,
      viewportWidth - rect.width - margin
    )
  );

  top = Math.max(
    margin,
    Math.min(
      top,
      viewportHeight - rect.height - margin
    )
  );

  crosshairControlBar.style.left = `${left}px`;
  crosshairControlBar.style.top = `${top}px`;
  crosshairControlBar.style.right = 'auto';

  crosshairControlBar.style.setProperty(
    'bottom',
    'auto',
    'important'
  );

  crosshairControlBar.style.transform = 'none';
}
  measureLineModeBtn?.addEventListener(
    'click',
    () => setMeasureMode('line')
  );

  measureAreaModeBtn?.addEventListener(
    'click',
    () => setMeasureMode('area')
  );
window.addEventListener(
  'resize',
  clampCrosshairControlBarToViewport
);

window.addEventListener(
  'orientationchange',
  () => {
    setTimeout(
      clampCrosshairControlBarToViewport,
      150
    );
  }
);